import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Input, Output, Pipe, forwardRef, ViewChild, Inject, Directive, inject, HostListener, ElementRef, ContentChildren, ContentChild, HostBinding, ViewChildren, NgModule } from '@angular/core';
import * as i2$2 from '@angular/forms';
import { FormGroup, FormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, ControlContainer, FormGroupDirective, Validators, FormArray, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Subscription, Subject, Observable, throwError, of, from } from 'rxjs';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@angular/material/core';
import { MAT_DATE_FORMATS, NativeDateAdapter, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import * as i1$1 from '@angular/material/datepicker';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import * as i1$3 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import * as i1 from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { isDate, parseISO, isValid, format, parse, getMonth, startOfToday, getYear, differenceInMonths } from 'date-fns';
import * as flatpickrImported from 'flatpickr';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import { filter, takeUntil, map, retry, catchError, shareReplay, tap, switchMap, withLatestFrom, concatMap, toArray } from 'rxjs/operators';
import { nlBE } from 'date-fns/locale';
import { SubSink } from 'subsink';
import * as i1$2 from '@angular/common/http';
import { HttpClient, HttpParams } from '@angular/common/http';
import { sortBy, isNumber, isNil, cloneDeep } from 'lodash-es';
import * as i2$3 from '@anb/ng-configuratie';
import { AnbNgConfiguratieService } from '@anb/ng-configuratie';
import { maskitoPrefixPostprocessorGenerator, maskitoAddOnFocusPlugin, maskitoRemoveOnBlurPlugin, maskitoParseNumber, maskitoNumberOptionsGenerator } from '@maskito/kit';
import { maskitoTransform } from '@maskito/core';
import * as i3 from '@maskito/angular';
import { MaskitoDirective, MaskitoPipe } from '@maskito/angular';
import numeral from 'numeral';
import * as i2$4 from '@anb/ng-ui';
import { ErrorService, AnbUIService, AnbUIElementsModule } from '@anb/ng-ui';

var ActivityStatusEnum;
(function (ActivityStatusEnum) {
    ActivityStatusEnum[ActivityStatusEnum["None"] = 0] = "None";
    ActivityStatusEnum[ActivityStatusEnum["Select"] = 1] = "Select";
    ActivityStatusEnum[ActivityStatusEnum["Insert"] = 2] = "Insert";
    ActivityStatusEnum[ActivityStatusEnum["View"] = 3] = "View";
    ActivityStatusEnum[ActivityStatusEnum["Edit"] = 4] = "Edit";
})(ActivityStatusEnum || (ActivityStatusEnum = {}));

class AnbFormGroup extends FormGroup {
    constructor() {
        super(...arguments);
        this.activityStatus = new BehaviorSubject(ActivityStatusEnum.None);
    }
    setActivityStatus(activityStatus) {
        this.activityStatus.next(activityStatus);
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbFormBuilder extends FormBuilder {
    // eslint-disable-next-line @typescript-eslint/ban-types
    anbGroup(controlsConfig, activityStatus = null, options = null) {
        const group = super.group(controlsConfig, options);
        const anbForm = new AnbFormGroup(group.controls, {
            asyncValidators: group.asyncValidator,
            updateOn: group.updateOn,
            validators: group.validator,
        });
        if (activityStatus) {
            anbForm.activityStatus = activityStatus;
        }
        return anbForm;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormBuilder, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormBuilder }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormBuilder, decorators: [{
            type: Injectable
        }] });

var ActivityStatusEnablementEnum;
(function (ActivityStatusEnablementEnum) {
    /** The widget is not enabled in any ActivitySatus, i.e. widget is always disabled.
    ReadOnly is the default 'zero' value (secure by default). It cannot be combined with other flags. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["ReadOnly"] = 0] = "ReadOnly";
    /** Enable widget when page is in ActivityStatus 'Select' only. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["SelectOnly"] = 1] = "SelectOnly";
    /** Enable widget when page is in ActivityStatus 'Insert' only (i.e. not enabled in ActivityStatus 'Edit'). */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["InsertOnly"] = 2] = "InsertOnly";
    /** Enable widget when page is in ActivityStatus 'Edit' only (not enabled in ActivityStatus 'Insert'). */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["EditOnly"] = 4] = "EditOnly";
    /** Enable widget when page is in ActivityStatus 'View' only */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["ViewOnly"] = 8] = "ViewOnly";
    /** Don't perform status enablement on the widget, i.e. the status of the widget is not modified.
    This flag cannot be combined with other flags. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["Ignore"] = 128] = "Ignore";
    // Combined flags
    /** Enable widget when page is in ActivityStatus 'Select' or 'Insert'. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["SelectInsert"] = 3] = "SelectInsert";
    /** SelectEdit: Enable widget when page is in ActivityStatus 'Select' or 'Edit' or 'Insert'. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["SelectEditInsert"] = 7] = "SelectEditInsert";
    /** EditInsertOnly: Enable widget when page is in ActivityStatus 'Edit' or 'Insert'. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["EditInsert"] = 6] = "EditInsert";
    /** Enable widget when page is in ActivityStatus 'View' or 'Edit' or 'Insert' (i.e. not enabled in ActivityStatus 'Select'). */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["ViewEditInsert"] = 14] = "ViewEditInsert";
    /** Enable widget in all possible ActivityStatuses, i.e. the  widget is always enabled. */
    ActivityStatusEnablementEnum[ActivityStatusEnablementEnum["Allways"] = 15] = "Allways";
})(ActivityStatusEnablementEnum || (ActivityStatusEnablementEnum = {}));

const principalNameKey = 'Authorization::PrincipalName';
class AnbFormService {
    constructor() {
        this.UserCodes = new BehaviorSubject([]);
    }
    markAsDirty(group) {
        group.markAsDirty();
        for (const i in group.controls) {
            if (group.controls[i] instanceof UntypedFormControl) {
                group.controls[i].markAsDirty();
            }
            else {
                this.markAsDirty(group.controls[i]);
            }
        }
    }
    setPolicies(policies) {
        this.Policies = policies;
    }
    get principalName() {
        const principalName = localStorage.getItem(principalNameKey);
        return principalName;
    }
    set principalName(name) {
        if (name === null) {
            localStorage.removeItem(principalNameKey);
        }
        else {
            localStorage.setItem(principalNameKey, name);
        }
    }
    hasPermissionOnTask(pTaskPermission) {
        // taskPermission is in form TASK/PERMISSION, like OOMP_IND_EIG/C
        const taskPermission = pTaskPermission.split('/');
        const policies = this.Policies;
        for (const task in Object.entries(policies)) {
            if (policies.hasOwnProperty(task)) {
                const permissions = policies[task];
                if (taskPermission[0] === task) {
                    for (const t of permissions) {
                        if (taskPermission[1] === t) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }
    isDisabled(ase, activityStatus, auth = '') {
        let isDisabled = true;
        if (activityStatus !== undefined) {
            isDisabled = true;
            if (ase === ActivityStatusEnablementEnum.ReadOnly) {
                isDisabled = true;
            }
            if (ase !== ActivityStatusEnablementEnum.Ignore) {
                switch (activityStatus) {
                    case ActivityStatusEnum.Select:
                        if (ase === ActivityStatusEnablementEnum.SelectOnly ||
                            ase === ActivityStatusEnablementEnum.SelectInsert ||
                            ase === ActivityStatusEnablementEnum.SelectEditInsert) {
                            isDisabled = false;
                        }
                        break;
                    case ActivityStatusEnum.Insert:
                        if (ase === ActivityStatusEnablementEnum.InsertOnly ||
                            ase === ActivityStatusEnablementEnum.EditInsert ||
                            ase === ActivityStatusEnablementEnum.SelectEditInsert ||
                            ase === ActivityStatusEnablementEnum.SelectInsert ||
                            ase === ActivityStatusEnablementEnum.ViewEditInsert) {
                            isDisabled = false;
                        }
                        break;
                    case ActivityStatusEnum.View:
                        if (ase === ActivityStatusEnablementEnum.ViewEditInsert || ase === ActivityStatusEnablementEnum.ViewOnly) {
                            isDisabled = false;
                        }
                        break;
                    case ActivityStatusEnum.Edit:
                        if (ase === ActivityStatusEnablementEnum.EditInsert ||
                            ase === ActivityStatusEnablementEnum.EditOnly ||
                            ase === ActivityStatusEnablementEnum.SelectEditInsert ||
                            ase === ActivityStatusEnablementEnum.ViewEditInsert) {
                            isDisabled = false;
                        }
                        break;
                    default:
                        isDisabled = true;
                }
            }
            else {
                isDisabled = false;
            }
            if (!isDisabled) {
                // Authorization check
                if (auth !== undefined && auth !== '') {
                    // this.auth = TASK/PERMISSION string
                    if (this.hasPermissionOnTask(auth)) {
                        isDisabled = false;
                    }
                    else {
                        isDisabled = true;
                    }
                }
            }
        }
        return isDisabled;
    }
    scrollToTop() {
        window.scrollTo(0, 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormService, decorators: [{
            type: Injectable
        }] });

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
class BaseFormElementComponent {
    constructor(baseFormService) {
        this.baseFormService = baseFormService;
        this.visible = true;
        this.ase = ActivityStatusEnablementEnum.ReadOnly;
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onValueChange = new EventEmitter();
        this.customErrorList = [];
        // Wanneer er errors zijn op de form, zullen de errors met de opgegeven error keys uit deze array worden weergegeven bij deze component.
        this.formErrorKeysToDisplay = [];
        this.subs = new Subscription();
        this.disabled$ = new BehaviorSubject(null);
        this.propagateChange = (_) => { };
    }
    setValue(value) {
        this.propagateChange(value);
        this.valueChange(value);
    }
    valueChange(value) {
        this.onValueChange.emit(value);
    }
    writeValue(value) { }
    registerOnChange(fn) { }
    registerOnTouched(fn) { }
    ngOnChanges(changes) {
        if (this.form) {
            if (this.form.parent && this.form.parent.controls && this.form.parent.controls['ANB']) {
                const val = this.form.parent.controls['ANB'].get('ActivityStatus').value;
                this.securityCheck(val);
            }
            else if (this.form.activityStatus) {
                this.form.activityStatus.subscribe((pActivityStatus) => {
                    this.securityCheck(pActivityStatus);
                });
            }
            else {
                this.securityCheck(null);
            }
        }
    }
    ngOnInit() {
        // We kunnen de formcontrol doorgeven als "formControl", maar ook door form en formControlName door te geven
        // <anb-input-text formControlName="Dossiernummer" [form]="openbaaronderzoekmpDetailForm"></anb-input-text>
        // <anb-input-text [formControl]="openbaaronderzoekmpDetailForm.controls.Dossiernummer"></anb-input-text>
        // Bij bevonstaande voorbeelden is bij beiden form beschikbaar, met het voordeel bij de bovenstaande dat deze ook op de hoogte is van de naam en eventueel het form kan aanroepen (om te zien of het submitted is?)
        if (!this.fc && this.form && this.formControlName) {
            if (this.form.controls[this.formControlName]) {
                const c = this.form.controls[this.formControlName];
                if (c instanceof UntypedFormControl) {
                    this.fc = c;
                }
            }
            else {
                // FormControl can be part of a FormGroup
                Object.keys(this.form.controls).forEach((key) => {
                    if (key === this.formControlName) {
                        this.fc = this.form.controls[key];
                    }
                    else {
                        // Check if formcontrol is formgroup and loop controls
                        if (this.form.controls[key] instanceof UntypedFormGroup) {
                            const vGroup = this.form.controls[key];
                            Object.keys(vGroup.controls).forEach((k) => {
                                if (k === this.formControlName) {
                                    this.fc = vGroup.controls[k];
                                }
                            });
                        }
                    }
                });
            }
        }
        // We kunnen een id doorgeven (bv.     <anb-input-textformControlName= "Dossiernummer" [id] = "'test'"> </anb-input-text>)
        // Wanneer er geen doorgegeven is, vullen we deze zelf in
        if (!this.id) {
            this.id = this.formControlName;
            const parent = this.getParentName();
            if (this.hasValue(parent))
                this.id = parent + '.' + this.id;
        }
        // If the model has been changed from outside this control, we need to update the value of this control
        if (this.fc) {
            const sub = this.fc.valueChanges.subscribe((c) => {
                this.writeValue(c);
            });
            this.subs.add(sub);
        }
        if (this.form && this.form.parent) {
            if (this.form.parent) {
                const sub = this.form.parent.valueChanges.subscribe((val) => {
                    if (val.ANB) {
                        this.securityCheck(val.ANB.ActivityStatus);
                    }
                });
                this.subs.add(sub);
            }
            if (this.form.parent.controls['ANB']) {
                const sub = this.form.parent.controls['ANB'].get('ActivityStatus').valueChanges.subscribe((val) => {
                    this.securityCheck(val);
                });
                this.subs.add(sub);
            }
        }
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    getActivityStatus() {
        if (this.form) {
            if (this.form.parent && this.form.parent.controls && this.form.parent.controls['ANB']) {
                return this.form.parent.controls['ANB'].value.ActivityStatus;
            }
            else {
                return this.form.controls.ANB.value.ActivityStatus;
            }
        }
    }
    securityCheck(pActivityStatus) {
        this.disabled = this.baseFormService.isDisabled(this.ase, pActivityStatus, this.auth);
        this.disabled$.next(this.disabled);
    }
    hasValue(obj) {
        // Similar to the concept isNullOrEmpty, but rather isNullOrUndefinedOrEmpty, but that name it to long
        return obj !== undefined && obj !== null && obj !== '';
    }
    coalesce(...values) {
        for (const value of values) {
            if (this.hasValue(value))
                return value;
        }
    }
    getParentName() {
        if (this.fc && this.fc.parent && this.fc.parent.parent) {
            const c = this.fc.parent.parent.controls;
            for (const key in c) {
                if (c[key] === this.fc.parent) {
                    return key;
                }
            }
        }
        return '';
    }
    getId() {
        return this.id;
    }
    getLabel() {
        return this.label;
    }
    isValid() {
        return !this.fc || !this.fc.invalid; // https://stackoverflow.com/questions/65693343/angular-reactive-form-disabled-control-is-not-valid
    }
    isFormValid() {
        let isValid = true;
        if (this.form && !this.form.valid && this.getFormErrors()) {
            isValid = false;
        }
        return isValid;
    }
    showError() {
        // Enkel als fout aanduiden bij het invullen (of bewaren, dan markeren we het als dirty)
        return (!this.isValid() || !this.isFormValid()) && !this.fc.pristine && !this.disabled;
    }
    getError() {
        if (this.isValid() && this.isFormValid()) {
            return '';
        }
        if (this.fc.pristine) {
            // Enkel als fout aanduiden bij het invullen (of bewaren, dan markeren we het als dirty)
            return '';
        }
        if (this.fc.errors) {
            const errorMessages = this.getErrorMessages(this.fc.errors);
            if (errorMessages) {
                return errorMessages;
            }
            // Order of handling errors is important: first we handle input errors, and finally required errors, otherwise the input errors won't show.
            // Also, we handle custom errors first, in case the application uses custom messages and doesn't want to rely on the default error messages.
            // FILE SIZE ERROR HANDLING
            const customFileSizeError = this.customErrorList.filter((ce) => ((ce.formControlName.indexOf('.') === -1 && ce.formControlName === this.formControlName) ||
                (ce.formControlName.indexOf('.') > -1 && ce.formControlName.split('.')[1] === this.formControlName)) &&
                ce.validationKey === 'filesize')[0];
            if (customFileSizeError) {
                return customFileSizeError.errorMessage;
            }
            else {
                if (this.fc.errors['filesize']) {
                    return this.getLabel() + ' bestandsgrootte werd overschreden.';
                }
            }
            if (this.fc.errors['maxFileSize']) {
                const message = this.fc.errors['maxFileSize'].message;
                if (message) {
                    return message;
                }
            }
            // FILE TYPE ERROR HANDLING
            if (this.fc.errors['wrongFiletype']) {
                const message = this.fc.errors['wrongFiletype'].message;
                if (message) {
                    return message;
                }
            }
            // INPUT LENGTH ERROR HANDLING
            const customMinLengthError = this.customErrorList.filter((ce) => ((ce.formControlName.indexOf('.') === -1 && ce.formControlName === this.formControlName) ||
                (ce.formControlName.indexOf('.') > -1 && ce.formControlName.split('.')[1] === this.formControlName)) &&
                ce.validationKey === 'minlength')[0];
            if (customMinLengthError) {
                return customMinLengthError.errorMessage;
            }
            else {
                if (this.fc.errors['minlength']) {
                    return this.getLabel() + ' heeft tenminste ' + this.fc.errors['minlength'].requiredLength + ' karakters nodig';
                }
            }
            // REQUIRED ERROR HANDLING
            const customRequiredError = this.customErrorList.filter((ce) => ((ce.formControlName.indexOf('.') === -1 && ce.formControlName === this.formControlName) ||
                (ce.formControlName.indexOf('.') > -1 && ce.formControlName.split('.')[1] === this.formControlName)) &&
                ce.validationKey === 'required')[0];
            if (customRequiredError) {
                return customRequiredError.errorMessage;
            }
            else {
                if (this.fc.errors['required']) {
                    return this.getLabel() + ' is verplicht.';
                }
            }
        }
        if (this.form?.errors) {
            return this.getErrorMessages(this.getFormErrors());
        }
        return '';
    }
    getFormErrors() {
        if (this.form?.errors && this.formErrorKeysToDisplay?.length) {
            let hasError = false;
            const validationErrorsResult = {};
            this.formErrorKeysToDisplay.forEach((key) => {
                const error = this.form.getError(key);
                if (error) {
                    hasError = true;
                    validationErrorsResult[key] = error;
                }
            });
            if (hasError) {
                return validationErrorsResult;
            }
        }
        return null;
    }
    getErrorMessages(errors) {
        const result = [];
        if (errors) {
            Object.keys(errors).forEach((keyError) => {
                const customError = this.customErrorList.filter((ce) => ((ce.formControlName.indexOf('.') === -1 && ce.formControlName === this.formControlName) ||
                    (ce.formControlName.indexOf('.') > -1 && ce.formControlName.split('.')[1] === this.formControlName)) &&
                    ce.validationKey === keyError)[0];
                if (customError) {
                    result.push(customError.errorMessage);
                }
            });
            if (result.length > 0) {
                return result.join('<br />');
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseFormElementComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BaseFormElementComponent, selector: "app-base-form-element", inputs: { label: "label", fc: ["formControl", "fc"], formControlName: "formControlName", id: "id", form: "form", subLabel: "subLabel", disabled: "disabled", visible: "visible", ase: "ase", auth: "auth", annotation: "annotation", customErrorList: "customErrorList", formErrorKeysToDisplay: "formErrorKeysToDisplay" }, outputs: { onValueChange: "onValueChange" }, usesOnChanges: true, ngImport: i0, template: "", styles: [""] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseFormElementComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-base-form-element', template: "" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { label: [{
                type: Input
            }], fc: [{
                type: Input,
                args: ['formControl']
            }], formControlName: [{
                type: Input
            }], id: [{
                type: Input
            }], form: [{
                type: Input
            }], subLabel: [{
                type: Input
            }], disabled: [{
                type: Input
            }], visible: [{
                type: Input
            }], ase: [{
                type: Input
            }], auth: [{
                type: Input
            }], annotation: [{
                type: Input
            }], onValueChange: [{
                type: Output
            }], customErrorList: [{
                type: Input
            }], formErrorKeysToDisplay: [{
                type: Input
            }] } });

class SafePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value, type = 'html') {
        // debugger;
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Invalid safe type specified: ${type}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, name: "safe" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SafePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'safe' }]
        }], ctorParameters: function () { return [{ type: i1.DomSanitizer }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbCheckboxComponent extends BaseFormElementComponent {
    writeValue(_value) {
        this.value = _value;
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onChange(event) {
        this.value = event.target.checked;
        // Update form
        this.propagateChange(this.value);
    }
    constructor(formService) {
        super(formService);
        this.formService = formService;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCheckboxComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbCheckboxComponent, selector: "anb-checkbox", inputs: { name: "name", value: "value" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbCheckboxComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbCheckboxComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum form__label checkbox\" [class.invisible]=\"!visible\">\n  <label class=\"\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id\">\n    <input\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [value]=\"value\"\n      [checked]=\"value\"\n      (change)=\"onChange($event)\"\n      [disabled]=\"disabled\"\n      [attr.id]=\"id\"\n    /><span></span> <span [innerHtml]=\"label | safe: 'html'\"></span>\n  </label>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-checkbox', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbCheckboxComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbCheckboxComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum form__label checkbox\" [class.invisible]=\"!visible\">\n  <label class=\"\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id\">\n    <input\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [value]=\"value\"\n      [checked]=\"value\"\n      (change)=\"onChange($event)\"\n      [disabled]=\"disabled\"\n      [attr.id]=\"id\"\n    /><span></span> <span [innerHtml]=\"label | safe: 'html'\"></span>\n  </label>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

class ErrorUtils {
    static removeErrors(keys, control) {
        if (!control || !keys || keys.length === 0) {
            return;
        }
        const remainingErrors = keys.reduce((errors, key) => {
            delete errors[key];
            return errors;
        }, { ...control.errors });
        control.setErrors(remainingErrors);
        if (Object.keys(control.errors || {}).length === 0) {
            control.setErrors(null);
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static addErrors(errors, control) {
        if (!control || !errors) {
            return;
        }
        control.setErrors({ ...control.errors, ...errors });
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const flatpickr = flatpickrImported.default;
const dateToday = new Date();
class AnbDatepickerDeprecatedComponent extends BaseFormElementComponent {
    writeValue(value) {
        if (value) {
            let date = value;
            if (!isDate(date)) {
                date = parseISO(date);
            }
            if (isValid(date)) {
                this.value = format(date, 'dd/MM/yyyy');
            }
        }
        else {
            this.value = null;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.maxDate = dateToday;
        this.minDate = new Date(dateToday.getFullYear() - 120, dateToday.getMonth(), dateToday.getDate());
        this.allowInput = false;
        this.static = false;
        this.value = '';
        console.warn('<anb-datepicker-deprecated> is deprecated. Please use <anb-datepicker>.');
    }
    ngOnInit() {
        super.ngOnInit();
    }
    ngAfterViewInit() {
        this.datePicker = flatpickr(this.input.nativeElement, {
            dateFormat: 'd/m/Y',
            locale: Dutch,
            maxDate: this.maxDate,
            minDate: this.minDate,
            wrap: true,
            allowInput: this.allowInput,
            static: this.static,
            onClose: function (dates, dateStr, instance) {
                // Aan value geraken die in input field ingevuld is
                if (isValid(parse(instance.input.value, 'dd/MM/yyyy', new Date()))) {
                    instance.setDate(instance.input.value);
                }
            },
        });
    }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    onChange(event) {
        this.value = event.target.value;
        this.propagateChange(this.value);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onBlur(event) {
        if (this.fc) {
            if (this.value && !isValid(parse(this.value, 'dd/MM/yyyy', new Date()))) {
                ErrorUtils.addErrors({
                    wrongFormat: { message: `Ongeldige datum.` },
                }, this.fc);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerDeprecatedComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbDatepickerDeprecatedComponent, selector: "anb-datepicker-deprecated", inputs: { maxDate: "maxDate", minDate: "minDate", allowInput: "allowInput", static: "static" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbDatepickerDeprecatedComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbDatepickerDeprecatedComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["flatpickr"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [ngClass]=\"{ 'flex-container': static === true }\">\n  <label for=\"{{ getId() }}\" class=\"webuniversum form__label\" style=\"float: left; clear: left\" [innerHtml]=\"label\"></label>\n  <span class=\"subvraag\">{{ subLabel }}</span>\n  <div style=\"width: 150px\" class=\"flatpickr\" #flatpickr>\n    <div class=\"datepicker\" style=\"display: inline\">\n      <input\n        autocomplete=\"off\"\n        class=\"input-field js-datepicker-bound input-field--datepicker\"\n        placeholder=\"dd/mm/jjjj\"\n        [class.input-field--error]=\"showError() && !disabled\"\n        [class.input-field--disabled]=\"disabled\"\n        [value]=\"value\"\n        [attr.id]=\"id\"\n        [disabled]=\"disabled\"\n        name=\"datepicker\"\n        (change)=\"onChange($event)\"\n        (keyup)=\"onChange($event)\"\n        (blur)=\"onBlur($event)\"\n        #inputDatepicker\n        data-input\n      />\n      <label class=\"datepicker__icon\" data-toggle></label>\n    </div>\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n      {{ getError() }}\n    </div>\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.flex-container{display:flex;flex-direction:column}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerDeprecatedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-datepicker-deprecated', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbDatepickerDeprecatedComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbDatepickerDeprecatedComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [ngClass]=\"{ 'flex-container': static === true }\">\n  <label for=\"{{ getId() }}\" class=\"webuniversum form__label\" style=\"float: left; clear: left\" [innerHtml]=\"label\"></label>\n  <span class=\"subvraag\">{{ subLabel }}</span>\n  <div style=\"width: 150px\" class=\"flatpickr\" #flatpickr>\n    <div class=\"datepicker\" style=\"display: inline\">\n      <input\n        autocomplete=\"off\"\n        class=\"input-field js-datepicker-bound input-field--datepicker\"\n        placeholder=\"dd/mm/jjjj\"\n        [class.input-field--error]=\"showError() && !disabled\"\n        [class.input-field--disabled]=\"disabled\"\n        [value]=\"value\"\n        [attr.id]=\"id\"\n        [disabled]=\"disabled\"\n        name=\"datepicker\"\n        (change)=\"onChange($event)\"\n        (keyup)=\"onChange($event)\"\n        (blur)=\"onBlur($event)\"\n        #inputDatepicker\n        data-input\n      />\n      <label class=\"datepicker__icon\" data-toggle></label>\n    </div>\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n      {{ getError() }}\n    </div>\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.flex-container{display:flex;flex-direction:column}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { input: [{
                type: ViewChild,
                args: ['flatpickr']
            }], maxDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }], allowInput: [{
                type: Input
            }], static: [{
                type: Input
            }] } });

class CustomError {
    constructor(
    /** Name of formcontrol */
    formControlName, 
    /** Validator key (required, minlength, maxlength, ...)  */
    validationKey, 
    /** errorMessage which is shown to user  */
    errorMessage) {
        this.formControlName = formControlName;
        this.validationKey = validationKey;
        this.errorMessage = errorMessage;
    }
}

class DatePickerError extends CustomError {
    constructor(errorValidationKey, errorMessage, formControlName) {
        super(formControlName, errorValidationKey, errorMessage);
    }
}

const DATEPICKER_FORMATS = {
    parse: {
        dateInput: 'dd/MM/yyyy',
    },
    display: {
        dateInput: 'dd/MM/yyyy',
        monthYearLabel: 'MMM yyyy',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM',
    },
};

class DatePickerService {
    constructor() { }
    parseStringToDate(dateString) {
        return parse(dateString, DATEPICKER_FORMATS.parse.dateInput, new Date());
    }
    parseDateToString(date) {
        if (!date) {
            return '';
        }
        return format(date, 'dd/MM/yyyy');
    }
    isValid(value) {
        return typeof value === 'string' || value instanceof String ? isValid(this.parseStringToDate(value)) : isValid(value);
    }
    completeAndFormatDate(value) {
        if (!value) {
            return value;
        }
        const dateElements = value.split('/');
        const formattedInput = dateElements.length === 1
            ? this.completeAndFormatSingleDateString(dateElements[0])
            : this.completeAndFormatMultipleDateElements(dateElements);
        return this.isValid(formattedInput) ? formattedInput : null;
    }
    completeAndFormatSingleDateString(singleDateString) {
        try {
            const day = this.parseDay(singleDateString.substring(0, 2));
            const month = this.parseMonth(singleDateString.substring(2, 4));
            const year = this.parseYear(singleDateString.substring(4));
            return [day, month, year].join('/');
        }
        catch (error) {
            return null;
        }
    }
    parseDay(dayString) {
        return dayString.padStart(2, '0');
    }
    parseMonth(monthString) {
        const [currentMonth, _] = this.getCurrentMonthAndYear();
        return (monthString?.length > 0 ? monthString : currentMonth).padStart(2, '0');
    }
    parseYear(yearString) {
        const [_, currentYear] = this.getCurrentMonthAndYear();
        return this.padYear(yearString?.length > 0 ? yearString : currentYear, currentYear);
    }
    completeAndFormatMultipleDateElements(dateElements) {
        const [currentMonth, currentYear] = this.getCurrentMonthAndYear();
        const day = dateElements[0].padStart(2, '0');
        const month = (dateElements[1] ?? currentMonth).padStart(2, '0');
        const year = this.padYear(dateElements[2] ?? currentYear, currentYear);
        return [day, month, year].join('/');
    }
    getCurrentMonthAndYear() {
        return [(getMonth(startOfToday()) + 1).toString(), getYear(startOfToday()).toString()];
    }
    padYear(unformattedYearValue, currentYear) {
        const decennium = currentYear[2];
        const century = currentYear[1];
        const millennium = currentYear[0];
        return unformattedYearValue.padStart(2, decennium).padStart(3, century).padStart(4, millennium);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatePickerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatePickerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatePickerService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

const today = new Date();
class AnbDatepickerBaseComponent extends BaseFormElementComponent {
    constructor(formService, datePickerService) {
        super(formService);
        this.formService = formService;
        this.datePickerService = datePickerService;
        this.maxDate = new Date(today.getFullYear() + 120, today.getMonth(), today.getDate());
        this.minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
    }
    initializeDatepickerBase() {
        this.subs.add(this.fc.valueChanges.pipe(filter(() => !!this.fc.errors)).subscribe(() => {
            this.transformMaxDateError(this.fc.errors);
            this.transformMinDateError(this.fc.errors);
            this.addFormattingError(this.fc.errors);
        }));
        this.formattingError = new DatePickerError('wrongFormat', 'Ongeldige datum.', this.formControlName);
        this.minDateError = new DatePickerError('minDate', `Datum mag niet kleiner zijn dan: ${this.datePickerService.parseDateToString(this.minDate)}`, this.formControlName);
        this.maxDateError = new DatePickerError('maxDate', `Datum mag niet groter zijn dan: ${this.datePickerService.parseDateToString(this.maxDate)}`, this.formControlName);
    }
    addFormattingError(errors) {
        const parsingError = errors['matDatepickerParse'];
        if (!parsingError) {
            return;
        }
        ErrorUtils.removeErrors(['matDatepickerParse'], this.fc);
        ErrorUtils.addErrors({
            [this.formattingError.validationKey]: { message: this.formattingError.errorMessage },
        }, this.fc);
        this.addCustomError(this.formattingError);
    }
    transformMinDateError(errors) {
        const minDateError = errors['matDatepickerMin'];
        if (!minDateError) {
            return;
        }
        ErrorUtils.removeErrors(['matDatepickerMin'], this.fc);
        ErrorUtils.addErrors({
            [this.minDateError.validationKey]: { message: this.minDateError.errorMessage },
        }, this.fc);
        this.addCustomError(this.minDateError);
    }
    transformMaxDateError(errors) {
        const maxDateError = errors['matDatepickerMax'];
        if (!maxDateError) {
            return;
        }
        ErrorUtils.removeErrors(['matDatepickerMax'], this.fc);
        ErrorUtils.addErrors({
            [this.maxDateError.validationKey]: { message: this.maxDateError.errorMessage },
        }, this.fc);
        this.addCustomError(this.maxDateError);
    }
    addCustomError(error) {
        if (this.customErrorList?.length === 0) {
            this.customErrorList = [error];
            return;
        }
        if (this.customErrorList.findIndex((err) => err.validationKey === error.validationKey) === -1) {
            this.customErrorList.push(error);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerBaseComponent, deps: [{ token: AnbFormService }, { token: DatePickerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbDatepickerBaseComponent, selector: "ng-component", inputs: { maxDate: "maxDate", minDate: "minDate" }, usesInheritance: true, ngImport: i0, template: "" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerBaseComponent, decorators: [{
            type: Component,
            args: [{ template: "" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: DatePickerService }]; }, propDecorators: { maxDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }] } });

/** Custom header component for Material datepicker. */
class AnbDatepickerHeaderComponent {
    constructor(calendar, dateAdapter, dateFormats, cdr) {
        this.calendar = calendar;
        this.dateAdapter = dateAdapter;
        this.dateFormats = dateFormats;
        this.destroyed = new Subject();
        calendar.stateChanges.pipe(takeUntil(this.destroyed)).subscribe(() => cdr.markForCheck());
    }
    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }
    get periodLabel() {
        return this.dateAdapter.format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel).toLocaleUpperCase();
    }
    canGoPrevious() {
        const difference = differenceInMonths(this.calendar.minDate, this.calendar.activeDate);
        if (difference === 0) {
            return false;
        }
        return true;
    }
    canGoNext() {
        const difference = differenceInMonths(this.calendar.maxDate, this.calendar.activeDate);
        if (difference === 0) {
            return false;
        }
        return true;
    }
    previousClicked() {
        this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
    }
    nextClicked() {
        this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerHeaderComponent, deps: [{ token: i1$1.MatCalendar }, { token: i2$1.DateAdapter }, { token: MAT_DATE_FORMATS }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbDatepickerHeaderComponent, selector: "anb-datepicker-header", ngImport: i0, template: "<div class=\"anb-datepicker-header\">\n  <button\n    class=\"anb-datepicker-header-button mat-calendar-previous-button mat-icon-button mat-button-base\"\n    mat-icon-button\n    (click)=\"previousClicked()\"\n    [disabled]=\"!canGoPrevious()\"\n  ></button>\n  <span class=\"anb-datepicker-header-label\">{{ periodLabel }}</span>\n  <button\n    class=\"anb-datepicker-header-button mat-calendar-next-button mat-icon-button mat-button-base\"\n    mat-icon-button\n    (click)=\"nextClicked()\"\n    [disabled]=\"!canGoNext()\"\n  ></button>\n</div>\n", styles: [".anb-datepicker-header{display:flex;align-items:center;padding:.5em}.anb-datepicker-header-label{flex:1;height:1em;font-weight:500;text-align:center}.anb-datepicker-header-double-arrow .mat-icon{margin:-22%}.no-border{border:none}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-datepicker-header', template: "<div class=\"anb-datepicker-header\">\n  <button\n    class=\"anb-datepicker-header-button mat-calendar-previous-button mat-icon-button mat-button-base\"\n    mat-icon-button\n    (click)=\"previousClicked()\"\n    [disabled]=\"!canGoPrevious()\"\n  ></button>\n  <span class=\"anb-datepicker-header-label\">{{ periodLabel }}</span>\n  <button\n    class=\"anb-datepicker-header-button mat-calendar-next-button mat-icon-button mat-button-base\"\n    mat-icon-button\n    (click)=\"nextClicked()\"\n    [disabled]=\"!canGoNext()\"\n  ></button>\n</div>\n", styles: [".anb-datepicker-header{display:flex;align-items:center;padding:.5em}.anb-datepicker-header-label{flex:1;height:1em;font-weight:500;text-align:center}.anb-datepicker-header-double-arrow .mat-icon{margin:-22%}.no-border{border:none}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.MatCalendar }, { type: i2$1.DateAdapter }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DATE_FORMATS]
                }] }, { type: i0.ChangeDetectorRef }]; } });

class AnbDateAdapter extends NativeDateAdapter {
    parse(value) {
        return value ? parse(value, DATEPICKER_FORMATS.parse.dateInput, new Date()) : value;
    }
    format(date, displayFormat) {
        try {
            return format(date, displayFormat, { locale: nlBE });
        }
        catch (error) {
            return null;
        }
    }
    getFirstDayOfWeek() {
        return 1;
    }
    getDayOfWeekNames(style) {
        if (style === 'narrow') // We don't want the narrow style and minimally short style.
            style = 'short';
        return super.getDayOfWeekNames(style);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDateAdapter, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDateAdapter }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDateAdapter, decorators: [{
            type: Injectable
        }] });

class AnbDatepickerComponent extends AnbDatepickerBaseComponent {
    get isDisabled() {
        return this.disabled;
    }
    constructor(formService, datePickerService) {
        super(formService, datePickerService);
        this.formService = formService;
        this.datePickerService = datePickerService;
        this.allowInput = true;
        this.enableYearSelection = true;
        this.openOnInputClick = false;
        this.customPeriodpickerHeader = AnbDatepickerHeaderComponent;
    }
    ngOnInit() {
        super.ngOnInit();
        this.disabled$.subscribe((isDisabled) => {
            this.disableComponent(isDisabled);
        });
        this.disableComponent(this.isDisabled);
        this.fc = this.fc ?? this.form.get(this.formControlName);
        this.initializeDatepickerBase();
    }
    ngAfterViewInit() {
        this.makeInputFieldReadOnly(this.allowInput);
        this.disableIcon(this.isDisabled);
    }
    writeValue(obj) { }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) { }
    onBlur(event) {
        // this to make sure user has navigated away from date picker by clicking on anything but the calendar
        if (event.relatedTarget) {
            return;
        }
        this.form.get(this.formControlName).markAsDirty();
        this.form.get(this.formControlName).updateValueAndValidity();
        if (this.datePickerService.isValid(this.input?.nativeElement?.value)) {
            return;
        }
        const formattedValue = this.datePickerService.completeAndFormatDate(this.input?.nativeElement?.value);
        if (this.datePickerService.isValid(formattedValue)) {
            this.form.get(this.formControlName).setValue(this.datePickerService.parseStringToDate(formattedValue));
        }
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (changes.disabled) {
            this.disabled = changes.disabled.currentValue;
            this.disableComponent(this.disabled);
        }
        if (changes.allowInput) {
            this.makeInputFieldReadOnly(this.allowInput);
        }
    }
    autoCompleteDate() {
        const x = this.datePickerService;
        this.picker.select(x.parseStringToDate(x.completeAndFormatDate(this.input.nativeElement.value)));
        this.picker?.close();
    }
    openViaInput() {
        if (this.isDisabled || !this.openOnInputClick) {
            return;
        }
        this.picker?.open();
        this.focusOnInput();
    }
    openViaIcon() {
        if (this.isDisabled) {
            return;
        }
        this.picker?.open();
        this.focusOnInput();
    }
    focusOnInput() {
        if (this.input) {
            setTimeout(() => {
                this.input.nativeElement.focus();
            }, 0);
        }
    }
    disableComponent(isDisabled) {
        const _ = isDisabled
            ? this.form.get(this.formControlName).disable({ emitEvent: false })
            : this.form.get(this.formControlName).enable({ emitEvent: false });
        this.disableIcon(isDisabled);
    }
    makeInputFieldReadOnly(allowInput) {
        const _ = allowInput
            ? this.input?.nativeElement?.removeAttribute('readonly')
            : this.input?.nativeElement?.setAttribute('readonly', null);
    }
    disableIcon(isDisabled) {
        const _ = isDisabled
            ? this.pickerIcon?.nativeElement?.setAttribute('disabled', null)
            : this.pickerIcon?.nativeElement?.removeAttribute('disabled');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerComponent, deps: [{ token: AnbFormService }, { token: DatePickerService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbDatepickerComponent, selector: "anb-datepicker", inputs: { allowInput: "allowInput", enableYearSelection: "enableYearSelection", openOnInputClick: "openOnInputClick" }, providers: [
            { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMATS },
            { provide: DateAdapter, useClass: AnbDateAdapter },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: AnbDatepickerComponent,
                multi: true,
            },
            {
                provide: AnbDatepickerBaseComponent,
                useExisting: forwardRef(() => AnbDatepickerComponent),
                multi: true,
            },
            DatePickerService,
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["pickerInput"], descendants: true }, { propertyName: "picker", first: true, predicate: MatDatepicker, descendants: true }, { propertyName: "pickerIcon", first: true, predicate: ["icon"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"anb-input\">\n  <label for=\"{{ getId() }}\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span class=\"subvraag\">{{ subLabel }}</span>\n  <div>\n    <div class=\"datepicker\">\n      <div class=\"vl-input-group\">\n        <input\n          #pickerInput\n          ngDefaultControl\n          matInput\n          placeholder=\"dd/mm/yyyy\"\n          autocomplete=\"off\"\n          class=\"vl-datepicker__input-field vl-input-field vl-input-field--block js-vl-datepicker-input\"\n          [matDatepicker]=\"picker\"\n          [min]=\"minDate\"\n          [max]=\"maxDate\"\n          [formControlName]=\"formControlName\"\n          [class.input-field--error]=\"showError() && !isDisabled\"\n          [attr.id]=\"id\"\n          (click)=\"openViaInput()\"\n          (blur)=\"onBlur($event)\"\n          (keydown.enter)=\"autoCompleteDate()\"\n          (keydown.tab)=\"autoCompleteDate()\"\n        />\n        <button\n          #icon\n          type=\"button\"\n          name=\"button\"\n          class=\"vl-input-addon js-vl-datepicker-toggle js-vl-tooltip bg-white\"\n          [class.vl-button--disabled]=\"isDisabled\"\n          (click)=\"openViaIcon()\"\n        >\n          <i class=\"vl-vi vl-vi-calendar\" aria-hidden=\"true\"></i>\n          <span class=\"vl-u-visually-hidden\">Kies datum</span>\n        </button>\n        <mat-datepicker #picker [calendarHeaderComponent]=\"enableYearSelection === true ? null : customPeriodpickerHeader\"></mat-datepicker>\n      </div>\n    </div>\n\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n  </div>\n</div>\n", styles: [".flex-container{display:flex;flex-direction:column}::ng-deep .mat-calendar-controls .mat-icon-button:hover .mat-button-focus-overlay,::ng-deep .anb-datepicker-header-button:hover{opacity:1!important;background-color:#05c!important}::ng-deep .mat-calendar-next-button.mat-icon-button:hover,::ng-deep .mat-calendar-previous-button.mat-icon-button:hover{color:#fff!important}::ng-deep .mat-calendar-body-selected{background-color:#05c!important}::ng-deep .mat-calendar-body-label{vertical-align:bottom}::ng-deep .mat-calendar-body-cell:not(.mat-calendar-body-disabled):hover>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical),::ng-deep .cdk-keyboard-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical),::ng-deep .cdk-program-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical){background-color:#0000001a}::ng-deep .mat-calendar-table-header{text-transform:uppercase}::ng-deep .datepicker{width:15rem}.bg-white{background-color:#fff}button.vl-button--disabled{background-color:#f4f5f7}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i1$1.MatDatepicker, selector: "mat-datepicker", exportAs: ["matDatepicker"] }, { kind: "directive", type: i1$1.MatDatepickerInput, selector: "input[matDatepicker]", inputs: ["matDatepicker", "min", "max", "matDatepickerFilter"], exportAs: ["matDatepickerInput"] }, { kind: "pipe", type: SafePipe, name: "safe" }], viewProviders: [
            {
                provide: ControlContainer,
                useExisting: FormGroupDirective,
            },
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-datepicker', providers: [
                        { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMATS },
                        { provide: DateAdapter, useClass: AnbDateAdapter },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: AnbDatepickerComponent,
                            multi: true,
                        },
                        {
                            provide: AnbDatepickerBaseComponent,
                            useExisting: forwardRef(() => AnbDatepickerComponent),
                            multi: true,
                        },
                        DatePickerService,
                    ], viewProviders: [
                        {
                            provide: ControlContainer,
                            useExisting: FormGroupDirective,
                        },
                    ], template: "<div class=\"anb-input\">\n  <label for=\"{{ getId() }}\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span class=\"subvraag\">{{ subLabel }}</span>\n  <div>\n    <div class=\"datepicker\">\n      <div class=\"vl-input-group\">\n        <input\n          #pickerInput\n          ngDefaultControl\n          matInput\n          placeholder=\"dd/mm/yyyy\"\n          autocomplete=\"off\"\n          class=\"vl-datepicker__input-field vl-input-field vl-input-field--block js-vl-datepicker-input\"\n          [matDatepicker]=\"picker\"\n          [min]=\"minDate\"\n          [max]=\"maxDate\"\n          [formControlName]=\"formControlName\"\n          [class.input-field--error]=\"showError() && !isDisabled\"\n          [attr.id]=\"id\"\n          (click)=\"openViaInput()\"\n          (blur)=\"onBlur($event)\"\n          (keydown.enter)=\"autoCompleteDate()\"\n          (keydown.tab)=\"autoCompleteDate()\"\n        />\n        <button\n          #icon\n          type=\"button\"\n          name=\"button\"\n          class=\"vl-input-addon js-vl-datepicker-toggle js-vl-tooltip bg-white\"\n          [class.vl-button--disabled]=\"isDisabled\"\n          (click)=\"openViaIcon()\"\n        >\n          <i class=\"vl-vi vl-vi-calendar\" aria-hidden=\"true\"></i>\n          <span class=\"vl-u-visually-hidden\">Kies datum</span>\n        </button>\n        <mat-datepicker #picker [calendarHeaderComponent]=\"enableYearSelection === true ? null : customPeriodpickerHeader\"></mat-datepicker>\n      </div>\n    </div>\n\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n  </div>\n</div>\n", styles: [".flex-container{display:flex;flex-direction:column}::ng-deep .mat-calendar-controls .mat-icon-button:hover .mat-button-focus-overlay,::ng-deep .anb-datepicker-header-button:hover{opacity:1!important;background-color:#05c!important}::ng-deep .mat-calendar-next-button.mat-icon-button:hover,::ng-deep .mat-calendar-previous-button.mat-icon-button:hover{color:#fff!important}::ng-deep .mat-calendar-body-selected{background-color:#05c!important}::ng-deep .mat-calendar-body-label{vertical-align:bottom}::ng-deep .mat-calendar-body-cell:not(.mat-calendar-body-disabled):hover>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical),::ng-deep .cdk-keyboard-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical),::ng-deep .cdk-program-focused .mat-calendar-body-active>.mat-calendar-body-cell-content:not(.mat-calendar-body-selected):not(.mat-calendar-body-comparison-identical){background-color:#0000001a}::ng-deep .mat-calendar-table-header{text-transform:uppercase}::ng-deep .datepicker{width:15rem}.bg-white{background-color:#fff}button.vl-button--disabled{background-color:#f4f5f7}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: DatePickerService }]; }, propDecorators: { input: [{
                type: ViewChild,
                args: ['pickerInput']
            }], picker: [{
                type: ViewChild,
                args: [MatDatepicker]
            }], pickerIcon: [{
                type: ViewChild,
                args: ['icon']
            }], allowInput: [{
                type: Input
            }], enableYearSelection: [{
                type: Input
            }], openOnInputClick: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbDropdownComponent extends BaseFormElementComponent {
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.isHorizontal = false;
        /** Chosen item  */
        this.value = '';
    }
    writeValue(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onChange(event) {
        // Get value
        this.value = event.target.value;
        // update form
        this.setValue(this.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDropdownComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbDropdownComponent, selector: "anb-dropdown", inputs: { values: "values", value: "value", dropdownID: "dropdownID", placeholder: "placeholder" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbDropdownComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n\n    <select\n      class=\"webuniversum select col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"\n      style=\"text-overflow: ellipsis\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.placeholder]=\"placeholder && (value === '' || value === null || value === undefined)\"\n      [attr.name]=\"dropdownID\"\n      [attr.id]=\"'dropdown_' + dropdownID\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n    >\n      <option *ngIf=\"placeholder\" disabled selected hidden>{{ placeholder }}</option>\n      <option *ngFor=\"let v of values\" [value]=\"v.dropdownModelID\" [selected]=\"value === v.dropdownModelID\" [disabled]=\"disabled\">\n        {{ v.description }}\n      </option>\n    </select>\n  </label>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: ["", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2$2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-dropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbDropdownComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n\n    <select\n      class=\"webuniversum select col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"\n      style=\"text-overflow: ellipsis\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.placeholder]=\"placeholder && (value === '' || value === null || value === undefined)\"\n      [attr.name]=\"dropdownID\"\n      [attr.id]=\"'dropdown_' + dropdownID\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n    >\n      <option *ngIf=\"placeholder\" disabled selected hidden>{{ placeholder }}</option>\n      <option *ngFor=\"let v of values\" [value]=\"v.dropdownModelID\" [selected]=\"value === v.dropdownModelID\" [disabled]=\"disabled\">\n        {{ v.description }}\n      </option>\n    </select>\n  </label>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: ["::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { values: [{
                type: Input
            }], value: [{
                type: Input
            }], dropdownID: [{
                type: Input
            }], placeholder: [{
                type: Input
            }] } });

class AnbErrorContainerComponent {
    constructor() {
        this.formControlName = '';
        this.customErrorList = [];
        this.hidden = false;
    }
    showError() {
        return !this.isValid() && !this.getElement()?.pristine && this.hasErrors() && !this.hidden;
    }
    getError() {
        const element = this.getElement();
        if (element.errors) {
            const result = [];
            Object.keys(element.errors).forEach((keyError) => {
                const customError = this.customErrorList.filter((ce) => ((ce.formControlName.indexOf('.') === -1 && ce.formControlName === this.formControlName) ||
                    (ce.formControlName.indexOf('.') > -1 && ce.formControlName.split('.')[1] === this.formControlName)) &&
                    ce.validationKey === keyError)[0];
                if (customError) {
                    result.push(customError.errorMessage);
                }
            });
            if (result.length > 0) {
                return result.join('<br />');
            }
        }
    }
    isValid() {
        const element = this.getElement();
        return !element || element.valid;
    }
    hasErrors() {
        const element = this.getElement();
        if (element.errors) {
            return Object.keys(element.errors).length > 0;
        }
        return false;
    }
    getElement() {
        if (this.formControlName) {
            return this.form?.get(this.formControlName);
        }
        return this.form;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbErrorContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbErrorContainerComponent, selector: "anb-error-container", inputs: { form: "form", formControlName: "formControlName", customErrorList: "customErrorList", hidden: "hidden" }, ngImport: i0, template: "<div>\n  <div [ngClass]=\"{ 'input-field--error anb-form-error-container--error': showError() }\">\n    <ng-content></ng-content>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".anb-form-error-container--error{border:2px solid #db3434}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbErrorContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-error-container', template: "<div>\n  <div [ngClass]=\"{ 'input-field--error anb-form-error-container--error': showError() }\">\n    <ng-content></ng-content>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".anb-form-error-container--error{border:2px solid #db3434}\n"] }]
        }], propDecorators: { form: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], customErrorList: [{
                type: Input
            }], hidden: [{
                type: Input
            }] } });

/** The base component for each form. Each form must implement this component. */
class AnbBaseFormComponent {
    constructor() {
        this.aseEnum = ActivityStatusEnablementEnum;
    }
    /** Returns the length of a formArray */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getFormArrayLength(formArray) {
        return formArray.length;
    }
    /** Returns true when the formfield is required on a form */
    isRequiredField(field, form) {
        const form_field = form.get(field);
        if (!form_field.validator) {
            return false;
        }
        const validator = form_field.validator({});
        return validator && validator.required;
    }
}

class IfAseDirective {
    set ifAse(ase) {
        this.aseSrc = ase;
        this.updateView();
    }
    set ifAseForm(form) {
        this.formSrc = form;
        this.updateView();
    }
    constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateRef, viewContainerRef, anbFormService) {
        this.templateRef = templateRef;
        this.viewContainerRef = viewContainerRef;
        this.anbFormService = anbFormService;
    }
    ngOnInit() {
        this.updateView();
    }
    updateView() {
        if (this.formSrc && this.aseSrc) {
            this.isDisabled(this.aseSrc, this.formSrc).subscribe((disabled) => {
                this.viewContainerRef.clear();
                if (!disabled) {
                    // If condition is true add template to DOM
                    this.viewContainerRef.createEmbeddedView(this.templateRef);
                }
            });
        }
    }
    isDisabled(ase, form) {
        return new Observable((x) => {
            if (form && form.activityStatus) {
                form.activityStatus.subscribe((activityStatus) => {
                    const isDisabled = this.anbFormService.isDisabled(ase, activityStatus);
                    x.next(isDisabled);
                });
            }
            else {
                x.next(false);
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IfAseDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: IfAseDirective, selector: "[ifAse]", inputs: { ifAse: "ifAse", ifAseForm: "ifAseForm" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: IfAseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ifAse]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AnbFormService }]; }, propDecorators: { ifAse: [{
                type: Input
            }], ifAseForm: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @angular-eslint/no-output-on-prefix */
class AnbFormArrayEditorWrapperComponent extends AnbBaseFormComponent {
    get addForm() {
        if (!this.addFormGroupSrc) {
            this.addFormGroupSrc = this.getFormGroup(new GetFormGroup(this.formArray));
            this.addFormGroupSrc.valueChanges.subscribe(() => {
                this.handleAddingEmptyRow();
            });
        }
        return this.addFormGroupSrc;
    }
    get formArray() {
        return this.form.get(this.formArrayName);
    }
    get canEditInsert() {
        if (this.form && this.form.activityStatus) {
            return !this.anbFormService.isDisabled(ActivityStatusEnablementEnum.EditInsert, this.form.activityStatus.value);
        }
        return false;
    }
    constructor(anbFormService) {
        super();
        this.anbFormService = anbFormService;
        this.addButtonLabel = 'Voeg item toe';
        this.saveButtonLabel = 'Toevoegen';
        this.OkButtonLabel = 'OK';
        this.cancelButtonLabel = 'Annuleren';
        this.hideAddButton = false;
        this.hideRemoveButton = false;
        this.hideEditButton = false;
        this.allowStartEditingOnSelect = false;
        this.addNewRowDirectly = true;
        this.$toggleEditMode = new BehaviorSubject(null);
        this.$cancelEditRow = new BehaviorSubject(null);
        this.manualEditSave = false;
        this.buttonAlignment = 'right';
        this.showCancelButtonAsLink = false;
        /*
        Will add the added item on top, instead of bottom
        */
        this.addToTop = false;
        this.onAdd = new EventEmitter();
        this.onAddStart = new EventEmitter();
        this.onEdit = new EventEmitter();
        this.onRowEditCanceled = new EventEmitter();
        this.onEditStart = new EventEmitter();
        this.onDelete = new EventEmitter();
        this.onOpenChange = new EventEmitter();
        this.toggleAddForm = false;
        this.isInEditMode = false;
        this.toggleEditRow = [];
        this.currentEditRowValues = [];
        this.isAddingFirstRow = false;
        this.isAddingEmptyRow = false;
        this.subs = new Subscription();
    }
    ngOnInit() {
        this.initAddForm();
        const valueSub = this.form.get(this.formArrayName).valueChanges.subscribe(() => {
            this.initAddForm();
        });
        this.subs.add(valueSub);
        const toggleSub = this.$toggleEditMode.subscribe((toggleEditMode) => {
            if (!toggleEditMode) {
                return;
            }
            if (toggleEditMode.index !== -1) {
                this.toggleRowForEdit(toggleEditMode.index, toggleEditMode.form);
            }
        });
        this.subs.add(toggleSub);
        const cancelSub = this.$cancelEditRow.subscribe((cancelRowEdit) => {
            if (!cancelRowEdit) {
                return;
            }
            if (cancelRowEdit.index !== -1) {
                this.cancelEditRowOnClick(cancelRowEdit.index, cancelRowEdit.form);
            }
        });
        this.subs.add(cancelSub);
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    deleteRowOnClick(index, form, $event) {
        $event.stopPropagation();
        if (this.canDelete) {
            // Controle of we het item mogen verwijderen
            const item = this.formArray.controls[index];
            this.canDelete(item).then((result) => {
                if (result) {
                    // wanneer promise = true, dan verwijderen we het item
                    this.deleteItemConfirmDialog(index, form);
                }
            });
        }
        else {
            this.deleteItemConfirmDialog(index, form);
        }
    }
    deleteItemConfirmDialog(index, form) {
        if (this.showConfirmDialog) {
            const dialogSub = this.showConfirmDialog.subscribe((value) => {
                if (value) {
                    this.deleteItem(index, form);
                }
            });
            this.subs.add(dialogSub);
        }
        else {
            this.deleteItem(index, form);
        }
    }
    deleteItem(index, form) {
        if (this.onDelete) {
            this.onDelete.emit(form);
        }
        this.formArray.removeAt(index);
        this.form.markAsDirty();
    }
    selectRowOnClick(index, form) {
        if (this.allowStartEditingOnSelect && this.canEditInsert && !this.toggleAddForm) {
            // Wanneer er een andere rij in edit mode is, gaan we deze rij eerst valideren en bewaren
            if (this.currentEditRowForm && this.isInEditMode) {
                if (!this.isFormGroupValid(this.currentEditRowForm)) {
                    return;
                }
                if (this.onEdit) {
                    this.onEdit.emit(this.currentEditRowForm);
                }
            }
            this.currentEditRowValues[index] = form.value;
            this.toggleRowForEdit(index, form);
        }
    }
    editRowOnClick(index, form) {
        this.currentEditRowValues[index] = form.value;
        if (this.onEditStart) {
            this.onEditStart.emit(form);
        }
        this.onOpenChange.emit(true);
        this.toggleRowForEdit(index, form);
    }
    saveEditRowOnClick(index, form) {
        // With manual edit save, toggleRowForEdit must be triggered manually on caller project.
        if (this.manualEditSave && this.onEdit) {
            this.onEdit.emit(form);
            return;
        }
        if (!this.isFormGroupValid(form)) {
            return;
        }
        if (this.onEdit) {
            this.onEdit.emit(form);
        }
        this.onOpenChange.emit(false);
        this.toggleRowForEdit(index, form);
    }
    cancelEditRowOnClick(index, form) {
        if (this.currentEditRowValues[index]) {
            form.setValue(this.currentEditRowValues[index]);
        }
        this.onOpenChange.emit(false);
        this.onRowEditCanceled.emit(form);
        this.toggleRowForEdit(index, form);
    }
    saveAddRowOnClick() {
        if (!this.isFormGroupValid(this.addForm)) {
            return;
        }
        if (this.onAdd) {
            this.onAdd.emit(this.addForm);
        }
        this.onOpenChange.emit(false);
        if (this.isAddingEmptyRow) {
            this.toggleEditRow = [];
            this.isAddingEmptyRow = false;
            this.isAddingFirstRow = false;
        }
        else {
            if (this.addToTop) {
                this.formArray.insert(0, this.addForm);
            }
            else {
                this.formArray.push(this.addForm);
            }
        }
        this.form.markAsDirty();
        this.addFormGroupSrc = null;
        this.toggleAddForm = false;
    }
    cancelAddRowOnClick() {
        this.addForm.reset();
        this.addFormGroupSrc = null;
        if (this.isAddingEmptyRow) {
            if (this.addToTop) {
                this.formArray.removeAt(0);
            }
            else {
                this.formArray.removeAt(this.formArray.length - 1);
            }
            this.toggleEditRow = [];
            this.isAddingEmptyRow = false;
            this.isAddingFirstRow = false;
        }
        this.toggleAddForm = this.formArray.length <= 0 && this.addNewRowDirectly;
        this.isInEditMode = false;
    }
    addRowOnClick() {
        this.toggleAddForm = !this.toggleAddForm;
        if (this.onAddStart) {
            this.onAddStart.emit(this.addForm);
        }
        this.onOpenChange.emit(this.toggleAddForm);
        this.handleAddingEmptyRow(true);
    }
    initAddForm() {
        if (this.isAddingEmptyRow) {
            return;
        }
        if (!this.hideAddButton && this.formArray.length === 0 && this.addNewRowDirectly) {
            this.toggleAddForm = true;
            this.onOpenChange.emit(true);
        }
        else {
            this.toggleAddForm = false;
        }
    }
    toggleRowForEdit(index, form) {
        let isToggled = false;
        if (!this.toggleEditRow[index]) {
            isToggled = true;
        }
        this.toggleEditRow = []; // reset all
        this.toggleEditRow[index] = isToggled;
        this.isInEditMode = isToggled;
        this.currentEditRowForm = isToggled ? form : null;
    }
    isFormGroupValid(formGroup) {
        if (!formGroup.valid) {
            this.anbFormService.markAsDirty(formGroup);
        }
        return formGroup.valid;
    }
    handleAddingEmptyRow(forceAdd = false) {
        if (this.formArray.length <= 0 || forceAdd) {
            this.isAddingEmptyRow = true;
            this.isAddingFirstRow = this.formArray.length === 0;
            this.toggleEditRow = []; // reset all
            if (this.addToTop) {
                this.formArray.insert(0, this.addForm);
                this.toggleEditRow[0] = true;
            }
            else {
                this.formArray.push(this.addForm);
                this.toggleEditRow[this.formArray.length - 1] = true;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormArrayEditorWrapperComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbFormArrayEditorWrapperComponent, selector: "anb-formarray-editor-wrapper", inputs: { form: "form", formArrayName: "formArrayName", getFormGroup: "getFormGroup", columnsHeaderTemplate: "columnsHeaderTemplate", columnsTemplate: "columnsTemplate", addTemplate: "addTemplate", editTemplate: "editTemplate", extraAddButtonsTemplate: "extraAddButtonsTemplate", addButtonLabel: "addButtonLabel", saveButtonLabel: "saveButtonLabel", OkButtonLabel: "OkButtonLabel", cancelButtonLabel: "cancelButtonLabel", hideAddButton: "hideAddButton", hideRemoveButton: "hideRemoveButton", hideEditButton: "hideEditButton", allowStartEditingOnSelect: "allowStartEditingOnSelect", addNewRowDirectly: "addNewRowDirectly", $toggleEditMode: "$toggleEditMode", $cancelEditRow: "$cancelEditRow", manualEditSave: "manualEditSave", buttonAlignment: "buttonAlignment", showCancelButtonAsLink: "showCancelButtonAsLink", addToTop: "addToTop", showConfirmDialog: "showConfirmDialog", canDelete: "canDelete" }, outputs: { onAdd: "onAdd", onAddStart: "onAddStart", onEdit: "onEdit", onRowEditCanceled: "onRowEditCanceled", onEditStart: "onEditStart", onDelete: "onDelete", onOpenChange: "onOpenChange" }, usesInheritance: true, ngImport: i0, template: "<section *ngIf=\"getFormArrayLength(form.get(formArrayName)) > 0 && !isAddingFirstRow\" [formGroup]=\"form\">\n  <table class=\"vl-data-table vl-data-table--hover vl-data-table--middle vl-u-spacer\">\n    <thead>\n      <tr>\n        <ng-container [ngTemplateOutlet]=\"columnsHeaderTemplate\"> </ng-container>\n        <th></th>\n      </tr>\n    </thead>\n    <tbody>\n      <ng-container [formArrayName]=\"formArrayName\" *ngFor=\"let row of formArray.controls; let i = index\">\n        <tr [formGroupName]=\"i\" [formGroup]=\"row\" *ngIf=\"!toggleEditRow[i]\" (click)=\"selectRowOnClick(i, row)\">\n          <ng-container [ngTemplateOutlet]=\"columnsTemplate\" [ngTemplateOutletContext]=\"{ $implicit: row }\"> </ng-container>\n          <td class=\"vl-u-align-right\" nowrap>\n            <div class=\"vl-action-group vl-action-group--align-right\" *ifAse=\"aseEnum.EditInsert; form: form\">\n              <a\n                href=\"javascript:void(0)\"\n                class=\"vl-link\"\n                *ngIf=\"!toggleAddForm && !isInEditMode && !hideEditButton\"\n                (click)=\"editRowOnClick(i, row)\"\n              >\n                Bewerk\n              </a>\n\n              <a\n                href=\"javascript:void(0)\"\n                class=\"vl-link\"\n                *ngIf=\"!toggleAddForm && !isInEditMode && !hideRemoveButton\"\n                (click)=\"deleteRowOnClick(i, row, $event)\"\n              >\n                <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span>\n                Verwijder\n              </a>\n            </div>\n          </td>\n        </tr>\n        <tr *ngIf=\"toggleEditRow[i] && !toggleAddForm\">\n          <td colspan=\"20\">\n            <form [formGroup]=\"row\" (ngSubmit)=\"saveEditRowOnClick(i, row)\">\n              <ng-container [ngTemplateOutlet]=\"editTemplate\" [ngTemplateOutletContext]=\"{ $implicit: row }\"> </ng-container>\n              <div\n                class=\"vl-action-group\"\n                [ngClass]=\"{\n                  'vl-action-group--align-left': buttonAlignment === 'left',\n                  'vl-action-group--align-right': buttonAlignment === 'right',\n                  'flex-button-group flex-reverse': buttonAlignment === 'left'\n                }\"\n              >\n                <ng-container *ngIf=\"showCancelButtonAsLink\">\n                  <a (click)=\"cancelEditRowOnClick(i, row)\" class=\"vl-link\">\n                    <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-close\"></span>{{ cancelButtonLabel }}\n                  </a>\n                </ng-container>\n                <ng-container *ngIf=\"!showCancelButtonAsLink\">\n                  <button class=\"vl-button vl-button--secondary\" type=\"button\" (click)=\"cancelEditRowOnClick(i, row)\">\n                    {{ cancelButtonLabel }}\n                  </button>\n                </ng-container>\n                <button class=\"vl-button\" type=\"submit\">\n                  {{ OkButtonLabel }}\n                </button>\n              </div>\n            </form>\n          </td>\n        </tr>\n      </ng-container>\n    </tbody>\n  </table>\n</section>\n\n<ng-container *ifAse=\"aseEnum.EditInsert; form: form\">\n  <section *ngIf=\"toggleAddForm\" class=\"vl-form__group\">\n    <form [formGroup]=\"addForm\" (ngSubmit)=\"saveAddRowOnClick()\">\n      <ng-container [ngTemplateOutlet]=\"addTemplate\" [ngTemplateOutletContext]=\"{ $implicit: addForm }\"> </ng-container>\n\n      <div\n        class=\"vl-action-group\"\n        [ngClass]=\"{\n          'vl-action-group--align-left': buttonAlignment === 'left',\n          'vl-action-group--align-right': buttonAlignment === 'right',\n          'flex-button-group flex-reverse': buttonAlignment === 'left'\n        }\"\n      >\n        <ng-container *ngIf=\"showCancelButtonAsLink\">\n          <a\n            *ngIf=\"isAddingEmptyRow && (addForm?.dirty || !isAddingFirstRow || (isAddingFirstRow && !addNewRowDirectly))\"\n            (click)=\"cancelAddRowOnClick()\"\n            class=\"vl-link\"\n          >\n            <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-close\"></span>{{ cancelButtonLabel }}\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"!showCancelButtonAsLink\">\n          <button\n            class=\"vl-button vl-button--secondary\"\n            type=\"button\"\n            *ngIf=\"isAddingEmptyRow && (addForm?.dirty || !isAddingFirstRow || (isAddingFirstRow && !addNewRowDirectly))\"\n            (click)=\"cancelAddRowOnClick()\"\n          >\n            {{ cancelButtonLabel }}\n          </button>\n        </ng-container>\n        <button class=\"vl-button\" type=\"submit\" *ngIf=\"addForm?.dirty\">\n          {{ saveButtonLabel }}\n        </button>\n      </div>\n    </form>\n  </section>\n</ng-container>\n<section *ngIf=\"!hideAddButton && !toggleAddForm && !isInEditMode\">\n  <ng-container *ifAse=\"aseEnum.EditInsert; form: form\">\n    <div class=\"vl-action-group vl-action-group--align-left\">\n      <button class=\"vl-button vl-button--secondary vl-button--icon-before vl-button--narrow\" type=\"button\" (click)=\"addRowOnClick()\">\n        <i class=\"vl-button__icon vl-button__icon--before vl-vi vl-vi-add\" aria-hidden=\"true\"></i>\n        {{ addButtonLabel }}\n      </button>\n\n      <ng-container [ngTemplateOutlet]=\"extraAddButtonsTemplate\"> </ng-container>\n    </div>\n  </ng-container>\n</section>\n", styles: [".flex-button-group{display:flex;gap:1.5rem}.flex-reverse{flex-direction:row-reverse}.vl-action-group--align-left{justify-content:left}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2$2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormGroupName, selector: "[formGroupName]", inputs: ["formGroupName"] }, { kind: "directive", type: i2$2.FormArrayName, selector: "[formArrayName]", inputs: ["formArrayName"] }, { kind: "directive", type: IfAseDirective, selector: "[ifAse]", inputs: ["ifAse", "ifAseForm"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormArrayEditorWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-formarray-editor-wrapper', template: "<section *ngIf=\"getFormArrayLength(form.get(formArrayName)) > 0 && !isAddingFirstRow\" [formGroup]=\"form\">\n  <table class=\"vl-data-table vl-data-table--hover vl-data-table--middle vl-u-spacer\">\n    <thead>\n      <tr>\n        <ng-container [ngTemplateOutlet]=\"columnsHeaderTemplate\"> </ng-container>\n        <th></th>\n      </tr>\n    </thead>\n    <tbody>\n      <ng-container [formArrayName]=\"formArrayName\" *ngFor=\"let row of formArray.controls; let i = index\">\n        <tr [formGroupName]=\"i\" [formGroup]=\"row\" *ngIf=\"!toggleEditRow[i]\" (click)=\"selectRowOnClick(i, row)\">\n          <ng-container [ngTemplateOutlet]=\"columnsTemplate\" [ngTemplateOutletContext]=\"{ $implicit: row }\"> </ng-container>\n          <td class=\"vl-u-align-right\" nowrap>\n            <div class=\"vl-action-group vl-action-group--align-right\" *ifAse=\"aseEnum.EditInsert; form: form\">\n              <a\n                href=\"javascript:void(0)\"\n                class=\"vl-link\"\n                *ngIf=\"!toggleAddForm && !isInEditMode && !hideEditButton\"\n                (click)=\"editRowOnClick(i, row)\"\n              >\n                Bewerk\n              </a>\n\n              <a\n                href=\"javascript:void(0)\"\n                class=\"vl-link\"\n                *ngIf=\"!toggleAddForm && !isInEditMode && !hideRemoveButton\"\n                (click)=\"deleteRowOnClick(i, row, $event)\"\n              >\n                <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span>\n                Verwijder\n              </a>\n            </div>\n          </td>\n        </tr>\n        <tr *ngIf=\"toggleEditRow[i] && !toggleAddForm\">\n          <td colspan=\"20\">\n            <form [formGroup]=\"row\" (ngSubmit)=\"saveEditRowOnClick(i, row)\">\n              <ng-container [ngTemplateOutlet]=\"editTemplate\" [ngTemplateOutletContext]=\"{ $implicit: row }\"> </ng-container>\n              <div\n                class=\"vl-action-group\"\n                [ngClass]=\"{\n                  'vl-action-group--align-left': buttonAlignment === 'left',\n                  'vl-action-group--align-right': buttonAlignment === 'right',\n                  'flex-button-group flex-reverse': buttonAlignment === 'left'\n                }\"\n              >\n                <ng-container *ngIf=\"showCancelButtonAsLink\">\n                  <a (click)=\"cancelEditRowOnClick(i, row)\" class=\"vl-link\">\n                    <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-close\"></span>{{ cancelButtonLabel }}\n                  </a>\n                </ng-container>\n                <ng-container *ngIf=\"!showCancelButtonAsLink\">\n                  <button class=\"vl-button vl-button--secondary\" type=\"button\" (click)=\"cancelEditRowOnClick(i, row)\">\n                    {{ cancelButtonLabel }}\n                  </button>\n                </ng-container>\n                <button class=\"vl-button\" type=\"submit\">\n                  {{ OkButtonLabel }}\n                </button>\n              </div>\n            </form>\n          </td>\n        </tr>\n      </ng-container>\n    </tbody>\n  </table>\n</section>\n\n<ng-container *ifAse=\"aseEnum.EditInsert; form: form\">\n  <section *ngIf=\"toggleAddForm\" class=\"vl-form__group\">\n    <form [formGroup]=\"addForm\" (ngSubmit)=\"saveAddRowOnClick()\">\n      <ng-container [ngTemplateOutlet]=\"addTemplate\" [ngTemplateOutletContext]=\"{ $implicit: addForm }\"> </ng-container>\n\n      <div\n        class=\"vl-action-group\"\n        [ngClass]=\"{\n          'vl-action-group--align-left': buttonAlignment === 'left',\n          'vl-action-group--align-right': buttonAlignment === 'right',\n          'flex-button-group flex-reverse': buttonAlignment === 'left'\n        }\"\n      >\n        <ng-container *ngIf=\"showCancelButtonAsLink\">\n          <a\n            *ngIf=\"isAddingEmptyRow && (addForm?.dirty || !isAddingFirstRow || (isAddingFirstRow && !addNewRowDirectly))\"\n            (click)=\"cancelAddRowOnClick()\"\n            class=\"vl-link\"\n          >\n            <span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-close\"></span>{{ cancelButtonLabel }}\n          </a>\n        </ng-container>\n        <ng-container *ngIf=\"!showCancelButtonAsLink\">\n          <button\n            class=\"vl-button vl-button--secondary\"\n            type=\"button\"\n            *ngIf=\"isAddingEmptyRow && (addForm?.dirty || !isAddingFirstRow || (isAddingFirstRow && !addNewRowDirectly))\"\n            (click)=\"cancelAddRowOnClick()\"\n          >\n            {{ cancelButtonLabel }}\n          </button>\n        </ng-container>\n        <button class=\"vl-button\" type=\"submit\" *ngIf=\"addForm?.dirty\">\n          {{ saveButtonLabel }}\n        </button>\n      </div>\n    </form>\n  </section>\n</ng-container>\n<section *ngIf=\"!hideAddButton && !toggleAddForm && !isInEditMode\">\n  <ng-container *ifAse=\"aseEnum.EditInsert; form: form\">\n    <div class=\"vl-action-group vl-action-group--align-left\">\n      <button class=\"vl-button vl-button--secondary vl-button--icon-before vl-button--narrow\" type=\"button\" (click)=\"addRowOnClick()\">\n        <i class=\"vl-button__icon vl-button__icon--before vl-vi vl-vi-add\" aria-hidden=\"true\"></i>\n        {{ addButtonLabel }}\n      </button>\n\n      <ng-container [ngTemplateOutlet]=\"extraAddButtonsTemplate\"> </ng-container>\n    </div>\n  </ng-container>\n</section>\n", styles: [".flex-button-group{display:flex;gap:1.5rem}.flex-reverse{flex-direction:row-reverse}.vl-action-group--align-left{justify-content:left}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { form: [{
                type: Input
            }], formArrayName: [{
                type: Input
            }], getFormGroup: [{
                type: Input
            }], columnsHeaderTemplate: [{
                type: Input
            }], columnsTemplate: [{
                type: Input
            }], addTemplate: [{
                type: Input
            }], editTemplate: [{
                type: Input
            }], extraAddButtonsTemplate: [{
                type: Input
            }], addButtonLabel: [{
                type: Input
            }], saveButtonLabel: [{
                type: Input
            }], OkButtonLabel: [{
                type: Input
            }], cancelButtonLabel: [{
                type: Input
            }], hideAddButton: [{
                type: Input
            }], hideRemoveButton: [{
                type: Input
            }], hideEditButton: [{
                type: Input
            }], allowStartEditingOnSelect: [{
                type: Input
            }], addNewRowDirectly: [{
                type: Input
            }], $toggleEditMode: [{
                type: Input
            }], $cancelEditRow: [{
                type: Input
            }], manualEditSave: [{
                type: Input
            }], buttonAlignment: [{
                type: Input
            }], showCancelButtonAsLink: [{
                type: Input
            }], addToTop: [{
                type: Input
            }], showConfirmDialog: [{
                type: Input
            }], canDelete: [{
                type: Input
            }], onAdd: [{
                type: Output
            }], onAddStart: [{
                type: Output
            }], onEdit: [{
                type: Output
            }], onRowEditCanceled: [{
                type: Output
            }], onEditStart: [{
                type: Output
            }], onDelete: [{
                type: Output
            }], onOpenChange: [{
                type: Output
            }] } });
class GetFormGroup {
    constructor(formArray) {
        this.formArray = formArray;
    }
}

class DropdownModel {
    constructor(dropdownModelID, description, tooltip, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalData, objectID, objectType) {
        this.dropdownModelID = dropdownModelID;
        this.description = description;
        this.tooltip = tooltip;
        this.additionalData = additionalData;
        this.objectID = objectID;
        this.objectType = objectType;
        if (!objectID) {
            objectID = dropdownModelID;
        }
    }
}

const CACHE_SIZE$1 = 1;
class BasisregisterService {
    constructor() {
        this.http = inject(HttpClient);
        this.configuratieService = inject(AnbNgConfiguratieService);
        this.basisregisterUrlKey = '/appl/configuratieservice/basisregister-url';
        this.municipalitiesCache = [];
    }
    get basisregisterUrl() {
        return this.configuratieService.getValue(this.basisregisterUrlKey);
    }
    getMunicipalitySuggestion(q) {
        return this.getMunicipalities().pipe(map((x) => x.filter((y) => y.name?.toLowerCase().indexOf(q.toLowerCase()) !== -1).slice(0, 10)));
    }
    getPostcalCodeSuggestion(q) {
        return this.getMunicipalities().pipe(map((x) => x.filter((y) => y.postalCode?.toLowerCase().indexOf(q.toLowerCase()) !== -1).slice(0, 10)));
    }
    getMunicipalities(gewestCode = null) {
        if (!this.municipalitiesCache[gewestCode]) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const params = { GeldigheidsDatum: new Date().toISOString() };
            if (gewestCode) {
                params.GewestCode = gewestCode;
            }
            this.municipalitiesCache[gewestCode] = this.http
                .get(`${this.basisregisterUrl}/api/v1/gemeenten?type=VlaamseGemeenten`, { responseType: 'json', params: params })
                .pipe(map((x) => sortBy(x.filter((f) => f.postCode), (u) => u.gemeenteNaam).map((y, index) => ({
                index: index + 1,
                name: y.gemeenteNaam,
                postalCode: y.postCode,
                nisCode: y.gemeenteNisCode,
            }))), retry(3), catchError(this.handleError), shareReplay(CACHE_SIZE$1));
        }
        return this.municipalitiesCache[gewestCode];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError(err) {
        return throwError(err);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BasisregisterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BasisregisterService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BasisregisterService, decorators: [{
            type: Injectable
        }] });

const CACHE_SIZE = 1;
class MasterEnReferentieDataService {
    get referentieDataUrl() {
        return this.configuratieService.getValue(this.referentieDataUrlKey);
    }
    constructor(http, configuratieService) {
        this.http = http;
        this.configuratieService = configuratieService;
        this.referentieDataUrlKey = '/appl/configuratieservice/referentiedata-url';
    }
    getCountries() {
        if (!this.countriesCache$) {
            this.countriesCache$ = this.http
                .get(`${this.referentieDataUrl}/api/v1/Land/landLijst`, {
                responseType: 'json',
                params: { GeldigheidsDatum: new Date().toISOString() },
            })
                .pipe(map((x) => sortBy(x.response.filter((f) => f.isO3Code && f.naam), (u) => u.naam).map((y, index) => ({
                index: index,
                isO3Code: y.isO3Code,
                name: y.naam,
            }))), retry(3), catchError(this.handleError), shareReplay(CACHE_SIZE));
        }
        return this.countriesCache$;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError(err) {
        return throwError(err);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MasterEnReferentieDataService, deps: [{ token: i1$2.HttpClient }, { token: i2$3.AnbNgConfiguratieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MasterEnReferentieDataService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MasterEnReferentieDataService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: i2$3.AnbNgConfiguratieService }]; } });

const AnbInputAddressConstants = {
    foutePostcodeGemeenteErrorKey: 'foutePostcodeGemeente',
    foutePostcodeGemeenteErrorMessage: 'Ongeldige combinatie gemeente en postcode.',
};
const ANB_INPUT_ADDRESS_FORM_PARAMS = {
    straat: 'straat',
    huisnummer: 'huisnummer',
    bus: 'bus',
    postcode: 'postcode',
    gemeente: 'gemeente',
    land: 'land',
};

class AnbInputAddressUtils {
    static addCustomErrorsForInputAddressComponent(customErrorList) {
        if (!customErrorList) {
            customErrorList = [];
        }
        customErrorList.push(new CustomError(ANB_INPUT_ADDRESS_FORM_PARAMS.postcode, AnbInputAddressConstants.foutePostcodeGemeenteErrorKey, AnbInputAddressConstants.foutePostcodeGemeenteErrorMessage));
        customErrorList.push(new CustomError(ANB_INPUT_ADDRESS_FORM_PARAMS.gemeente, AnbInputAddressConstants.foutePostcodeGemeenteErrorKey, AnbInputAddressConstants.foutePostcodeGemeenteErrorMessage));
        customErrorList.push(new CustomError('', AnbInputAddressConstants.foutePostcodeGemeenteErrorKey, AnbInputAddressConstants.foutePostcodeGemeenteErrorMessage));
        return customErrorList;
    }
}

var PredefinedMaskEnum;
(function (PredefinedMaskEnum) {
    PredefinedMaskEnum["INSZ"] = "INSZ";
    PredefinedMaskEnum["KBO"] = "KBO";
    PredefinedMaskEnum["IBAN"] = "IBAN";
    PredefinedMaskEnum["BTW"] = "BTW";
})(PredefinedMaskEnum || (PredefinedMaskEnum = {}));

const KBOMask = {
    mask: [
        ...new Array(4).fill(/\d/),
        '.',
        ...new Array(3).fill(/\d/),
        '.',
        ...new Array(3).fill(/\d/),
    ]
};
const IBANMask = {
    postprocessors: [maskitoPrefixPostprocessorGenerator('BE')],
    plugins: [maskitoAddOnFocusPlugin('BE'), maskitoRemoveOnBlurPlugin('BE')],
    mask: [
        'BE',
        ...new Array(2).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
    ]
};
const BTWMask = {
    postprocessors: [maskitoPrefixPostprocessorGenerator('BE')],
    plugins: [maskitoAddOnFocusPlugin('BE'), maskitoRemoveOnBlurPlugin('BE')],
    mask: [
        'BE',
        ...new Array(4).fill(/\d/),
        '.',
        ...new Array(3).fill(/\d/),
        '.',
        ...new Array(3).fill(/\d/),
    ]
};
const INSZMask = {
    mask: [
        ...new Array(2).fill(/\d/),
        '.',
        ...new Array(2).fill(/\d/),
        '.',
        ...new Array(2).fill(/\d/),
        '-',
        ...new Array(3).fill(/\d/),
        '.',
        ...new Array(2).fill(/\d/)
    ]
};

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbInputTextComponent extends BaseFormElementComponent {
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        this.setMask();
        if (this.mask) {
            this.value = maskitoTransform(String(value) || '', this.mask);
        }
        else {
            this.value = value || '';
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService); // Constructors for derived classes must contain a 'super' call.
        this.formService = formService;
        this.inputType = 'text';
        this.rawValue = false;
        this.predefinedMask = null;
        this.value = '';
        this.isTouched = false;
    }
    ngOnInit() {
        super.ngOnInit();
        this.setMask();
    }
    setMask() {
        if (this.predefinedMask) {
            switch (this.predefinedMask) {
                case PredefinedMaskEnum.KBO:
                    this.mask = KBOMask;
                    break;
                case PredefinedMaskEnum.IBAN:
                    this.mask = IBANMask;
                    break;
                case PredefinedMaskEnum.BTW:
                    this.mask = BTWMask;
                    break;
                case PredefinedMaskEnum.INSZ:
                    this.mask = INSZMask;
            }
        }
    }
    getTrueValue(value) {
        if ((this.mask || this.predefinedMask) && this.rawValue)
            return value.replace(/[^a-zA-Z0-9]/g, '');
        return value;
    }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    onChange(event) {
        this.value = event.target.value;
        // Update form
        this.propagateChange(this.getTrueValue(this.value));
        this.valueChange(this.getTrueValue(this.value));
    }
    onFocus() {
        this.isTouched = true;
    }
    onBlur() {
        this.isTouched = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputTextComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputTextComponent, selector: "anb-input-text", inputs: { placeholder: "placeholder", maxlength: "maxlength", inputType: "inputType", rawValue: "rawValue", autocomplete: "autocomplete", mask: "mask", predefinedMask: "predefinedMask" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputTextComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputTextComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "textInput", first: true, predicate: ["textInput"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <!-- [class]=\"class\" -->\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    [type]=\"inputType\"\n    placeholder=\"\"\n    [value]=\"value\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n    (focus)=\"onFocus()\"\n    [maskito]=\"mask\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxlength\"\n    [attr.autocomplete]=\"autocomplete\"\n    #textInput\n  />\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i3.MaskitoDirective, selector: "[maskito]", inputs: ["maskito", "maskitoElement"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputTextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-text', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputTextComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputTextComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <!-- [class]=\"class\" -->\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    [type]=\"inputType\"\n    placeholder=\"\"\n    [value]=\"value\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n    (focus)=\"onFocus()\"\n    [maskito]=\"mask\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxlength\"\n    [attr.autocomplete]=\"autocomplete\"\n    #textInput\n  />\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { textInput: [{
                type: ViewChild,
                args: ['textInput', { static: true }]
            }], placeholder: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], inputType: [{
                type: Input
            }], rawValue: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], mask: [{
                type: Input
            }], predefinedMask: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbSearchDropdownComponent extends BaseFormElementComponent {
    constructor(formService, elementRef, renderer) {
        super(formService);
        this.formService = formService;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.isHorizontal = false;
        /** Chosen item */
        this.value = '';
        // ID for empty value
        this.canSelectEmptyID = null;
        // Label for empty value
        this.canSelectEmptyLabel = '-- Selecteer een optie --';
        this.searchValue = '';
        this.expanded = false;
    }
    ngOnInit() {
        super.ngOnInit();
        this.filterdValues = Object.assign([], this.values);
    }
    ngOnDestroy() {
        this.destroyOutsideClickHandler();
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (changes.values !== undefined) {
            this.expanded = false;
            this.destroyOutsideClickHandler();
            if (this.value) {
                const currentValues = changes.values.currentValue;
                const selectedDropdownModelValue = currentValues?.find((x) => x.dropdownModelID === this.value);
                if (selectedDropdownModelValue === undefined) {
                    this.resetValue();
                }
                this.updateSelectedValueDescription();
            }
        }
    }
    writeValue(value) {
        if (this.value !== value) {
            this.value = value;
        }
        this.updateSelectedValueDescription();
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    resetSearch() {
        this.searchValue = '';
        this.filterdValues = this.values;
        if (this.canSelectEmpty && this.filterdValues.filter((x) => x.dropdownModelID === this.canSelectEmptyID).length === 0) {
            this.filterdValues.unshift(new DropdownModel(this.canSelectEmptyID, this.canSelectEmptyLabel));
        }
    }
    resetValue() {
        this.value = '';
        // update form
        // this.propagateChange(this.value);
        // is done by the setValue function
        this.setValue(this.value);
    }
    updateSelectedValueDescription() {
        let description = '';
        if (this.value) {
            const selectedDropdownModelValue = this.values?.find((x) => x.dropdownModelID === this.value);
            if (selectedDropdownModelValue) {
                description = selectedDropdownModelValue.description;
            }
        }
        else {
            if (this.canSelectEmptyLabel) {
                description = this.canSelectEmptyLabel;
            }
        }
        this.selectedValueDescription = description;
    }
    onExpandDropdownButtonClick() {
        this.resetSearch();
        this.expanded = !this.expanded;
        if (this.expanded) {
            setTimeout(() => {
                if (this.searchInput) {
                    this.searchInput.nativeElement.focus();
                }
            }, 0);
            // We doen dit hier omdat anders de document clickhandler meermaals getriggerd gaat worden.
            // Dit zorgde voor performantie problemen wanneer er meerdere componenten op een pagina stonden
            this.outsideClickListenerFn = this.renderer.listen('document', 'click', (e) => {
                if (this.expanded && !this.elementRef.nativeElement.contains(e.target)) {
                    this.onExpandDropdownButtonClick();
                    this.destroyOutsideClickHandler();
                }
            });
        }
    }
    onSearchChange(event) {
        const searchValue = event.target.value;
        this.filterdValues = this.values.filter((x) => x.description.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
    }
    onSelectButtonClick(event, value) {
        // Get value
        this.value = value.dropdownModelID;
        this.selectedValueDescription = value.description;
        this.expanded = false;
        this.destroyOutsideClickHandler();
        // update form
        // is done by the setValue function
        // this.propagateChange(this.value);
        this.setValue(this.value);
    }
    destroyOutsideClickHandler() {
        if (this.outsideClickListenerFn) {
            this.outsideClickListenerFn();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbSearchDropdownComponent, deps: [{ token: AnbFormService }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbSearchDropdownComponent, selector: "anb-search-dropdown", inputs: { values: "values", value: "value", dropdownID: "dropdownID", canSelectEmpty: "canSelectEmpty", canSelectEmptyID: "canSelectEmptyID", canSelectEmptyLabel: "canSelectEmptyLabel" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbSearchDropdownComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "searchInput", first: true, predicate: ["searchInput"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\" style=\"z-index: 80000\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"></label>\n  <span *ngIf=\"label\" class=\"vraag form__label\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <div class=\"js-select\">\n    <button\n      type=\"button\"\n      class=\"js-select__input\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.button--disabled]=\"disabled\"\n      [class.placeholder]=\"value === canSelectEmptyID || value === '' || value === null || value === undefined\"\n      [disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      {{ selectedValueDescription }}\n    </button>\n    <div class=\"select__wrapper\" *ngIf=\"expanded\">\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n      </div>\n      <div class=\"select__list-wrapper\" role=\"listbox\">\n        <section class=\"select__list\" data-records=\"\">\n          <div class=\"select__item\" *ngFor=\"let v of filterdValues\">\n            <button\n              class=\"select__cta\"\n              [class.select__cta--active]=\"value === v.dropdownModelID ? true : false\"\n              [attr.data-selected]=\"value === v.dropdownModelID ? true : false\"\n              type=\"button\"\n              tabindex=\"-1\"\n              (click)=\"onSelectButtonClick($event, v)\"\n              title=\"{{ v.description }}\"\n            >\n              <span class=\"select__cta__title cut\" [attr.title]=\"v.tooltip ? v.tooltip : null\">{{ v.description }}</span>\n            </button>\n          </div>\n        </section>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\" [innerHtml]=\"annotation | safe: 'html'\"></div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".cut{overflow:hidden;display:inline-block;text-overflow:ellipsis;white-space:nowrap;max-width:100%;margin-bottom:-4px}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbSearchDropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-search-dropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbSearchDropdownComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\" style=\"z-index: 80000\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"></label>\n  <span *ngIf=\"label\" class=\"vraag form__label\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <div class=\"js-select\">\n    <button\n      type=\"button\"\n      class=\"js-select__input\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.button--disabled]=\"disabled\"\n      [class.placeholder]=\"value === canSelectEmptyID || value === '' || value === null || value === undefined\"\n      [disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      {{ selectedValueDescription }}\n    </button>\n    <div class=\"select__wrapper\" *ngIf=\"expanded\">\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n      </div>\n      <div class=\"select__list-wrapper\" role=\"listbox\">\n        <section class=\"select__list\" data-records=\"\">\n          <div class=\"select__item\" *ngFor=\"let v of filterdValues\">\n            <button\n              class=\"select__cta\"\n              [class.select__cta--active]=\"value === v.dropdownModelID ? true : false\"\n              [attr.data-selected]=\"value === v.dropdownModelID ? true : false\"\n              type=\"button\"\n              tabindex=\"-1\"\n              (click)=\"onSelectButtonClick($event, v)\"\n              title=\"{{ v.description }}\"\n            >\n              <span class=\"select__cta__title cut\" [attr.title]=\"v.tooltip ? v.tooltip : null\">{{ v.description }}</span>\n            </button>\n          </div>\n        </section>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\" [innerHtml]=\"annotation | safe: 'html'\"></div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".cut{overflow:hidden;display:inline-block;text-overflow:ellipsis;white-space:nowrap;max-width:100%;margin-bottom:-4px}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { values: [{
                type: Input
            }], value: [{
                type: Input
            }], dropdownID: [{
                type: Input
            }], searchInput: [{
                type: ViewChild,
                args: ['searchInput']
            }], canSelectEmpty: [{
                type: Input
            }], canSelectEmptyID: [{
                type: Input
            }], canSelectEmptyLabel: [{
                type: Input
            }] } });

/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbInputTextAutocompleteComponent extends BaseFormElementComponent {
    constructor(formService, elementRef) {
        super(formService);
        this.formService = formService;
        this.elementRef = elementRef;
        this.inputType = 'text';
        this.isLoading = false;
        this.noResultsMessageEnabled = true;
        this.onValueSelected$ = new EventEmitter();
        this.onFocus$ = new EventEmitter();
        this.onModelSelected$ = new EventEmitter();
        this.value = '';
        this.expanded = false;
    }
    ngOnInit() {
        super.ngOnInit();
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    onChange(event) {
        if (this.value === event.target.value) {
            return;
        }
        this.value = event.target.value;
        this.propagateChange(this.value);
        if (!this.value || this.value.length < 1) {
            this.searchResults = null;
            this.expanded = false;
        }
        this.expanded = true;
        this.onValueChange.next(this.value);
    }
    onDocumentClick(event) {
        let clickedComponent = event.target;
        let isInside = false;
        do {
            if (clickedComponent === this.elementRef.nativeElement) {
                isInside = true;
                if (this.searchResults) {
                    this.expanded = true;
                }
            }
            clickedComponent = clickedComponent.parentNode;
        } while (clickedComponent);
        if (!isInside) {
            this.expanded = false;
            this.isLoading = false;
        }
    }
    onValueClick(event) {
        const text = event.target.innerText;
        this.value = text;
        this.isLoading = false;
        this.expanded = false;
        this.onValueSelected$.next(text);
    }
    onFocus(event) {
        const text = event.target.value;
        this.onFocus$.next(text);
    }
    onValueClickGetModel(event) {
        this.value = event.description;
        this.isLoading = false;
        this.expanded = false;
        this.onModelSelected$.next(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputTextAutocompleteComponent, deps: [{ token: AnbFormService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputTextAutocompleteComponent, selector: "anb-input-text-autocomplete", inputs: { placeholder: "placeholder", maxlength: "maxlength", inputType: "inputType", searchResults: "searchResults", isLoading: "isLoading", browserAutocomplete: "browserAutocomplete", noResultsMessageEnabled: "noResultsMessageEnabled" }, outputs: { onValueSelected$: "onValueSelected$", onFocus$: "onFocus$", onModelSelected$: "onModelSelected$" }, host: { listeners: { "document:click": "onDocumentClick($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputTextAutocompleteComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputTextAutocompleteComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <div class=\"js-autocomplete\">\n    <input\n      class=\"webuniversum input-field input-field--block\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      [type]=\"inputType\"\n      placeholder=\"\"\n      [value]=\"value\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      (keyup)=\"onChange($event)\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.maxlength]=\"maxlength\"\n      [autocomplete]=\"browserAutocomplete\"\n      (focusin)=\"onFocus($event)\"\n    />\n\n    <div class=\"autocomplete__loader\" [attr.data-show]=\"isLoading\" data-loader=\"\" aria-hidden=\"true\"></div>\n    <div\n      *ngIf=\"expanded && (searchResults?.length > 0 || (searchResults?.length < 1 && noResultsMessageEnabled))\"\n      class=\"autocomplete\"\n      data-content=\"\"\n      aria-hidden=\"false\"\n      data-show=\"true\"\n      [attr.aria-labelledby]=\"id\"\n    >\n      <div class=\"autocomplete__list-wrapper\">\n        <ul class=\"autocomplete__list\" data-records=\"\" role=\"listbox\">\n          <li *ngIf=\"searchResults && searchResults.length < 1 && noResultsMessageEnabled\" class=\"autocomplete__item\" role=\"option\">\n            <span class=\"autocomplete__cta\">\n              <span class=\"autocomplete__cta__title vl-u-text--italic\">Geen resultaten gevonden</span>\n            </span>\n          </li>\n          <li\n            *ngFor=\"let searchResult of searchResults\"\n            class=\"autocomplete__item\"\n            role=\"option\"\n            (click)=\"onValueClick($event); onValueClickGetModel(searchResult)\"\n          >\n            <a\n              class=\"autocomplete__cta\"\n              href=\"javascript:void(0)\"\n              tabindex=\"-1\"\n              data-index=\"1\"\n              data-record=\"\"\n              data-focus=\"\"\n              data-value=\"searchResult.description\"\n            >\n              <span class=\"autocomplete__cta__title\">{{ searchResult.description }}</span>\n            </a>\n          </li>\n        </ul>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: ["", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputTextAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-text-autocomplete', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputTextAutocompleteComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputTextAutocompleteComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <div class=\"js-autocomplete\">\n    <input\n      class=\"webuniversum input-field input-field--block\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      [type]=\"inputType\"\n      placeholder=\"\"\n      [value]=\"value\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      (keyup)=\"onChange($event)\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.maxlength]=\"maxlength\"\n      [autocomplete]=\"browserAutocomplete\"\n      (focusin)=\"onFocus($event)\"\n    />\n\n    <div class=\"autocomplete__loader\" [attr.data-show]=\"isLoading\" data-loader=\"\" aria-hidden=\"true\"></div>\n    <div\n      *ngIf=\"expanded && (searchResults?.length > 0 || (searchResults?.length < 1 && noResultsMessageEnabled))\"\n      class=\"autocomplete\"\n      data-content=\"\"\n      aria-hidden=\"false\"\n      data-show=\"true\"\n      [attr.aria-labelledby]=\"id\"\n    >\n      <div class=\"autocomplete__list-wrapper\">\n        <ul class=\"autocomplete__list\" data-records=\"\" role=\"listbox\">\n          <li *ngIf=\"searchResults && searchResults.length < 1 && noResultsMessageEnabled\" class=\"autocomplete__item\" role=\"option\">\n            <span class=\"autocomplete__cta\">\n              <span class=\"autocomplete__cta__title vl-u-text--italic\">Geen resultaten gevonden</span>\n            </span>\n          </li>\n          <li\n            *ngFor=\"let searchResult of searchResults\"\n            class=\"autocomplete__item\"\n            role=\"option\"\n            (click)=\"onValueClick($event); onValueClickGetModel(searchResult)\"\n          >\n            <a\n              class=\"autocomplete__cta\"\n              href=\"javascript:void(0)\"\n              tabindex=\"-1\"\n              data-index=\"1\"\n              data-record=\"\"\n              data-focus=\"\"\n              data-value=\"searchResult.description\"\n            >\n              <span class=\"autocomplete__cta__title\">{{ searchResult.description }}</span>\n            </a>\n          </li>\n        </ul>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: ["::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i0.ElementRef }]; }, propDecorators: { placeholder: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], inputType: [{
                type: Input
            }], searchResults: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], browserAutocomplete: [{
                type: Input
            }], noResultsMessageEnabled: [{
                type: Input
            }], onValueSelected$: [{
                type: Output
            }], onFocus$: [{
                type: Output
            }], onModelSelected$: [{
                type: Output
            }], onDocumentClick: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

/**
 * Address search service: Url is fetched from parameter store
 * VERSION 4
 *
 * export
 * class AddressSearchService
 * extends
 */
class AddressSearchV4Service {
    get api() {
        return `${this.configuratieService.getValue(this.geopuntKey)}${this.apiVersionPath}`;
    }
    constructor(httpClient, configuratieService) {
        this.httpClient = httpClient;
        this.configuratieService = configuratieService;
        this.geopuntKey = '/appl/configuratieservice/geo-url/geolocation';
        this.apiVersionPath = '/v4';
    }
    /**
       * Provides auto-completion functionality for a free form single search line.
       * example: GET v4/Suggestion?q={q}&c={c}
       *
       * @param q A free form search string. May contain community names,
       * sub-community area names, zip codes, street names, house numbers or parts thereof. A minimum of two characters is required.
       * @param c Optional] The maximum number of items you want to retrieve in the result.
       *  You may not ask more than 25 items. If omitted defaults to 5.
  
       * @returns  Observable<{ 'SuggestionResult': string[] }>
       * @memberof AddressSearchService
       */
    getAddressSuggestion(q, c) {
        let params = new HttpParams();
        params = params.append('q', q);
        params = params.append('c', c.toString());
        return this.httpClient
            .jsonp(`${this.api}/suggestion?${params.toString()}`, 'callback')
            .pipe(catchError(this.handleError));
    }
    /**
     * Geocoding provides the location of an address or a poi. Reverse geocoding provides the closest address based on a given location.
     * example: GET v4/Location?q={q}&latlon={latlon}&xy={xy}&type={type}&c={c}
     *
     * @param q [Optional] A free form search string. May contain community names, sub-community area names,
     * zip codes, street names, house numbers, poi names, coordinates or parts thereof. A minimum of two characters is required.
     * @param lation [Optional] A coordinate in WGS84 projection.
     * Input takes the form [lat,lon] where lat and lon are provided in decimal degrees.
     * @param xy [Optional] A coordinate in Lambert-72 projection.
     * Input takes the form [x,y] where x and y are provided in meters.
     * @param type [Optional] The location types you want to restrict the result to.
     * Valid types are 'Municipality', 'Thoroughfarename', 'Housenumber'. The filter has no effect on reverse geocoding requests.
     * @param  c [Optional] The maximum number of items you want to retrieve in the result.
     * You may not ask more than 5 items. If omitted defaults to 1.
     * @returns
     * @memberof AddressSearchService
     */
    getLocation(q = null, lation = null, xy = null, type = null, c = null) {
        let params = new HttpParams();
        if (q) {
            params = params.append('q', q);
        }
        if (lation) {
            params = params.append('lation', lation);
        }
        if (xy) {
            params = params.append('xy', xy.toString());
        }
        if (c) {
            params = params.append('c', c.toString());
        }
        if (type) {
            params = params.append('type', type);
        }
        return this.httpClient
            .jsonp(`${this.api}/location?${params.toString()}`, 'callback')
            .pipe(catchError(this.handleError));
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleError(err) {
        return throwError(err);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AddressSearchV4Service, deps: [{ token: i1$2.HttpClient }, { token: i2$3.AnbNgConfiguratieService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AddressSearchV4Service }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AddressSearchV4Service, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: i2$3.AnbNgConfiguratieService }]; } });

class AnbStreetAutocompleteDirective {
    constructor(host, adressSearchService) {
        this.host = host;
        this.adressSearchService = adressSearchService;
    }
    /**
     * On init
     */
    ngOnInit() {
        if (this.host) {
            this.onValueChangeSub = this.host.onValueChange.subscribe((value) => {
                const searchResults = new Array();
                // Voeg eigen input toe als eerste selectie keuze
                searchResults.push(new DropdownModel(null, value));
                if (value && value.length > 2) {
                    this.host.isLoading = true;
                    this.adressSearchService.getAddressSuggestion(value, 5).subscribe((result) => {
                        if (result.SuggestionResult.length > 0) {
                            result.SuggestionResult.forEach((element) => {
                                searchResults.push(new DropdownModel(element, element));
                            });
                        }
                        this.host.isLoading = false;
                    });
                }
                this.host.searchResults = searchResults;
            });
            // Handle selectie in de suggestielijst
            this.onModelSelectedSub = this.host.onModelSelected$.subscribe((value) => {
                if (value.dropdownModelID) {
                    this.setControlValues(value.description);
                }
            });
        }
    }
    /**
     * On destroy
     */
    ngOnDestroy() {
        this.onValueChangeSub?.unsubscribe();
        this.onModelSelectedSub?.unsubscribe();
    }
    /**
     * Sets control values
     * @param query
     */
    setControlValues(query) {
        this.adressSearchService.getLocation(query).subscribe((location) => {
            if (location?.LocationResult?.length) {
                const locationResult = location.LocationResult[0];
                const street = locationResult.Thoroughfarename;
                const houseNumber = locationResult.Housenumber;
                const municipality = locationResult.Municipality;
                const postalcode = locationResult.Zipcode;
                // Vul de postcode control wanneer deze is meegegeven
                if (this.postalCodeFormControl && postalcode && postalcode !== '0000') {
                    this.postalCodeFormControl.setValue(postalcode);
                }
                // Vul de gemeente control wanneer deze is meegegeven
                if (this.municipalityFormControl && municipality) {
                    this.municipalityFormControl.setValue(municipality);
                }
                // Vul de huisnummer control wanneer deze is meegegeven
                if (this.houseNumberFormControl && houseNumber) {
                    this.houseNumberFormControl.setValue(houseNumber);
                }
                // Vul de straat in de huidige control
                this.host.setValue(street ?? '');
                // Land
                if (this.countryFormControl) {
                    this.countryFormControl.setValue('BEL');
                }
                // Focus het huisnummer veld
                if (this.houseNumberElementRef) {
                    const nativeElement = this.houseNumberElementRef.nativeElement;
                    if (nativeElement) {
                        const inputElement = nativeElement.querySelector('input[type=text]');
                        if (inputElement) {
                            inputElement.focus();
                        }
                    }
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbStreetAutocompleteDirective, deps: [{ token: AnbInputTextAutocompleteComponent }, { token: AddressSearchV4Service }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AnbStreetAutocompleteDirective, selector: "[anbStreetAutocomplete]", inputs: { municipalityFormControl: "municipalityFormControl", postalCodeFormControl: "postalCodeFormControl", houseNumberFormControl: "houseNumberFormControl", countryFormControl: "countryFormControl", houseNumberElementRef: "houseNumberElementRef" }, providers: [AddressSearchV4Service], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbStreetAutocompleteDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[anbStreetAutocomplete]',
                    providers: [AddressSearchV4Service],
                }]
        }], ctorParameters: function () { return [{ type: AnbInputTextAutocompleteComponent }, { type: AddressSearchV4Service }]; }, propDecorators: { municipalityFormControl: [{
                type: Input
            }], postalCodeFormControl: [{
                type: Input
            }], houseNumberFormControl: [{
                type: Input
            }], countryFormControl: [{
                type: Input
            }], houseNumberElementRef: [{
                type: Input
            }] } });

class AnbPostalCodeAutocompleteDirective {
    constructor(host, basisregisterService) {
        this.host = host;
        this.basisregisterService = basisregisterService;
        this.subs = new Subscription();
        this.municipalities = [];
    }
    ngOnInit() {
        if (this.host) {
            this.host.noResultsMessageEnabled = false;
            // const valueSub = this.host.onValueChange.subscribe((value) => {
            //   const searchResults = new Array<DropdownModel>();
            //   this.host.isLoading = true;
            //   this.masterEnReferentieDataService.getPostcalCodeSuggestion(value).subscribe((municipalities) => {
            //     this.host.isLoading = false;
            //     this.municipalities = municipalities;
            //     this.municipalities.forEach((municipality) => {
            //       searchResults.push(new DropdownModel(municipality.index.toString(), `${municipality.postalCode} ${municipality.name}`));
            //     });
            //     this.host.searchResults = searchResults;
            //   });
            // });
            const valueSub = this.host.onValueChange
                .pipe(tap(() => (this.host.isLoading = true)), switchMap((value) => this.basisregisterService.getPostcalCodeSuggestion(value).pipe(map((municipalities) => {
                const searchResults = new Array();
                this.host.isLoading = false;
                this.municipalities = municipalities;
                this.municipalities.forEach((municipality) => {
                    searchResults.push(new DropdownModel(municipality.index.toString(), `${municipality.postalCode} ${municipality.name}`));
                });
                this.host.searchResults = searchResults;
                if (searchResults.length === 1 && value.length === 4) {
                    const municipality = this.municipalities.find((x) => x.index.toString() === searchResults[0].dropdownModelID);
                    this.setMunicipalityValue(municipality);
                }
                return searchResults;
            }))))
                .subscribe();
            this.subs.add(valueSub);
            // Plaats enkel de postcode in het inputveld
            const modelSub = this.host.onModelSelected$.subscribe((value) => {
                const id = value?.dropdownModelID;
                const municipality = this.municipalities.find((x) => x.index.toString() === id);
                this.host.setValue(municipality?.postalCode);
                this.setMunicipalityValue(municipality);
            });
            this.subs.add(modelSub);
        }
    }
    setMunicipalityValue(municipality) {
        // Vul de gemeente control wanneer deze is meegegeven
        if (this.municipalityFormControl && municipality) {
            this.municipalityFormControl.setValue(municipality.name);
        }
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPostalCodeAutocompleteDirective, deps: [{ token: AnbInputTextAutocompleteComponent }, { token: BasisregisterService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AnbPostalCodeAutocompleteDirective, selector: "[anbPostalCodeAutocomplete]", inputs: { municipalityFormControl: "municipalityFormControl" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPostalCodeAutocompleteDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[anbPostalCodeAutocomplete]',
                }]
        }], ctorParameters: function () { return [{ type: AnbInputTextAutocompleteComponent }, { type: BasisregisterService }]; }, propDecorators: { municipalityFormControl: [{
                type: Input
            }] } });

class AnbMunicipalityAutocompleteDirective {
    constructor(host, basisregisterService) {
        this.host = host;
        this.basisregisterService = basisregisterService;
        this.municipalities = [];
    }
    ngOnInit() {
        if (this.host) {
            this.host.noResultsMessageEnabled = false;
            this.onValueChangeSub = this.host.onValueChange.subscribe((value) => {
                const searchResults = new Array();
                this.host.isLoading = true;
                this.basisregisterService.getMunicipalitySuggestion(value).subscribe((municipalities) => {
                    this.host.isLoading = false;
                    this.municipalities = municipalities;
                    this.municipalities.forEach((municipality) => {
                        searchResults.push(new DropdownModel(municipality.index.toString(), `${municipality.postalCode} ${municipality.name}`));
                    });
                });
                this.host.searchResults = searchResults;
            });
            // Plaats enkel de gemeente in het inputveld
            this.onModelSelectedSub = this.host.onModelSelected$.subscribe((value) => {
                const id = value?.dropdownModelID;
                const municipality = this.municipalities?.find((x) => x.index.toString() === id);
                this.host.setValue(municipality?.name);
                // Vul de postcode control wanneer deze is meegegeven
                if (this.postalCodeFormControl && municipality) {
                    this.postalCodeFormControl.setValue(municipality.postalCode);
                }
            });
        }
    }
    ngOnDestroy() {
        this.onValueChangeSub?.unsubscribe();
        this.onModelSelectedSub?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMunicipalityAutocompleteDirective, deps: [{ token: AnbInputTextAutocompleteComponent }, { token: BasisregisterService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AnbMunicipalityAutocompleteDirective, selector: "[anbMunicipalityAutocomplete]", inputs: { postalCodeFormControl: "postalCodeFormControl" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMunicipalityAutocompleteDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[anbMunicipalityAutocomplete]',
                }]
        }], ctorParameters: function () { return [{ type: AnbInputTextAutocompleteComponent }, { type: BasisregisterService }]; }, propDecorators: { postalCodeFormControl: [{
                type: Input
            }] } });

class AnbInputAddressComponent extends AnbBaseFormComponent {
    constructor(anbFb, masterEnReferentieDataService, basisRegisterService) {
        super();
        this.anbFb = anbFb;
        this.masterEnReferentieDataService = masterEnReferentieDataService;
        this.basisRegisterService = basisRegisterService;
        this.ase = ActivityStatusEnablementEnum.ReadOnly;
        this.customErrorList = [];
        this.landDropdownList = [];
        this.straatLabel = 'Straat';
        this.huisnummerLabel = 'Nummer';
        this.busLabel = 'Bus';
        this.postcodeLabel = 'Postcode';
        this.gemeenteLabel = 'Gemeente';
        this.landLabel = 'Land';
        this.optioneelLabel = ' (optioneel)';
        this.lockGemeente = true;
        this.loadMasterEnReferentieDataLanden = false;
        this.asyncValidators = [];
        this.municipalityAse = this.ase;
        this.subs = new SubSink();
        this.FORM_PARAMS = ANB_INPUT_ADDRESS_FORM_PARAMS;
        this.foutePostcodeGemeenteErrorKey = AnbInputAddressConstants.foutePostcodeGemeenteErrorKey;
        this.subs.sink = this.basisRegisterService.getMunicipalities().subscribe((municipalities) => {
            this.municipalities = municipalities;
            this.updateMunicipalityAse(this.addressFormGroup.value);
        });
        // Init a default formGroup
        if (!this.formGroup) {
            this.addressFormGroup = anbFb.anbGroup({
                [this.FORM_PARAMS.straat]: [null],
                [this.FORM_PARAMS.huisnummer]: [null],
                [this.FORM_PARAMS.bus]: [null],
                [this.FORM_PARAMS.postcode]: [null],
                [this.FORM_PARAMS.gemeente]: [null],
                [this.FORM_PARAMS.land]: [null],
            });
            this.setValidators();
        }
    }
    ngOnInit() {
        if (this.loadMasterEnReferentieDataLanden) {
            this.masterEnReferentieDataService.getCountries().subscribe((countries) => {
                if (countries) {
                    const countryDropdownList = new Array();
                    countries.forEach((country) => {
                        countryDropdownList.push(new DropdownModel(country.isO3Code, country.name));
                    });
                    this.landDropdownList = countryDropdownList;
                }
            });
        }
        this.subs.sink = this.addressFormGroup.valueChanges.subscribe((formGroupValues) => {
            this.updateMunicipalityAse(formGroupValues);
        });
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    ngAfterViewInit() {
        this.customErrorList = AnbInputAddressUtils.addCustomErrorsForInputAddressComponent(this.customErrorList);
    }
    ngOnChanges(changes) {
        if (changes.formGroup?.currentValue) {
            this.addressFormGroup = changes.formGroup.currentValue;
            this.setValidators();
        }
        if (changes.ase?.currentValue) {
            this.municipalityAse = changes.ase?.currentValue;
        }
    }
    setValidators() {
        if (this.addressFormGroup) {
            let asyncValidators = this.asyncValidators;
            if (!Array.isArray(asyncValidators)) {
                asyncValidators = [asyncValidators];
            }
            this.addressFormGroup.setAsyncValidators([].concat(this.postCodeGemeenteAsyncValidator(), asyncValidators));
        }
    }
    updateMunicipalityAse(formGroup) {
        this.municipalityAse = this.ase;
        if (this.lockGemeente) {
            const landControl = this.addressFormGroup.get(this.FORM_PARAMS.land);
            const gemeente = formGroup.gemeente;
            const postcode = formGroup.postcode;
            const land = formGroup?.land;
            const checkLand = !landControl || (landControl && land === 'BEL');
            const municipality = this.municipalities?.find((x) => x.postalCode === postcode && x.name === gemeente);
            if (checkLand && postcode?.length === 4 && municipality) {
                this.municipalityAse = ActivityStatusEnablementEnum.ReadOnly;
            }
        }
    }
    postCodeGemeenteAsyncValidator() {
        return (control) => {
            return new Observable((o) => {
                const landControl = this.addressFormGroup.get(ANB_INPUT_ADDRESS_FORM_PARAMS.land);
                const postcode = this.addressFormGroup.get(ANB_INPUT_ADDRESS_FORM_PARAMS.postcode)?.value;
                const gemeente = this.addressFormGroup.get(ANB_INPUT_ADDRESS_FORM_PARAMS.gemeente)?.value;
                const land = landControl?.value;
                const checkLand = !landControl || (landControl && land === 'BEL');
                if (control && checkLand) {
                    if (postcode && gemeente) {
                        this.subs.sink = this.basisRegisterService.getMunicipalities().subscribe((municipalities) => {
                            const municipality = municipalities.find((x) => x.postalCode === postcode && x.name === gemeente);
                            if (!this.lockGemeente && !municipality) {
                                o.next({ [this.foutePostcodeGemeenteErrorKey]: true });
                                o.complete();
                            }
                            else {
                                o.next(null);
                                o.complete();
                            }
                        });
                    }
                    else {
                        o.next(null);
                        o.complete();
                    }
                }
                else {
                    o.next(null);
                    o.complete();
                }
            });
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputAddressComponent, deps: [{ token: AnbFormBuilder }, { token: MasterEnReferentieDataService }, { token: BasisregisterService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputAddressComponent, selector: "anb-input-address", inputs: { formGroup: "formGroup", ase: "ase", customErrorList: "customErrorList", landDropdownList: "landDropdownList", straatLabel: "straatLabel", huisnummerLabel: "huisnummerLabel", busLabel: "busLabel", postcodeLabel: "postcodeLabel", gemeenteLabel: "gemeenteLabel", landLabel: "landLabel", optioneelLabel: "optioneelLabel", lockGemeente: "lockGemeente", loadMasterEnReferentieDataLanden: "loadMasterEnReferentieDataLanden", asyncValidators: "asyncValidators" }, providers: [MasterEnReferentieDataService, BasisregisterService], viewQueries: [{ propertyName: "houseNumberElementRef", first: true, predicate: ["houseNumber"], descendants: true, read: ElementRef, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-container [formGroup]=\"addressFormGroup\">\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.straat)\">\n    <div class=\"form__row\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.straat\"\n        [label]=\"straatLabel.concat(isRequiredField(FORM_PARAMS.straat, addressFormGroup) ? '' : optioneelLabel)\"\n        [houseNumberElementRef]=\"houseNumberElementRef\"\n        [houseNumberFormControl]=\"addressFormGroup.get(FORM_PARAMS.huisnummer)\"\n        [postalCodeFormControl]=\"addressFormGroup.get(FORM_PARAMS.postcode)\"\n        [municipalityFormControl]=\"addressFormGroup.get(FORM_PARAMS.gemeente)\"\n        [countryFormControl]=\"addressFormGroup.get(FORM_PARAMS.land)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"50\"\n        anbStreetAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.huisnummer)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text\n        #houseNumber\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.huisnummer\"\n        [label]=\"huisnummerLabel.concat(isRequiredField(FORM_PARAMS.huisnummer, addressFormGroup) ? '' : optioneelLabel)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"15\"\n        autocomplete=\"undefined\"\n      >\n        <!-- Ref: https://adamsilver.io/blog/stopping-chrome-from-ignoring-autocomplete-off/ -->\n      </anb-input-text>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.bus)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.bus\"\n        [label]=\"busLabel.concat(isRequiredField(FORM_PARAMS.bus, addressFormGroup) ? '' : optioneelLabel)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"15\"\n        autocomplete=\"undefined\"\n      >\n        <!-- Ref: https://adamsilver.io/blog/stopping-chrome-from-ignoring-autocomplete-off/ -->\n      </anb-input-text>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.postcode)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.postcode\"\n        [label]=\"postcodeLabel.concat(isRequiredField(FORM_PARAMS.postcode, addressFormGroup) ? '' : optioneelLabel)\"\n        [municipalityFormControl]=\"addressFormGroup.get(FORM_PARAMS.gemeente)\"\n        [customErrorList]=\"customErrorList\"\n        [formErrorKeysToDisplay]=\"[this.foutePostcodeGemeenteErrorKey]\"\n        maxlength=\"15\"\n        anbPostalCodeAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.gemeente)\">\n    <div class=\"form__row\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"municipalityAse\"\n        [formControlName]=\"FORM_PARAMS.gemeente\"\n        #municipality\n        [label]=\"gemeenteLabel.concat(isRequiredField(FORM_PARAMS.gemeente, addressFormGroup) ? '' : optioneelLabel)\"\n        [postalCodeFormControl]=\"addressFormGroup.get(FORM_PARAMS.postcode)\"\n        [customErrorList]=\"customErrorList\"\n        [formErrorKeysToDisplay]=\"[this.foutePostcodeGemeenteErrorKey]\"\n        maxlength=\"100\"\n        anbMunicipalityAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.land)\">\n    <div class=\"form__row select-w-300\">\n      <anb-search-dropdown\n        [formControlName]=\"FORM_PARAMS.land\"\n        [label]=\"landLabel.concat(isRequiredField(FORM_PARAMS.land, addressFormGroup) ? '' : optioneelLabel)\"\n        [form]=\"addressFormGroup\"\n        [customErrorList]=\"customErrorList\"\n        [values]=\"landDropdownList\"\n        [ase]=\"ase\"\n      >\n      </anb-search-dropdown>\n    </div>\n  </ng-container>\n</ng-container>\n", styles: ["::ng-deep .input-w-150 input{width:150px!important;display:block}::ng-deep .select-w-300 .js-select{width:300px!important}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: AnbInputTextComponent, selector: "anb-input-text", inputs: ["placeholder", "maxlength", "inputType", "rawValue", "autocomplete", "mask", "predefinedMask"] }, { kind: "component", type: AnbSearchDropdownComponent, selector: "anb-search-dropdown", inputs: ["values", "value", "dropdownID", "canSelectEmpty", "canSelectEmptyID", "canSelectEmptyLabel"] }, { kind: "component", type: AnbInputTextAutocompleteComponent, selector: "anb-input-text-autocomplete", inputs: ["placeholder", "maxlength", "inputType", "searchResults", "isLoading", "browserAutocomplete", "noResultsMessageEnabled"], outputs: ["onValueSelected$", "onFocus$", "onModelSelected$"] }, { kind: "directive", type: AnbStreetAutocompleteDirective, selector: "[anbStreetAutocomplete]", inputs: ["municipalityFormControl", "postalCodeFormControl", "houseNumberFormControl", "countryFormControl", "houseNumberElementRef"] }, { kind: "directive", type: AnbPostalCodeAutocompleteDirective, selector: "[anbPostalCodeAutocomplete]", inputs: ["municipalityFormControl"] }, { kind: "directive", type: AnbMunicipalityAutocompleteDirective, selector: "[anbMunicipalityAutocomplete]", inputs: ["postalCodeFormControl"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputAddressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-address', providers: [MasterEnReferentieDataService, BasisregisterService], template: "<ng-container [formGroup]=\"addressFormGroup\">\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.straat)\">\n    <div class=\"form__row\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.straat\"\n        [label]=\"straatLabel.concat(isRequiredField(FORM_PARAMS.straat, addressFormGroup) ? '' : optioneelLabel)\"\n        [houseNumberElementRef]=\"houseNumberElementRef\"\n        [houseNumberFormControl]=\"addressFormGroup.get(FORM_PARAMS.huisnummer)\"\n        [postalCodeFormControl]=\"addressFormGroup.get(FORM_PARAMS.postcode)\"\n        [municipalityFormControl]=\"addressFormGroup.get(FORM_PARAMS.gemeente)\"\n        [countryFormControl]=\"addressFormGroup.get(FORM_PARAMS.land)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"50\"\n        anbStreetAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.huisnummer)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text\n        #houseNumber\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.huisnummer\"\n        [label]=\"huisnummerLabel.concat(isRequiredField(FORM_PARAMS.huisnummer, addressFormGroup) ? '' : optioneelLabel)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"15\"\n        autocomplete=\"undefined\"\n      >\n        <!-- Ref: https://adamsilver.io/blog/stopping-chrome-from-ignoring-autocomplete-off/ -->\n      </anb-input-text>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.bus)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.bus\"\n        [label]=\"busLabel.concat(isRequiredField(FORM_PARAMS.bus, addressFormGroup) ? '' : optioneelLabel)\"\n        [customErrorList]=\"customErrorList\"\n        maxlength=\"15\"\n        autocomplete=\"undefined\"\n      >\n        <!-- Ref: https://adamsilver.io/blog/stopping-chrome-from-ignoring-autocomplete-off/ -->\n      </anb-input-text>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.postcode)\">\n    <div class=\"form__row input-w-150\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"ase\"\n        [formControlName]=\"FORM_PARAMS.postcode\"\n        [label]=\"postcodeLabel.concat(isRequiredField(FORM_PARAMS.postcode, addressFormGroup) ? '' : optioneelLabel)\"\n        [municipalityFormControl]=\"addressFormGroup.get(FORM_PARAMS.gemeente)\"\n        [customErrorList]=\"customErrorList\"\n        [formErrorKeysToDisplay]=\"[this.foutePostcodeGemeenteErrorKey]\"\n        maxlength=\"15\"\n        anbPostalCodeAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.gemeente)\">\n    <div class=\"form__row\">\n      <anb-input-text-autocomplete\n        [form]=\"addressFormGroup\"\n        [ase]=\"municipalityAse\"\n        [formControlName]=\"FORM_PARAMS.gemeente\"\n        #municipality\n        [label]=\"gemeenteLabel.concat(isRequiredField(FORM_PARAMS.gemeente, addressFormGroup) ? '' : optioneelLabel)\"\n        [postalCodeFormControl]=\"addressFormGroup.get(FORM_PARAMS.postcode)\"\n        [customErrorList]=\"customErrorList\"\n        [formErrorKeysToDisplay]=\"[this.foutePostcodeGemeenteErrorKey]\"\n        maxlength=\"100\"\n        anbMunicipalityAutocomplete\n      >\n      </anb-input-text-autocomplete>\n    </div>\n  </ng-container>\n\n  <ng-container *ngIf=\"addressFormGroup && addressFormGroup.get(FORM_PARAMS.land)\">\n    <div class=\"form__row select-w-300\">\n      <anb-search-dropdown\n        [formControlName]=\"FORM_PARAMS.land\"\n        [label]=\"landLabel.concat(isRequiredField(FORM_PARAMS.land, addressFormGroup) ? '' : optioneelLabel)\"\n        [form]=\"addressFormGroup\"\n        [customErrorList]=\"customErrorList\"\n        [values]=\"landDropdownList\"\n        [ase]=\"ase\"\n      >\n      </anb-search-dropdown>\n    </div>\n  </ng-container>\n</ng-container>\n", styles: ["::ng-deep .input-w-150 input{width:150px!important;display:block}::ng-deep .select-w-300 .js-select{width:300px!important}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormBuilder }, { type: MasterEnReferentieDataService }, { type: BasisregisterService }]; }, propDecorators: { formGroup: [{
                type: Input
            }], ase: [{
                type: Input
            }], customErrorList: [{
                type: Input
            }], landDropdownList: [{
                type: Input
            }], straatLabel: [{
                type: Input
            }], huisnummerLabel: [{
                type: Input
            }], busLabel: [{
                type: Input
            }], postcodeLabel: [{
                type: Input
            }], gemeenteLabel: [{
                type: Input
            }], landLabel: [{
                type: Input
            }], optioneelLabel: [{
                type: Input
            }], lockGemeente: [{
                type: Input
            }], loadMasterEnReferentieDataLanden: [{
                type: Input
            }], asyncValidators: [{
                type: Input
            }], houseNumberElementRef: [{
                type: ViewChild,
                args: ['houseNumber', { static: true, read: ElementRef }]
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
const keyCodes = {
    enter: 13,
    escape: 27,
    left: 37,
    up: 38,
    right: 39,
    down: 40,
};
const Helper = {
    anyChanges(properties, changes) {
        for (const property of properties) {
            if (changes[property] !== undefined) {
                return true;
            }
        }
        return false;
    },
    createNumericRegex(hasDecimal, hasSign) {
        let regexString = '^';
        if (hasSign) {
            regexString += '-?';
        }
        regexString += '(?:(?:\\d+';
        if (hasDecimal) {
            regexString += '((\\.|\\,)\\d*)?';
        }
        regexString += ')|(?:(\\.|\\,)\\d*))?$';
        return new RegExp(regexString);
    },
};
function createMinValidator(min) {
    return (control) => {
        if (isNumber(control.value) && control.value < min) {
            return {
                minError: {
                    minValue: min,
                    value: control.value,
                },
            };
        }
        return null;
    };
}
function createMaxValidator(max) {
    return (control) => {
        if (isNumber(control.value) && control.value > max) {
            return {
                maxError: {
                    maxValue: max,
                    value: control.value,
                },
            };
        }
        return null;
    };
}
class AnbInputNumericComponent extends BaseFormElementComponent {
    constructor(formService, renderer2) {
        super(formService);
        this.formService = formService;
        this.renderer2 = renderer2;
        this.min = Number.MIN_SAFE_INTEGER;
        this.max = Number.MAX_SAFE_INTEGER;
        this.decimals = 2;
        this.format = '0,0.00';
        this.autoCorrect = false;
        this.rangeValidation = true;
        this.focus = new EventEmitter();
        this.blur = new EventEmitter();
        this.enter = new EventEmitter();
        this.escape = new EventEmitter();
        this.minValidateFn = Validators.nullValidator;
        this.maxValidateFn = Validators.nullValidator;
        this.focused = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.ngChange = (value) => { };
        this.ngTouched = () => { };
        const anbLocale = numeral.locales?.anb;
        if (!anbLocale) {
            numeral.register('locale', 'anb', {
                delimiters: {
                    thousands: '.',
                    decimal: ',',
                },
                abbreviations: {
                    thousand: 'k',
                    million: ' mln',
                    billion: ' mld',
                    trillion: ' bln',
                },
                ordinal: function (number) {
                    const remainder = number % 100;
                    return (number !== 0 && remainder <= 1) || remainder === 8 || remainder >= 20 ? 'ste' : 'de';
                },
                currency: {
                    symbol: '€ ',
                },
            });
        }
        numeral.locale('anb');
    }
    focusInput() {
        this.numericInput.nativeElement.focus();
    }
    blurInput() {
        this.numericInput.nativeElement.blur();
    }
    validate(control) {
        return this.minValidateFn(control) || this.maxValidateFn(control);
    }
    writeValue(value) {
        const newValue = this.restrictModelValue(value);
        this.value = newValue;
        // Wanneer een gebruiker een kommagetal invoert en dan het cijfer net na de komma verwijderd, dan wordt de komma ook verwijderd.
        // We zetten de input value enkel wanneer de component niet gefocust is, zodat de gebruiker de komma niet verliest.
        if (!this.focused) {
            this.setInputValue();
        }
    }
    registerOnChange(fn) {
        this.ngChange = fn;
    }
    registerOnTouched(fn) {
        this.ngTouched = fn;
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        this.verifySettings();
        if (Helper.anyChanges(['autoCorrect', 'decimals'], changes)) {
            delete this.numericRegex;
        }
        if (Helper.anyChanges(['min', 'max', 'rangeValidation'], changes)) {
            if (isNumber(this.min) && this.rangeValidation) {
                this.minValidateFn = createMinValidator(this.min);
            }
            else {
                this.minValidateFn = Validators.nullValidator;
            }
            if (isNumber(this.max) && this.rangeValidation) {
                this.maxValidateFn = createMaxValidator(this.max);
            }
            else {
                this.maxValidateFn = Validators.nullValidator;
            }
            this.ngChange(this.value);
        }
        if (Helper.anyChanges(['format'], changes)) {
            this.setInputValue();
        }
    }
    handleInput() {
        const element = this.numericInput.nativeElement;
        const selectionStart = element.selectionStart;
        const selectionEnd = element.selectionEnd;
        const value = element.value;
        if (!this.isValidInput(value)) {
            element.value = this.inputValue;
            this.setSelection(selectionStart - 1, selectionEnd - 1);
        }
        else {
            const orginalInputValue = this.parseNumber(value);
            let limitInputValue = this.restrictDecimals(orginalInputValue);
            if (this.autoCorrect) {
                limitInputValue = this.limitValue(limitInputValue);
            }
            if (orginalInputValue !== limitInputValue) {
                this.setInputValue(limitInputValue);
                this.setSelection(selectionStart, selectionEnd);
            }
            else {
                this.inputValue = value;
            }
            this.updateValue(limitInputValue);
        }
    }
    handleFocus() {
        if (!this.focused) {
            this.focused = true;
            this.setInputValue();
            setTimeout(() => this.setSelection(0, this.inputValue.length));
        }
        this.focus.emit();
    }
    handleBlur() {
        if (this.focused) {
            this.focused = false;
            this.ngTouched();
            this.setInputValue();
        }
        this.blur.emit();
    }
    handleKeyDown(event) {
        if (!this.disabled) {
            switch (event.which) {
                case keyCodes.down:
                    this.addStep(-1);
                    break;
                case keyCodes.up:
                    this.addStep(1);
                    break;
                case keyCodes.enter:
                    this.enter.emit();
                    break;
                case keyCodes.escape:
                    this.escape.emit();
                    break;
            }
        }
    }
    verifySettings() {
        if (isNumber(this.min) && isNumber(this.max) && this.min > this.max) {
            throw new Error('The max value should be bigger than the min value');
        }
        if (isNumber(this.decimals) && this.decimals < 0) {
            throw new Error('The decimals value should be bigger than 0');
        }
    }
    isValidInput(input) {
        let numericRegex = this.numericRegex;
        if (isNil(numericRegex)) {
            let hasDecimal = true;
            if (isNumber(this.decimals) && this.decimals === 0) {
                hasDecimal = false;
            }
            let hasSign = true;
            if (isNumber(this.min) && this.min >= 0 && this.autoCorrect) {
                hasSign = false;
            }
            numericRegex = Helper.createNumericRegex(hasDecimal, hasSign);
        }
        return numericRegex.test(input);
    }
    parseNumber(input) {
        input = input.replace('.', ',');
        return numeral(input).value();
    }
    addStep(step) {
        let value = this.value ? step + this.value : step;
        value = this.limitValue(value);
        value = this.restrictDecimals(value);
        this.setInputValue(value);
        this.updateValue(value);
    }
    limitValue(value) {
        if (isNumber(this.max) && value > this.max) {
            return this.max;
        }
        if (isNumber(this.min) && value < this.min) {
            return this.min;
        }
        return value;
    }
    // private isInRange(value: number): boolean {
    //   if (isNumber(value)) {
    //     if (isNumber(this.min) && value < this.min) {
    //       return false;
    //     }
    //     if (isNumber(this.max) && value > this.max) {
    //       return false;
    //     }
    //   }
    //   return true;
    // }
    restrictModelValue(value) {
        let newValue = this.restrictDecimals(value);
        if (this.autoCorrect && this.limitValue(newValue) !== newValue) {
            newValue = null;
        }
        return newValue;
    }
    restrictDecimals(value) {
        if (isNumber(this.decimals)) {
            const words = String(value).split('.');
            if (words.length === 2) {
                const decimalPart = words[1];
                if (decimalPart.length > this.decimals) {
                    value = parseFloat(words[0] + '.' + decimalPart.substr(0, this.decimals));
                }
            }
        }
        return value;
    }
    setInputValue(value = null) {
        if (isNil(value)) {
            value = this.value;
        }
        const inputValue = this.formatValue(value);
        this.renderer2.setProperty(this.numericInput.nativeElement, 'value', inputValue);
        this.inputValue = inputValue;
    }
    updateValue(value) {
        if (this.value !== value) {
            this.value = value;
            this.ngChange(value);
            this.onValueChange.emit(value);
        }
    }
    formatValue(value) {
        if (!isNil(value)) {
            if (this.focused) {
                return this.formatInputValue(value);
            }
            else {
                return this.formatNumber(value);
            }
        }
        return '';
    }
    formatInputValue(value) {
        let result = String(value);
        result = result.replace('.', ',');
        return result;
    }
    formatNumber(value) {
        return numeral(value).format(this.format);
    }
    setSelection(start, end) {
        this.numericInput.nativeElement.setSelectionRange(start, end);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputNumericComponent, deps: [{ token: AnbFormService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputNumericComponent, selector: "anb-input-numeric", inputs: { min: "min", max: "max", value: "value", placeholder: "placeholder", decimals: "decimals", format: "format", autoCorrect: "autoCorrect", rangeValidation: "rangeValidation", autocomplete: "autocomplete" }, outputs: { focus: "focus", blur: "blur", enter: "enter", escape: "escape" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputNumericComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputNumericComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "numericInput", first: true, predicate: ["numericInput"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field\"\n    type=\"text\"\n    inputmode=\"decimal\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.autocomplete]=\"autocomplete\"\n    (input)=\"handleInput()\"\n    (focus)=\"handleFocus()\"\n    (blur)=\"handleBlur()\"\n    (keydown)=\"handleKeyDown($event)\"\n    #numericInput\n  />\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputNumericComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-numeric', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputNumericComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputNumericComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field\"\n    type=\"text\"\n    inputmode=\"decimal\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.autocomplete]=\"autocomplete\"\n    (input)=\"handleInput()\"\n    (focus)=\"handleFocus()\"\n    (blur)=\"handleBlur()\"\n    (keydown)=\"handleKeyDown($event)\"\n    #numericInput\n  />\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i0.Renderer2 }]; }, propDecorators: { numericInput: [{
                type: ViewChild,
                args: ['numericInput', { static: true }]
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], decimals: [{
                type: Input
            }], format: [{
                type: Input
            }], autoCorrect: [{
                type: Input
            }], rangeValidation: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], focus: [{
                type: Output
            }], blur: [{
                type: Output
            }], enter: [{
                type: Output
            }], escape: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbInputPercentComponent extends BaseFormElementComponent {
    getShownValue() {
        return this.value + '%';
    }
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    getValueWithoutPercent(value) {
        if (value) {
            if (value.indexOf('%') > 0) {
                value = value.substring(0, value.indexOf('%') - 1); // laat het percentteken vallen in de waarde
            }
            value = parseFloat(value);
            value = Math.round(value * Math.pow(10, this.numberOfDecimals)) / Math.pow(10, this.numberOfDecimals);
        }
        return value;
    }
    writeValue(value) {
        if (value) {
            this.value = this.getValueWithoutPercent(value);
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.value = 0;
        this.numberOfDecimals = 2;
    }
    ngOnInit() {
        super.ngOnInit();
    }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = this.getValueWithoutPercent(value);
        }
    }
    onChange(event) {
        this.value = this.getValueWithoutPercent(event.target.value);
        // update form
        this.propagateChange(this.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputPercentComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputPercentComponent, selector: "anb-input-percent", inputs: { placeholder: "placeholder" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputPercentComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputPercentComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <!-- [class]=\"class\" -->\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field input-number\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    type=\"text\"\n    placeholder=\"\"\n    [value]=\"getShownValue()\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n    [attr.placeholder]=\"placeholder\"\n  />\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:5em}.input-number{text-align:right}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputPercentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-percent', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputPercentComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputPercentComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <!-- [class]=\"class\" -->\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    class=\"webuniversum input-field input-number\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n    type=\"text\"\n    placeholder=\"\"\n    [value]=\"getShownValue()\"\n    [attr.id]=\"id\"\n    [disabled]=\"disabled\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n    [attr.placeholder]=\"placeholder\"\n  />\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:5em}.input-number{text-align:right}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { placeholder: [{
                type: Input
            }] } });

class AnbMultiLevelDropdownModel {
    constructor(dropdownModelID, description, tooltip, selectable = true) {
        this.dropdownModelID = dropdownModelID;
        this.description = description;
        this.tooltip = tooltip;
        this.selectable = selectable;
        this.children = new Array();
    }
}

class SelectItemComponent {
    constructor() {
        this.selectedValues = new Array();
        this.expanded = false;
        this.expandAll = false;
        this.showTooltip = false;
        this.itemSelected = new EventEmitter();
    }
    ngOnInit() {
        this.level++;
    }
    ngOnChanges(changes) {
        if (changes.expandAll) {
            this.expanded = changes.expandAll.currentValue;
        }
    }
    onItemSelected(value) {
        this.itemSelected.emit(value);
    }
    onChildItemSelected(value) {
        this.onItemSelected(value);
    }
    getDropdownIDs(value) {
        return value.map((d) => d.dropdownModelID);
    }
    onExpandClick() {
        this.expanded = !this.expanded;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SelectItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SelectItemComponent, selector: "anb-select-item", inputs: { value: "value", level: "level", isSelected: "isSelected", selectedValues: "selectedValues", hasChildren: "hasChildren", expanded: "expanded", expandAll: "expandAll", showTooltip: "showTooltip" }, outputs: { itemSelected: "itemSelected" }, usesOnChanges: true, ngImport: i0, template: "<span [class.js-accordion--open]=\"expanded\">\n  <span *ngIf=\"hasChildren\" class=\"node-toggle toggle--arrow-down\">\n    <i class=\"toggle__icon\" aria-hidden=\"true\" (click)=\"onExpandClick()\"></i>\n  </span>\n</span>\n<button\n  (click)=\"onItemSelected(value)\"\n  [disabled]=\"!value.selectable\"\n  [class]=\"'select__cta cta__'\"\n  [class.select__cta--active]=\"isSelected\"\n  aria-selected=\"false\"\n  [attr.id]=\"'select__list__item__id__' + value.dropdownModelID\"\n  [attr.data-index]=\"value.dropdownModelID\"\n  [attr.data-value]=\"value.dropdownModelID\"\n  [attr.data-label]=\"value.description\"\n  [attr.data-selected]=\"isSelected\"\n  type=\"button\"\n>\n  <span\n    class=\"select__cta__title\"\n    [style.color]=\"value.selectable ? 'inherit' : '#aaa'\"\n    [attr.title]=\"showTooltip ? value.tooltip : null\"\n    >{{ value.description }}</span\n  >\n</button>\n<ul [class.expanded]=\"expanded\">\n  <li *ngFor=\"let v of value.children\" class=\"select__item\">\n    <anb-select-item\n      *ngIf=\"value && value.children\"\n      [value]=\"v\"\n      [level]=\"level\"\n      [selectedValues]=\"selectedValues\"\n      [isSelected]=\"getDropdownIDs(selectedValues).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n      (itemSelected)=\"onChildItemSelected($event)\"\n      [hasChildren]=\"v.children && v.children.length > 0\"\n      [expandAll]=\"expandAll\"\n      [showTooltip]=\"showTooltip\"\n    >\n    </anb-select-item>\n  </li>\n</ul>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SelectItemComponent, selector: "anb-select-item", inputs: ["value", "level", "isSelected", "selectedValues", "hasChildren", "expanded", "expandAll", "showTooltip"], outputs: ["itemSelected"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SelectItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-select-item', template: "<span [class.js-accordion--open]=\"expanded\">\n  <span *ngIf=\"hasChildren\" class=\"node-toggle toggle--arrow-down\">\n    <i class=\"toggle__icon\" aria-hidden=\"true\" (click)=\"onExpandClick()\"></i>\n  </span>\n</span>\n<button\n  (click)=\"onItemSelected(value)\"\n  [disabled]=\"!value.selectable\"\n  [class]=\"'select__cta cta__'\"\n  [class.select__cta--active]=\"isSelected\"\n  aria-selected=\"false\"\n  [attr.id]=\"'select__list__item__id__' + value.dropdownModelID\"\n  [attr.data-index]=\"value.dropdownModelID\"\n  [attr.data-value]=\"value.dropdownModelID\"\n  [attr.data-label]=\"value.description\"\n  [attr.data-selected]=\"isSelected\"\n  type=\"button\"\n>\n  <span\n    class=\"select__cta__title\"\n    [style.color]=\"value.selectable ? 'inherit' : '#aaa'\"\n    [attr.title]=\"showTooltip ? value.tooltip : null\"\n    >{{ value.description }}</span\n  >\n</button>\n<ul [class.expanded]=\"expanded\">\n  <li *ngFor=\"let v of value.children\" class=\"select__item\">\n    <anb-select-item\n      *ngIf=\"value && value.children\"\n      [value]=\"v\"\n      [level]=\"level\"\n      [selectedValues]=\"selectedValues\"\n      [isSelected]=\"getDropdownIDs(selectedValues).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n      (itemSelected)=\"onChildItemSelected($event)\"\n      [hasChildren]=\"v.children && v.children.length > 0\"\n      [expandAll]=\"expandAll\"\n      [showTooltip]=\"showTooltip\"\n    >\n    </anb-select-item>\n  </li>\n</ul>\n" }]
        }], propDecorators: { value: [{
                type: Input
            }], level: [{
                type: Input
            }], isSelected: [{
                type: Input
            }], selectedValues: [{
                type: Input
            }], hasChildren: [{
                type: Input
            }], expanded: [{
                type: Input
            }], expandAll: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], itemSelected: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @angular-eslint/no-output-on-prefix */
// declare var vl: any;
class AnbMultiLevelMultiSelectComponent extends BaseFormElementComponent {
    constructor(elementRef, renderer, formService) {
        super(formService);
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.formService = formService;
        this.showTooltip = false;
        this.closeOnSelect = false;
        this.setAllSelectItemsExpanded = false;
        this.onlyOneSelectable = false;
        this.onAdd = new EventEmitter();
        this.onDelete = new EventEmitter();
        this.selectedValues = new Array();
        this.expanded = false;
        this.expandAllSelectItems = false;
        this.searchValue = '';
        this.level = 0;
    }
    ngOnInit() {
        this.expandAllSelectItems = this.setAllSelectItemsExpanded;
        this.outsideClickListenerFn = this.renderer.listen('document', 'click', (e) => {
            if (this.expanded && !this.elementRef.nativeElement.contains(e.target)) {
                this.onExpandDropdownButtonClick();
            }
        });
    }
    ngOnDestroy() {
        if (this.outsideClickListenerFn) {
            this.outsideClickListenerFn();
        }
    }
    writeValue(value) {
        if (value) {
            if (this.primaryKey &&
                this.valueKey &&
                value instanceof Array &&
                value.length > 0 &&
                !(value[0] instanceof AnbMultiLevelDropdownModel)) {
                const vDropDownList = new Array();
                value.forEach((element) => {
                    vDropDownList.push(new AnbMultiLevelDropdownModel(String(element[this.primaryKey]), String(element[this.valueKey]), String(element[this.tooltipKey])));
                });
                this.selectedValues = vDropDownList;
            }
            else {
                this.selectedValues = value;
            }
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange(event) {
        // update form
        this.propagateChange(this.selectedValues);
        // close dropdown
        if (this.closeOnSelect) {
            this.dropdownSearchListElementRef.nativeElement.setAttribute('data-show', false);
            this.expanded = false;
        }
    }
    onExpandDropdownButtonClick() {
        this.filteredValues = this.values;
        this.searchValue = '';
        // event.stopPropagation();
        if (!this.disabled) {
            this.expanded = !this.expanded;
        }
        if (this.expanded) {
            setTimeout(() => this.searchInputElementRef.nativeElement.focus(), 0);
            if (!this.setAllSelectItemsExpanded) {
                this.expandAllSelectItems = false;
            }
        }
    }
    onSearchChange(event) {
        const searchValue = event.target.value;
        const clonedValues = cloneDeep(this.values);
        this.filteredValues = this.searchFilter(searchValue, clonedValues, []);
        // Fix: Na het filteren moet je anders 2 maal klikken om een item te selecteren.
        setTimeout(() => this.searchInputElementRef.nativeElement.blur(), 0);
        setTimeout(() => this.searchInputElementRef.nativeElement.focus(), 0);
        this.expandAllSelectItems = true;
    }
    searchFilter(search, values, results = []) {
        for (const value of values) {
            let isPushed = false;
            if (value.description.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
                results.push(value);
                isPushed = true;
            }
            if (value.children && value.children.length > 0) {
                const childsearch = this.searchFilter(search, value.children);
                if (childsearch && childsearch.length > 0) {
                    value.children = childsearch;
                    if (!isPushed) {
                        results.push(value);
                    }
                }
                else {
                    if (isPushed) {
                        value.children = new Array();
                    }
                }
            }
        }
        return results;
    }
    resetSearch() {
        this.searchValue = '';
        this.filteredValues = this.values;
    }
    removeSelection(value) {
        event.stopPropagation();
        this.onDelete.emit(value);
        this.selectedValues = this.selectedValues.filter((item) => item.dropdownModelID !== value.dropdownModelID);
        this.onChange(this.selectedValues);
    }
    addSelection(value) {
        if (this.selectedValues.filter((item) => item.dropdownModelID === value.dropdownModelID).length === 0) {
            this.onAdd.emit(value);
            if (this.onlyOneSelectable) {
                this.selectedValues = [];
                this.selectedValues.push(value);
            }
            else {
                this.selectedValues.push(value);
            }
            this.onChange(this.selectedValues);
        }
        else {
            this.removeSelection(value);
        }
    }
    getDropdownIDs(value) {
        return value.map((d) => d.dropdownModelID);
    }
    onItemSelected(event) {
        this.addSelection(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMultiLevelMultiSelectComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbMultiLevelMultiSelectComponent, selector: "anb-multi-level-multi-select", inputs: { values: "values", multiSelectId: "multiSelectId", primaryKey: "primaryKey", valueKey: "valueKey", tooltipKey: "tooltipKey", showTooltip: "showTooltip", closeOnSelect: "closeOnSelect", setAllSelectItemsExpanded: "setAllSelectItemsExpanded", onlyOneSelectable: "onlyOneSelectable", placeholder: "placeholder", selectedValues: "selectedValues" }, outputs: { onAdd: "onAdd", onDelete: "onDelete" }, providers: [
            SafePipe,
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbMultiLevelMultiSelectComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "searchInputElementRef", first: true, predicate: ["searchInput"], descendants: true }, { propertyName: "dropdownSearchListElementRef", first: true, predicate: ["dropdownSearchList"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  </label>\n\n  <div class=\"js-select\" [attr.id]=\"multiSelectId\">\n    <select\n      multiple\n      class=\"select u-visually-hidden\"\n      style=\"text-overflow: ellipsis\"\n      [attr.name]=\"'multiselect_' + multiSelectId\"\n      [attr.id]=\"multiSelectId\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      aria-hidden=\"true\"\n    >\n      <option *ngIf=\"placeholder\" value=\"\" data-placeholder>{{ placeholder }}</option>\n      <option\n        *ngFor=\"let v of filteredValues\"\n        [attr.value]=\"v.dropdownModelID\"\n        [attr.selected]=\"selectedValues.indexOf(v.dropdownModelID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ v.Description }}\n      </option>\n    </select>\n\n    <!-- pills -->\n    <div\n      data-focus\n      [attr.id]=\"multiSelectId\"\n      aria-expanded=\"true\"\n      class=\"js-select__input js-select__input--multi\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      <div *ngFor=\"let value of selectedValues\" class=\"pill\" [class.pill--closable]=\"!disabled\">\n        <a id=\"pill\" class=\"pill__close\" (click)=\"removeSelection(value)\" *ngIf=\"!disabled\"></a>\n        <span [attr.title]=\"showTooltip ? value.tooltipText : null\">{{ value.description }}</span>\n      </div>\n    </div>\n\n    <!-- Search -->\n    <div\n      class=\"select__wrapper\"\n      data-content\n      aria-labelledby=\"multiSelectId\"\n      [attr.data-show]=\"expanded\"\n      aria-hidden=\"true\"\n      #dropdownSearchList\n    >\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          autocomplete=\"off\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n\n        <!-- Listbox wrapper (results) -->\n        <div class=\"select__list-wrapper multi_level_multi_select\" role=\"listbox\">\n          <section class=\"select__list\" data-records>\n            <ul>\n              <li *ngFor=\"let v of filteredValues\" class=\"select__item\">\n                <anb-select-item\n                  [value]=\"v\"\n                  [level]=\"level\"\n                  (itemSelected)=\"onItemSelected($event)\"\n                  [isSelected]=\"getDropdownIDs(selectedValues).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                  [selectedValues]=\"selectedValues\"\n                  [hasChildren]=\"v.children.length > 0\"\n                  [expandAll]=\"expandAllSelectItems\"\n                  [showTooltip]=\"showTooltip\"\n                >\n                </anb-select-item>\n              </li>\n            </ul>\n          </section>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".disabled{background-color:#f4f5f7}.js-select__input.disabled{cursor:default}.pill{height:auto}.pill__close{border-left:none!important;float:right;margin-top:1px}::ng-deep .multi_level_multi_select .select__list>ul li{margin-left:20px}::ng-deep .multi_level_multi_select .select__list>ul ul{display:none}::ng-deep .multi_level_multi_select .select__list>ul ul.expanded{display:inherit}::ng-deep .multi_level_multi_select .select__list>ul>li{margin-left:0}::ng-deep .multi_level_multi_select .select__list .node-toggle{display:block;top:7px;left:24px;z-index:2}::ng-deep .multi_level_multi_select .select__list .node-toggle i{width:15px;height:20px;position:absolute;display:block;left:0}::ng-deep .multi_level_multi_select .select__cta__title{left:10px}::ng-deep .multi_level_multi_select .select__item:last-child{margin-bottom:0}::ng-deep .multi_level_multi_select .select__cta:focus:after,::ng-deep .multi_level_multi_select .select__cta:hover:after,::ng-deep .multi_level_multi_select .select__cta[data-selected=true]:after{left:0}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2$2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "component", type: SelectItemComponent, selector: "anb-select-item", inputs: ["value", "level", "isSelected", "selectedValues", "hasChildren", "expanded", "expandAll", "showTooltip"], outputs: ["itemSelected"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMultiLevelMultiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-multi-level-multi-select', providers: [
                        SafePipe,
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbMultiLevelMultiSelectComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  </label>\n\n  <div class=\"js-select\" [attr.id]=\"multiSelectId\">\n    <select\n      multiple\n      class=\"select u-visually-hidden\"\n      style=\"text-overflow: ellipsis\"\n      [attr.name]=\"'multiselect_' + multiSelectId\"\n      [attr.id]=\"multiSelectId\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      aria-hidden=\"true\"\n    >\n      <option *ngIf=\"placeholder\" value=\"\" data-placeholder>{{ placeholder }}</option>\n      <option\n        *ngFor=\"let v of filteredValues\"\n        [attr.value]=\"v.dropdownModelID\"\n        [attr.selected]=\"selectedValues.indexOf(v.dropdownModelID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ v.Description }}\n      </option>\n    </select>\n\n    <!-- pills -->\n    <div\n      data-focus\n      [attr.id]=\"multiSelectId\"\n      aria-expanded=\"true\"\n      class=\"js-select__input js-select__input--multi\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      <div *ngFor=\"let value of selectedValues\" class=\"pill\" [class.pill--closable]=\"!disabled\">\n        <a id=\"pill\" class=\"pill__close\" (click)=\"removeSelection(value)\" *ngIf=\"!disabled\"></a>\n        <span [attr.title]=\"showTooltip ? value.tooltipText : null\">{{ value.description }}</span>\n      </div>\n    </div>\n\n    <!-- Search -->\n    <div\n      class=\"select__wrapper\"\n      data-content\n      aria-labelledby=\"multiSelectId\"\n      [attr.data-show]=\"expanded\"\n      aria-hidden=\"true\"\n      #dropdownSearchList\n    >\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          autocomplete=\"off\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n\n        <!-- Listbox wrapper (results) -->\n        <div class=\"select__list-wrapper multi_level_multi_select\" role=\"listbox\">\n          <section class=\"select__list\" data-records>\n            <ul>\n              <li *ngFor=\"let v of filteredValues\" class=\"select__item\">\n                <anb-select-item\n                  [value]=\"v\"\n                  [level]=\"level\"\n                  (itemSelected)=\"onItemSelected($event)\"\n                  [isSelected]=\"getDropdownIDs(selectedValues).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                  [selectedValues]=\"selectedValues\"\n                  [hasChildren]=\"v.children.length > 0\"\n                  [expandAll]=\"expandAllSelectItems\"\n                  [showTooltip]=\"showTooltip\"\n                >\n                </anb-select-item>\n              </li>\n            </ul>\n          </section>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".disabled{background-color:#f4f5f7}.js-select__input.disabled{cursor:default}.pill{height:auto}.pill__close{border-left:none!important;float:right;margin-top:1px}::ng-deep .multi_level_multi_select .select__list>ul li{margin-left:20px}::ng-deep .multi_level_multi_select .select__list>ul ul{display:none}::ng-deep .multi_level_multi_select .select__list>ul ul.expanded{display:inherit}::ng-deep .multi_level_multi_select .select__list>ul>li{margin-left:0}::ng-deep .multi_level_multi_select .select__list .node-toggle{display:block;top:7px;left:24px;z-index:2}::ng-deep .multi_level_multi_select .select__list .node-toggle i{width:15px;height:20px;position:absolute;display:block;left:0}::ng-deep .multi_level_multi_select .select__cta__title{left:10px}::ng-deep .multi_level_multi_select .select__item:last-child{margin-bottom:0}::ng-deep .multi_level_multi_select .select__cta:focus:after,::ng-deep .multi_level_multi_select .select__cta:hover:after,::ng-deep .multi_level_multi_select .select__cta[data-selected=true]:after{left:0}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: AnbFormService }]; }, propDecorators: { values: [{
                type: Input
            }], multiSelectId: [{
                type: Input
            }], primaryKey: [{
                type: Input
            }], valueKey: [{
                type: Input
            }], tooltipKey: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], closeOnSelect: [{
                type: Input
            }], setAllSelectItemsExpanded: [{
                type: Input
            }], onlyOneSelectable: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], searchInputElementRef: [{
                type: ViewChild,
                args: ['searchInput']
            }], onAdd: [{
                type: Output
            }], onDelete: [{
                type: Output
            }], dropdownSearchListElementRef: [{
                type: ViewChild,
                args: ['dropdownSearchList']
            }], selectedValues: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-explicit-any */
// declare var vl: any;
class AnbMultiSelectComponent extends BaseFormElementComponent {
    set selectedValues(value) {
        this.selectedValues$.next(value);
    }
    constructor(elementRef, renderer, formService) {
        super(formService);
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.formService = formService;
        this.showTooltip = true;
        // link buttons will be shown to select all or nothing. (select all: alle items will be selected or only filtered results will be selected)
        this.showSelectionButtons = false;
        this.closeOnSelect = false;
        // Placeholder for initial title
        this.placeholder = null;
        this.onAdd = new EventEmitter();
        this.onDelete = new EventEmitter();
        this.showSelectedValuesAsCommaDelimitedString = false;
        this.expanded = false;
        this.searchValue = '';
        this.selectedValues$ = new BehaviorSubject([]);
        this.addValueToSelection$ = new Subject();
        this.removeValueFromSelection$ = new Subject();
        this.subscriptions = new Array();
    }
    ngOnInit() {
        super.ngOnInit();
        this.outsideClickListenerFn = this.renderer.listen('document', 'click', (e) => {
            if (this.expanded && !this.elementRef.nativeElement.contains(e.target)) {
                this.onExpandDropdownButtonClick();
            }
        });
        this.subscriptions.push(this.removeValueFromSelection$
            .pipe(withLatestFrom(this.selectedValues$), map(([valueToRemove, currentValues]) => currentValues.filter((cv) => cv.dropdownModelID !== valueToRemove.dropdownModelID)))
            .subscribe(this.selectedValues$));
        this.subscriptions.push(this.addValueToSelection$
            .pipe(withLatestFrom(this.selectedValues$), map(([valueToAdd, currentValues]) => {
            currentValues.push(valueToAdd);
            return currentValues;
        }))
            .subscribe(this.selectedValues$));
        this.selectedValuesAsCommaSeparatedString$ = this.selectedValues$.pipe(map((sv) => sv.map((v) => v.description)), map((sv) => (sv.length > 0 ? sv.join(', ') : null)));
    }
    ngOnDestroy() {
        if (this.outsideClickListenerFn) {
            this.outsideClickListenerFn();
        }
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
    writeValue(value) {
        if (value) {
            if (this.primaryKey && this.valueKey && value instanceof Array && value.length > 0 && !(value[0] instanceof DropdownModel)) {
                const vDropDownList = new Array();
                value.forEach((element) => {
                    vDropDownList.push(new DropdownModel(String(element[this.primaryKey]), String(element[this.valueKey]), String(element[this.tooltipKey]), element.additionalData, element.objectID, element.objectType));
                });
                this.selectedValues$.next(vDropDownList);
            }
            else {
                this.selectedValues$.next(value);
            }
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange(event) {
        // update form
        this.propagateChange(this.selectedValues$.value);
        // close dropdown
        if (this.closeOnSelect) {
            this.dropdownSearchListElementRef.nativeElement.setAttribute('data-show', false);
            this.expanded = false;
        }
    }
    onExpandDropdownButtonClick() {
        this.filteredValues = this.values;
        this.searchValue = '';
        // event.stopPropagation();
        if (!this.disabled) {
            this.expanded = !this.expanded;
        }
        if (this.expanded) {
            setTimeout(() => this.searchInputElementRef.nativeElement.focus(), 0);
        }
    }
    onSearchChange(event) {
        const searchValue = event.target.value;
        this.filteredValues = this.values.filter((x) => x.description.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
    }
    resetSearch() {
        this.searchValue = '';
        this.filteredValues = this.values;
    }
    removeSelection(value) {
        event.stopPropagation();
        this.onDelete.emit(value);
        this.removeValueFromSelection$.next(value);
        this.onChange(this.selectedValues$.value);
    }
    addSelection(value) {
        if (this.selectedValues$.value.filter((item) => item.dropdownModelID === value.dropdownModelID).length === 0) {
            this.onAdd.emit(value);
            this.addValueToSelection$.next(value);
            this.onChange(this.selectedValues$.value);
        }
        else {
            this.removeSelection(value);
        }
    }
    getDropdownIDs(value) {
        return value.map((d) => d.dropdownModelID);
    }
    onClickSelecteerAlles() {
        this.selectedValues$.next(this.filteredValues.slice());
        this.onChange(this.selectedValues$.value);
    }
    onClickSelecteerNiets() {
        this.selectedValues$.next([]);
        this.onChange(this.selectedValues$.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMultiSelectComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbMultiSelectComponent, selector: "anb-multi-select", inputs: { values: "values", multiSelectId: "multiSelectId", primaryKey: "primaryKey", valueKey: "valueKey", tooltipKey: "tooltipKey", showTooltip: "showTooltip", showSelectionButtons: "showSelectionButtons", closeOnSelect: "closeOnSelect", placeholder: "placeholder", selectedValues: "selectedValues", showSelectedValuesAsCommaDelimitedString: "showSelectedValuesAsCommaDelimitedString" }, outputs: { onAdd: "onAdd", onDelete: "onDelete" }, providers: [
            SafePipe,
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbMultiSelectComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "searchInputElementRef", first: true, predicate: ["searchInput"], descendants: true }, { propertyName: "dropdownSearchListElementRef", first: true, predicate: ["dropdownSearchList"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  </label>\n\n  <div class=\"js-select\" [attr.id]=\"multiSelectId\">\n    <select\n      multiple\n      class=\"select u-visually-hidden\"\n      style=\"text-overflow: ellipsis\"\n      [attr.name]=\"'multiselect_' + multiSelectId\"\n      [attr.id]=\"multiSelectId\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      aria-hidden=\"true\"\n    >\n      <option *ngIf=\"placeholder\" value=\"\" data-placeholder>{{ placeholder }}</option>\n      <option\n        *ngFor=\"let v of filteredValues\"\n        [attr.value]=\"v.dropdownModelID\"\n        [attr.selected]=\"(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ v.description }}\n      </option>\n    </select>\n\n    <div\n      data-focus\n      [attr.id]=\"multiSelectId\"\n      aria-expanded=\"true\"\n      class=\"js-select__input js-select__input--multi\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      <div *ngIf=\"showSelectedValuesAsCommaDelimitedString; then showSelectionAsString; else showSelectionAsPills\"></div>\n\n      <!-- comma-separated string -->\n      <ng-template #showSelectionAsString>\n        <div *ngIf=\"selectedValuesAsCommaSeparatedString$ | async; then selectionString; else placeholder1\"></div>\n        <ng-template #selectionString>\n          <span\n            class=\"overflowing-text\"\n            *ngIf=\"selectedValuesAsCommaSeparatedString$ | async as commaSeparatedString\"\n            [attr.title]=\"showTooltip ? commaSeparatedString : null\"\n            >{{ commaSeparatedString }}</span\n          >\n        </ng-template>\n\n        <ng-template #placeholder1>\n          <span *ngIf=\"placeholder\" class=\"placeholder\">{{ placeholder }}</span>\n        </ng-template>\n      </ng-template>\n\n      <!-- pills -->\n      <ng-template #showSelectionAsPills>\n        <div *ngIf=\"(selectedValues$ | async)?.length > 0; then selectionPills; else placeholder2\"></div>\n\n        <ng-template #selectionPills>\n          <div *ngFor=\"let value of selectedValues$ | async\" class=\"pill\" [class.pill--closable]=\"!disabled\">\n            <a id=\"pill\" class=\"pill__close\" (click)=\"removeSelection(value)\" *ngIf=\"!disabled\"></a>\n            <span [attr.title]=\"showTooltip ? value.tooltip : null\">{{ value.description }}</span>\n          </div>\n        </ng-template>\n\n        <ng-template #placeholder2>\n          <span *ngIf=\"placeholder\" class=\"placeholder\">{{ placeholder }}</span>\n        </ng-template>\n      </ng-template>\n    </div>\n\n    <!-- Search -->\n    <div\n      class=\"select__wrapper\"\n      data-content\n      aria-labelledby=\"multiSelectId\"\n      [attr.data-show]=\"expanded\"\n      aria-hidden=\"true\"\n      #dropdownSearchList\n    >\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          autocomplete=\"off\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n\n        <ng-container *ngIf=\"showSelectionButtons\">\n          <div class=\"u-align-right\" style=\"padding-right: 20px\">\n            Selecteer\n            <a href=\"javascript:void(0)\" class=\"link-icon\" (click)=\"onClickSelecteerAlles()\"><span>alles</span></a>\n            <span>/</span>\n            <a href=\"javascript:void(0)\" class=\"link-icon\" (click)=\"onClickSelecteerNiets()\"><span>niets</span></a>\n          </div>\n        </ng-container>\n\n        <!-- Listbox wrapper (results) -->\n        <div class=\"select__list-wrapper\" role=\"listbox\">\n          <section class=\"select__list\" data-records>\n            <div *ngFor=\"let v of filteredValues\" class=\"select__item\">\n              <button\n                (click)=\"addSelection(v)\"\n                class=\"select__cta\"\n                [class.select__cta--active]=\"getDropdownIDs(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                aria-selected=\"false\"\n                [attr.id]=\"'select__list__item__id__' + v.dropdownModelID\"\n                [attr.data-index]=\"v.dropdownModelID\"\n                [attr.data-value]=\"v.dropdownModelID\"\n                [attr.data-label]=\"v.description\"\n                [attr.data-selected]=\"getDropdownIDs(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                [disabled]=\"disabled\"\n                type=\"button\"\n              >\n                <span class=\"select__cta__title\" [attr.title]=\"showTooltip ? v.tooltip : null\">{{ v.description }}</span>\n              </button>\n            </div>\n          </section>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\" [innerHtml]=\"annotation | safe: 'html'\"></div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".disabled{background-color:#f4f5f7}.js-select__input.disabled{cursor:default}.pill{height:auto}.pill__close{border-left:none!important;float:right;margin-top:1px}.overflowing-text{display:inline-block;width:99%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;height:auto;vertical-align:middle}.readonly ul{padding-left:1rem}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2$2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbMultiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-multi-select', providers: [
                        SafePipe,
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbMultiSelectComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  </label>\n\n  <div class=\"js-select\" [attr.id]=\"multiSelectId\">\n    <select\n      multiple\n      class=\"select u-visually-hidden\"\n      style=\"text-overflow: ellipsis\"\n      [attr.name]=\"'multiselect_' + multiSelectId\"\n      [attr.id]=\"multiSelectId\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      aria-hidden=\"true\"\n    >\n      <option *ngIf=\"placeholder\" value=\"\" data-placeholder>{{ placeholder }}</option>\n      <option\n        *ngFor=\"let v of filteredValues\"\n        [attr.value]=\"v.dropdownModelID\"\n        [attr.selected]=\"(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ v.description }}\n      </option>\n    </select>\n\n    <div\n      data-focus\n      [attr.id]=\"multiSelectId\"\n      aria-expanded=\"true\"\n      class=\"js-select__input js-select__input--multi\"\n      [class.select--error]=\"showError() && !disabled\"\n      [class.disabled]=\"disabled\"\n      (click)=\"onExpandDropdownButtonClick()\"\n    >\n      <div *ngIf=\"showSelectedValuesAsCommaDelimitedString; then showSelectionAsString; else showSelectionAsPills\"></div>\n\n      <!-- comma-separated string -->\n      <ng-template #showSelectionAsString>\n        <div *ngIf=\"selectedValuesAsCommaSeparatedString$ | async; then selectionString; else placeholder1\"></div>\n        <ng-template #selectionString>\n          <span\n            class=\"overflowing-text\"\n            *ngIf=\"selectedValuesAsCommaSeparatedString$ | async as commaSeparatedString\"\n            [attr.title]=\"showTooltip ? commaSeparatedString : null\"\n            >{{ commaSeparatedString }}</span\n          >\n        </ng-template>\n\n        <ng-template #placeholder1>\n          <span *ngIf=\"placeholder\" class=\"placeholder\">{{ placeholder }}</span>\n        </ng-template>\n      </ng-template>\n\n      <!-- pills -->\n      <ng-template #showSelectionAsPills>\n        <div *ngIf=\"(selectedValues$ | async)?.length > 0; then selectionPills; else placeholder2\"></div>\n\n        <ng-template #selectionPills>\n          <div *ngFor=\"let value of selectedValues$ | async\" class=\"pill\" [class.pill--closable]=\"!disabled\">\n            <a id=\"pill\" class=\"pill__close\" (click)=\"removeSelection(value)\" *ngIf=\"!disabled\"></a>\n            <span [attr.title]=\"showTooltip ? value.tooltip : null\">{{ value.description }}</span>\n          </div>\n        </ng-template>\n\n        <ng-template #placeholder2>\n          <span *ngIf=\"placeholder\" class=\"placeholder\">{{ placeholder }}</span>\n        </ng-template>\n      </ng-template>\n    </div>\n\n    <!-- Search -->\n    <div\n      class=\"select__wrapper\"\n      data-content\n      aria-labelledby=\"multiSelectId\"\n      [attr.data-show]=\"expanded\"\n      aria-hidden=\"true\"\n      #dropdownSearchList\n    >\n      <div class=\"select__form\">\n        <input\n          #searchInput\n          type=\"text\"\n          autocomplete=\"off\"\n          class=\"input-field input-field--block\"\n          [value]=\"searchValue\"\n          (input)=\"searchValue = $event.target.value\"\n          (change)=\"onSearchChange($event)\"\n          (keyup)=\"onSearchChange($event)\"\n        />\n\n        <ng-container *ngIf=\"showSelectionButtons\">\n          <div class=\"u-align-right\" style=\"padding-right: 20px\">\n            Selecteer\n            <a href=\"javascript:void(0)\" class=\"link-icon\" (click)=\"onClickSelecteerAlles()\"><span>alles</span></a>\n            <span>/</span>\n            <a href=\"javascript:void(0)\" class=\"link-icon\" (click)=\"onClickSelecteerNiets()\"><span>niets</span></a>\n          </div>\n        </ng-container>\n\n        <!-- Listbox wrapper (results) -->\n        <div class=\"select__list-wrapper\" role=\"listbox\">\n          <section class=\"select__list\" data-records>\n            <div *ngFor=\"let v of filteredValues\" class=\"select__item\">\n              <button\n                (click)=\"addSelection(v)\"\n                class=\"select__cta\"\n                [class.select__cta--active]=\"getDropdownIDs(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                aria-selected=\"false\"\n                [attr.id]=\"'select__list__item__id__' + v.dropdownModelID\"\n                [attr.data-index]=\"v.dropdownModelID\"\n                [attr.data-value]=\"v.dropdownModelID\"\n                [attr.data-label]=\"v.description\"\n                [attr.data-selected]=\"getDropdownIDs(selectedValues$ | async).indexOf(v.dropdownModelID) > -1 ? 'true' : false\"\n                [disabled]=\"disabled\"\n                type=\"button\"\n              >\n                <span class=\"select__cta__title\" [attr.title]=\"showTooltip ? v.tooltip : null\">{{ v.description }}</span>\n              </button>\n            </div>\n          </section>\n        </div>\n      </div>\n    </div>\n  </div>\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\" [innerHtml]=\"annotation | safe: 'html'\"></div>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".disabled{background-color:#f4f5f7}.js-select__input.disabled{cursor:default}.pill{height:auto}.pill__close{border-left:none!important;float:right;margin-top:1px}.overflowing-text{display:inline-block;width:99%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;height:auto;vertical-align:middle}.readonly ul{padding-left:1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: AnbFormService }]; }, propDecorators: { values: [{
                type: Input
            }], multiSelectId: [{
                type: Input
            }], primaryKey: [{
                type: Input
            }], valueKey: [{
                type: Input
            }], tooltipKey: [{
                type: Input
            }], showTooltip: [{
                type: Input
            }], showSelectionButtons: [{
                type: Input
            }], closeOnSelect: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], searchInputElementRef: [{
                type: ViewChild,
                args: ['searchInput']
            }], onAdd: [{
                type: Output
            }], onDelete: [{
                type: Output
            }], dropdownSearchListElementRef: [{
                type: ViewChild,
                args: ['dropdownSearchList']
            }], selectedValues: [{
                type: Input
            }], showSelectedValuesAsCommaDelimitedString: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
// const ch = choicesImported;
class AnbPillInputComponent extends BaseFormElementComponent {
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        // Wanneer de value aanpast, dan moeten we deze items toevoegen aan de choices.js component
        if (value && this.value !== value) {
            this.addPills(value);
        }
        this.value = value;
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService, elementRef) {
        super(formService); // Constructors for derived classes must contain a 'super' call.
        this.formService = formService;
        this.elementRef = elementRef;
        this.value = '';
    }
    ngOnDestroy() {
        this.deleteCurrentChoicesJsInstance();
    }
    ngAfterViewInit() {
        // Init the webuniversum input-pill
        const pillInput = window.vl?.pillInput;
        if (pillInput) {
            pillInput.dressAll();
        }
        // the vl package clones the input field.
        // When the component is disabled, the clone must be also disabled
        const nativeElement = this.elementRef.nativeElement;
        if (nativeElement) {
            this.clonedInput = nativeElement.querySelector('input.vl-pill__input.choices__input--cloned');
            this.inputInnerDiv = nativeElement.querySelector('.vl-pill-input__inner');
            this.choicesElement = nativeElement.querySelector('[data-vl-pill-input]');
            this.enableDisableClonedInput();
        }
    }
    ngOnInit() {
        super.ngOnInit();
    }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        this.enableDisableClonedInput();
    }
    onChange(event) {
        this.value = event.target.value;
        // update form
        this.propagateChange(this.value);
        this.valueChange(this.value);
    }
    enableDisableClonedInput() {
        if (this.form && this.clonedInput && this.disabled !== this.isCurrentlyDisabled) {
            this.clonedInput.disabled = this.disabled;
            if (this.inputInnerDiv) {
                const choices = this.getCurrentChoicesJsInstance();
                if (this.disabled) {
                    if (choices) {
                        choices.disable();
                    }
                    this.inputInnerDiv.classList.add('vl-pill-input--disabled');
                }
                else {
                    this.inputInnerDiv.classList.remove('vl-pill-input--disabled');
                    if (choices) {
                        choices.enable();
                    }
                }
            }
            this.isCurrentlyDisabled = this.disabled;
        }
    }
    addPills(value) {
        const choices = this.getCurrentChoicesJsInstance();
        if (choices && value) {
            choices.clearStore();
            choices.setValue(value.split(','));
        }
    }
    getCurrentChoicesJsInstance() {
        const pillInput = window.vl?.pillInput;
        const instances = pillInput.selectInstances;
        if (instances && instances.length) {
            const instance = instances.find((i) => i.baseId === `choices-${this.id}`);
            return instance;
        }
    }
    deleteCurrentChoicesJsInstance() {
        const instance = this.getCurrentChoicesJsInstance();
        const pillInput = window.vl?.pillInput;
        const instances = pillInput.selectInstances;
        const index = instances.indexOf(instance);
        pillInput.selectInstances.splice(index, 1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPillInputComponent, deps: [{ token: AnbFormService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbPillInputComponent, selector: "anb-pill-input", inputs: { placeholder: "placeholder" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbPillInputComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbPillInputComponent),
                multi: true,
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <div class=\"vl-pill-input__wrapper\" [class.error]=\"showError() && !disabled\">\n    <input\n      class=\"vl-input-field\"\n      type=\"text\"\n      [value]=\"value\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      (keyup)=\"onChange($event)\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.disabled]=\"disabled\"\n      data-vl-pill-input\n      data-vl-pill-input-unique=\"true\"\n    />\n  </div>\n\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError() && !disabled\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: ["::ng-deep .vl-pill-input__inner{background-color:#fff}::ng-deep .error .vl-pill-input__inner{background-color:#fbe6e6;border-color:#db3434;box-shadow:inset 0 0 0 1px #db3434}::ng-deep .vl-pill-input--disabled{background-color:#f4f5f7!important}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbPillInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-pill-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbPillInputComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbPillInputComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <div class=\"vl-pill-input__wrapper\" [class.error]=\"showError() && !disabled\">\n    <input\n      class=\"vl-input-field\"\n      type=\"text\"\n      [value]=\"value\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n      (keyup)=\"onChange($event)\"\n      [attr.placeholder]=\"placeholder\"\n      [attr.disabled]=\"disabled\"\n      data-vl-pill-input\n      data-vl-pill-input-unique=\"true\"\n    />\n  </div>\n\n  <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n  <div *ngIf=\"showError() && !disabled\" class=\"webuniversum form__error\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: ["::ng-deep .vl-pill-input__inner{background-color:#fff}::ng-deep .error .vl-pill-input__inner{background-color:#fbe6e6;border-color:#db3434;box-shadow:inset 0 0 0 1px #db3434}::ng-deep .vl-pill-input--disabled{background-color:#f4f5f7!important}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i0.ElementRef }]; }, propDecorators: { placeholder: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbQuestionComponent extends BaseFormElementComponent {
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.value = '';
    }
    ngOnInit() {
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbQuestionComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbQuestionComponent, selector: "anb-question", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbQuestionComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum form__label\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <!--<anb-popover [infoText]=\"subLabel\"></anb-popover>-->\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbQuestionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-question', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbQuestionComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum form__label\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <!--<anb-popover [infoText]=\"subLabel\"></anb-popover>-->\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbRadiobuttonComponent extends BaseFormElementComponent {
    // private value: string = '';
    // private propagateChange = (_: any) => { };
    // set value(val) {
    //    this._value = val;
    //    this.onChange(val);
    // }
    writeValue(value) {
        this.innervalue = value;
        // we zetten de waarde hier
        // wanneer je in de template checked rechtstreeks koppelt aan de methode, dan werkt het niet
        this.checked = this.isChecked();
        if (this.el.nativeElement != null)
            this.renderer.setProperty(this.el.nativeElement, 'checked', this.checked);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    isChecked() {
        // return !!this.innervalue === !!this.value;//!!will make sure null or undefined equals to false
        return this.innervalue === this.value; // do not even select false if undefined or null
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange(event) {
        this.innervalue = this.value; // event.target.checked;
        // update form
        this.propagateChange(this.innervalue);
    }
    constructor(formService, renderer) {
        super(formService);
        this.formService = formService;
        this.renderer = renderer;
        this.errorlabelhidden = false;
        this.checked = false;
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.name === undefined) {
            // je kan een name toevoegen, maar dit is niet verplicht
            // indien er geen name opgegeven is, koppelen we deze aan de formcontrolname
            this.name = this.formControlName;
        }
        if (this.value === undefined) {
            console.error("The property 'value' of the anb-radiobutton " + this.formControlName + ' is undefined.');
        }
        // bij de standaaard naamgeving zouden alle radiobuttons van dezelfde lijst, dezelfde id krijge, dit is hiermee opgelost
        this.id = this.id + '_' + this.value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbRadiobuttonComponent, deps: [{ token: AnbFormService }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbRadiobuttonComponent, selector: "anb-radiobutton", inputs: { name: "name", value: "value", errorlabelhidden: "errorlabelhidden", checked: "checked" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbRadiobuttonComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "el", first: true, predicate: ["input"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\">\n  <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n    <input\n      #input\n      type=\"radio\"\n      class=\"webuniversum radio__toggle\"\n      [value]=\"value\"\n      [name]=\"name\"\n      [attr.id]=\"id\"\n      (change)=\"onChange($event.target.value)\"\n      [checked]=\"checked\"\n      [disabled]=\"disabled\"\n    /><span></span><span [innerHtml]=\"label | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"showError() && !errorlabelhidden\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".radio.radio--disabled{color:#666}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbRadiobuttonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-radiobutton', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbRadiobuttonComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\">\n  <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n    <input\n      #input\n      type=\"radio\"\n      class=\"webuniversum radio__toggle\"\n      [value]=\"value\"\n      [name]=\"name\"\n      [attr.id]=\"id\"\n      (change)=\"onChange($event.target.value)\"\n      [checked]=\"checked\"\n      [disabled]=\"disabled\"\n    /><span></span><span [innerHtml]=\"label | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"showError() && !errorlabelhidden\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".radio.radio--disabled{color:#666}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i0.Renderer2 }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], errorlabelhidden: [{
                type: Input
            }], checked: [{
                type: Input
            }], el: [{
                type: ViewChild,
                args: ['input', { static: true }]
            }] } });

class S3Attachment {
}

class S3UploadService {
}

class Alert {
}
var AlertType;
(function (AlertType) {
    AlertType[AlertType["Success"] = 0] = "Success";
    AlertType[AlertType["Error"] = 1] = "Error";
    AlertType[AlertType["Info"] = 2] = "Info";
    AlertType[AlertType["Warning"] = 3] = "Warning";
})(AlertType || (AlertType = {}));

class AlertService {
    constructor() {
        this.subject = new Subject();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getAlert() {
        return this.subject.asObservable();
    }
    success(message) {
        this.alert(AlertType.Success, message, 'Succes!');
    }
    error(message) {
        this.alert(AlertType.Error, message, 'Fout!');
    }
    info(message) {
        this.alert(AlertType.Info, message, 'Informatie!');
    }
    warn(message) {
        this.alert(AlertType.Warning, message, 'Waarschuwing!');
    }
    alert(type, message, title) {
        this.subject.next({ type: type, message: message, title: title });
    }
    clear() {
        // clear alert
        this.subject.next(null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService, decorators: [{
            type: Injectable
        }] });

class FileService {
    static getRegExp(extensions) {
        return new RegExp(`(.*?)\.(${extensions})$`);
    }
    constructor(alertService, s3UploadService, http) {
        this.alertService = alertService;
        this.s3UploadService = s3UploadService;
        this.http = http;
    }
    verifyFiles(files, acceptExtensions) {
        if (files.length > 0) {
            const filesArray = Array.from(files);
            const filesNotMatchExtensions = filesArray.filter((file) => {
                const extensionsList = acceptExtensions
                    .toLowerCase()
                    .split(', ')
                    .map((extension) => extension.slice(1))
                    .join('|');
                const regexp = FileService.getRegExp(extensionsList);
                return !regexp.test(file.name.toLowerCase());
            });
            if (filesNotMatchExtensions.length) {
                this.error('Bestandsextensie  is niet toegelaten. Enkel deze extensies worden toegelaten: ' + acceptExtensions);
                return false;
            }
            return true;
        }
    }
    error(message) {
        this.alertService.error(message);
    }
    download(attachmentGuid, filename) {
        this.s3UploadService.getAttachment(attachmentGuid).subscribe((result) => {
            this.downloadByURL(result.preSignedURL, filename);
        });
    }
    downloadByURL(downloadUrl, filename) {
        this.downloadFile(downloadUrl).subscribe((file) => {
            this.downloadByBlob(file, filename);
        });
    }
    downloadByBlob(blobData, filename) {
        const contentType = '';
        const blob = new Blob([blobData], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }
    downloadFile(url) {
        return this.http.get(url, { responseType: 'blob' });
    }
    delete(attachmentGuid, dossierId) {
        if (dossierId) {
            return this.s3UploadService.deleteAttachment(attachmentGuid, dossierId);
        }
        else {
            return this.s3UploadService.deleteAttachment(attachmentGuid);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FileService, deps: [{ token: AlertService }, { token: S3UploadService }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FileService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FileService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AlertService }, { type: S3UploadService }, { type: i1$2.HttpClient }]; } });

class AnbFilePreviewerComponent {
    constructor() {
        this.noPreviewAvailable = 'Geen preview beschikbaar';
        this.previewData = null;
        this.subs = new SubSink();
        this.s3UploadService = inject(S3UploadService);
        this.sanitizer = inject(DomSanitizer);
    }
    ngOnInit() {
        this.attachmentPreview();
    }
    ngOnChanges(changes) {
        if (changes['attachmentId'] && !changes['attachmentId'].firstChange) {
            this.attachmentPreview();
        }
    }
    attachmentPreview() {
        this.loading = true;
        if (!this.attachmentId) {
            this.previewData = null;
            return;
        }
        const getAttachmentSub = this.s3UploadService.getAttachment(this.attachmentId).subscribe((attachment) => {
            if (attachment.fileName.toLowerCase().endsWith('.bmp') || attachment.fileName.toLowerCase().endsWith('.svg') || attachment.fileName.toLowerCase().endsWith('.png') || attachment.fileName.toLowerCase().endsWith('.jpg') || attachment.fileName.toLowerCase().endsWith('.jpeg')) {
                this.previewData = {
                    type: 'image',
                    data: attachment.preSignedURL
                };
            }
            else if (attachment.fileName.toLowerCase().endsWith('.pdf')) {
                this.previewData = {
                    type: 'pdf',
                    data: this.sanitizer.bypassSecurityTrustResourceUrl(attachment.preSignedURL)
                };
            }
            else if (attachment.fileName.toLowerCase().endsWith('.txt')) {
                this.previewData = {
                    type: 'text',
                    data: this.sanitizer.bypassSecurityTrustResourceUrl(attachment.preSignedURL)
                };
            }
            else {
                this.previewData = {
                    type: 'unsupported',
                    data: this.noPreviewAvailable
                };
            }
            this.loading = false;
        });
        this.subs.add(getAttachmentSub);
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFilePreviewerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbFilePreviewerComponent, selector: "anb-file-previewer", inputs: { attachmentId: "attachmentId", noPreviewAvailable: "noPreviewAvailable" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"file-preview\" *ngIf=\"previewData\">\n  <div *ngIf=\"loading\">Bezig met een voorbeeld van het document te laden</div>\n  <ng-container [ngSwitch]=\"previewData.type\">\n    <div *ngSwitchCase=\"'text'\">\n      <iframe [src]=\"previewData.data\" width=\"100%\" height=\"auto\" frameborder=\"0\"></iframe>\n    </div>\n    <div *ngSwitchCase=\"'pdf'\">\n      <iframe [src]=\"previewData.data\" width=\"100%\" height=\"auto\" frameborder=\"0\"></iframe>\n    </div>\n    <div *ngSwitchCase=\"'image'\">\n      <img [src]=\"previewData.data\" alt=\"Image preview\" style=\"max-width: 100%; height: auto\" />\n    </div>\n    <div *ngSwitchDefault>Geen voorbeeld beschikbaar voor dit type document.</div>\n  </ng-container>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i2.NgSwitchDefault, selector: "[ngSwitchDefault]" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFilePreviewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-file-previewer', template: "<div class=\"file-preview\" *ngIf=\"previewData\">\n  <div *ngIf=\"loading\">Bezig met een voorbeeld van het document te laden</div>\n  <ng-container [ngSwitch]=\"previewData.type\">\n    <div *ngSwitchCase=\"'text'\">\n      <iframe [src]=\"previewData.data\" width=\"100%\" height=\"auto\" frameborder=\"0\"></iframe>\n    </div>\n    <div *ngSwitchCase=\"'pdf'\">\n      <iframe [src]=\"previewData.data\" width=\"100%\" height=\"auto\" frameborder=\"0\"></iframe>\n    </div>\n    <div *ngSwitchCase=\"'image'\">\n      <img [src]=\"previewData.data\" alt=\"Image preview\" style=\"max-width: 100%; height: auto\" />\n    </div>\n    <div *ngSwitchDefault>Geen voorbeeld beschikbaar voor dit type document.</div>\n  </ng-container>\n</div>\n" }]
        }], propDecorators: { attachmentId: [{
                type: Input
            }], noPreviewAvailable: [{
                type: Input
            }] } });

class AnbS3UploadComponent extends BaseFormElementComponent {
    set acceptedFileTypes(input) {
        if (input)
            this.fileTypes = input;
    }
    constructor(formService, http, fb, s3UploadService, fileService) {
        super(formService);
        this.formService = formService;
        this.http = http;
        this.fb = fb;
        this.s3UploadService = s3UploadService;
        this.fileService = fileService;
        this.hideType = false;
        this.hideDescription = false;
        this.hideCreationDate = false;
        this.hideEditButton = true;
        this.hideSingleFileDeleteButton = true;
        this.disableExtraInfoForm = false;
        this.isTypeRequired = false;
        this.useIconButtons = false;
        this.hideDatatable = false;
        this.singleFile = false;
        this.seperateViewLink = false;
        this.alwaysDisplayLabel = false;
        this.hasPreview = false;
        this.descriptionLabel = 'Omschrijving';
        this.typeLabel = 'Type';
        this.filenameLabel = 'Bestandsnaam';
        this.creationDateLabel = 'Datum upload';
        this.saveButtonLabel = 'Opslaan';
        this.cancelButtonLabel = 'Annuleer';
        this.addAttachmentLabel = 'Bijlage toevoegen';
        this.noPreviewAvailable = 'Geen preview beschikbaar';
        this.onAttachmentAdding = new EventEmitter();
        this.onAttachmentAdded = new EventEmitter();
        this.onAttachmentDeleted = new EventEmitter();
        this.value = [];
        this.showExtraInfoForm = false;
        this.fileError = null;
        this.aseEnum = ActivityStatusEnablementEnum;
        this.readOnly = this.ase === ActivityStatusEnablementEnum.ViewOnly;
        this.fileTypes = '.pdf, .doc, .docx, .jpeg, .jpg, .png, .bmp, .rtf, .xls, .xlsx, .html, .htm, .eml, .msg, .gpx';
        const vForm = this.fb.group({
            s3attachment: [null, null],
            filename: [null, null],
            description: [null, null],
            type: [null, null],
        });
        this.extraInfoForm = new AnbFormGroup(vForm.controls);
        this.extraInfoForm.activityStatus.next(ActivityStatusEnum.Edit);
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (changes.isTypeRequired) {
            const control = this.extraInfoForm.get('type');
            if (changes.isTypeRequired.currentValue) {
                control.setValidators(Validators.required);
            }
            else {
                control.setValidators([]);
            }
            control.updateValueAndValidity();
        }
        else if (changes.ase) {
            this.readOnly = this.ase === ActivityStatusEnablementEnum.ViewOnly;
        }
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    writeValue(value) {
        if (value) {
            this.value = value;
        }
        else {
            this.value = [];
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onFileChange(event) {
        this.fileError = null;
        if (event.target.files && event.target.files.length) {
            const fileList = event.target.files;
            const [file] = fileList;
            let size = event.srcElement.files[0].size;
            size = size / 1000 / 1000; // in MB;
            if (this.maxFileSizeMB && size > this.maxFileSizeMB) {
                event.target.value = '';
                this.fileError = `Bestandsgrootte werd overschreden. Maximum ${this.maxFileSizeMB}MB toegelaten.`;
                return;
            }
            if (!this.fileService.verifyFiles(fileList, this.fileTypes)) {
                event.target.value = '';
                this.fileError = `Bestandsextensie is niet toegelaten. Enkel deze extensies worden toegelaten: ${this.fileTypes}`;
                return;
            }
            this.fileName = file.name;
            this.file = file;
            if (this.disableExtraInfoForm) {
                this.upload(file);
            }
            else {
                this.showExtraInfoFormDialog();
            }
        }
    }
    showExtraInfoFormDialog() {
        this.extraInfoForm.patchValue({
            filename: this.fileName,
        });
        this.showExtraInfoForm = true;
    }
    closeDialog() {
        this.showExtraInfoForm = false;
    }
    onSaveClick() {
        if (!this.extraInfoForm.valid) {
            this.formService.markAsDirty(this.extraInfoForm);
            return;
        }
        this.closeDialog();
        const s3Attachment = this.extraInfoForm.get('s3attachment')?.value;
        if (s3Attachment) {
            this.update(s3Attachment);
        }
        else {
            this.upload(this.file);
        }
    }
    onCancelClick() {
        this.fileInputField.nativeElement.value = '';
        this.extraInfoForm.reset();
        this.closeDialog();
    }
    upload(file) {
        if (!file) {
            throw new Error('No attachment file found.');
        }
        const attachment = new S3Attachment();
        attachment.fileName = this.fileName;
        attachment.type = this.attachmentType;
        attachment.typeId = this.attachmentTypeId;
        this.updateS3AttachmentWithFormValues(attachment);
        // Request preSignedUploadURL
        const uploadSub = this.s3UploadService
            .addAttachment(attachment)
            .pipe(concatMap((result) => {
            if (!result?.preSignedUploadURL) {
                return throwError('Could not get S3 preSignedUploadURL.');
            }
            this.onAttachmentAdding?.emit();
            // PUT to S3
            return this.http.put(result.preSignedUploadURL, file).pipe(switchMap(() => {
                // AcknowledgeAttachment
                attachment.attachmentGuid = result.attachmentGuid;
                return this.s3UploadService.acknowledgeAttachment(attachment).pipe(tap(() => {
                    this.fileInputField.nativeElement.value = '';
                    this.extraInfoForm.reset();
                    this.value.push(attachment);
                    this.setValue(this.value);
                    if (this.hasPreview) {
                        this.setAttachmentGuid(attachment.attachmentGuid);
                    }
                    this.onAttachmentAdded?.emit(attachment);
                }));
            }));
        }), catchError((error) => {
            this.handleUploadError();
            return throwError(error);
        }))
            .subscribe();
        this.subs.add(uploadSub);
    }
    update(attachment) {
        if (attachment) {
            this.updateS3AttachmentWithFormValues(attachment);
            this.setValue(this.value);
        }
    }
    updateS3AttachmentWithFormValues(attachment) {
        const descriptionControl = this.extraInfoForm.get('description');
        if (descriptionControl) {
            attachment.description = descriptionControl.value;
        }
        const typeControl = this.extraInfoForm.get('type');
        if (typeControl && typeControl.value) {
            attachment.typeId = typeControl.value;
            if (this.typeDropdownList) {
                const selectedValue = this.typeDropdownList.find((x) => x.dropdownModelID === typeControl.value);
                if (selectedValue) {
                    attachment.type = selectedValue.description;
                }
            }
        }
    }
    download(attachmentGuid, filename) {
        this.fileService.download(attachmentGuid, filename);
    }
    deleteAttachment(attachment) {
        const deleteSubs = this.s3UploadService.deleteAttachment(attachment.attachmentGuid).subscribe(() => {
            if (this.onAttachmentDeleted) {
                this.onAttachmentDeleted.emit(attachment);
            }
            // remove from array
            const index = this.value.indexOf(attachment, 0);
            if (index > -1) {
                this.value.splice(index, 1);
                this.setValue(this.value);
            }
        });
        this.subs.add(deleteSubs);
    }
    delete(attachment) {
        if (this.showConfirmDialog) {
            const deleteSub = this.showConfirmDialog.subscribe((value) => {
                if (value) {
                    this.deleteAttachment(attachment);
                }
            });
            this.subs.add(deleteSub);
        }
        else {
            this.deleteAttachment(attachment);
        }
    }
    view(attachment) {
        this.setAttachmentGuid(attachment.attachmentGuid);
    }
    edit(attachment) {
        this.extraInfoForm.patchValue({
            s3attachment: attachment,
            filename: attachment.fileName,
            description: attachment.description,
            type: attachment.typeId,
        });
        this.showExtraInfoForm = true;
    }
    handleUploadError() {
        this.fileInputField.nativeElement.value = '';
        this.extraInfoForm.reset();
    }
    setAttachmentGuid(attachmentGuid) {
        if (attachmentGuid == this.activeAttachment) {
            this.previewData = null;
            this.activeAttachment = null;
            return;
        }
        this.activeAttachment = attachmentGuid;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3UploadComponent, deps: [{ token: AnbFormService }, { token: i1$2.HttpClient }, { token: i2$2.UntypedFormBuilder }, { token: S3UploadService }, { token: FileService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbS3UploadComponent, selector: "anb-s3-upload", inputs: { acceptedFileTypes: "acceptedFileTypes", maxFileSizeMB: "maxFileSizeMB", hideType: "hideType", hideDescription: "hideDescription", hideCreationDate: "hideCreationDate", hideEditButton: "hideEditButton", hideSingleFileDeleteButton: "hideSingleFileDeleteButton", disableExtraInfoForm: "disableExtraInfoForm", isTypeRequired: "isTypeRequired", useIconButtons: "useIconButtons", attachmentType: "attachmentType", attachmentTypeId: "attachmentTypeId", hideDatatable: "hideDatatable", typeDropdownList: "typeDropdownList", singleFile: "singleFile", seperateViewLink: "seperateViewLink", alwaysDisplayLabel: "alwaysDisplayLabel", hasPreview: "hasPreview", descriptionLabel: "descriptionLabel", typeLabel: "typeLabel", filenameLabel: "filenameLabel", creationDateLabel: "creationDateLabel", saveButtonLabel: "saveButtonLabel", cancelButtonLabel: "cancelButtonLabel", addAttachmentLabel: "addAttachmentLabel", noPreviewAvailable: "noPreviewAvailable", showConfirmDialog: "showConfirmDialog" }, outputs: { onAttachmentAdding: "onAttachmentAdding", onAttachmentAdded: "onAttachmentAdded", onAttachmentDeleted: "onAttachmentDeleted" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbS3UploadComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbS3UploadComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "fileInputField", first: true, predicate: ["file"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<table class=\"data-table data-table-s3upload vl-u-spacer--small\" *ngIf=\"!hideDatatable && value && value.length > 0\">\n  <colgroup>\n    <col class=\"twenty\" *ngIf=\"!hideDescription\" />\n    <col class=\"four\" *ngIf=\"!hideType\" />\n    <col class=\"twenty\" />\n    <col class=\"six\" *ngIf=\"!hideCreationDate\" />\n    <col class=\"ten\" *ngIf=\"seperateViewLink\" />\n    <col class=\"five\" *ngIf=\"!hideEditButton && !disabled\" />\n    <col class=\"five\" />\n  </colgroup>\n  <thead>\n    <tr class=\"data-table__header\">\n      <th class=\"data-table__header-title\">{{ filenameLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideType\">{{ typeLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideDescription\">{{ descriptionLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideCreationDate\">{{ creationDateLabel }}</th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"seperateViewLink\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"!hideEditButton && !disabled\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"hasPreview\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\"></th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr *ngFor=\"let attachment of value\">\n      <td class=\"ellipsis_cell\">\n        <div *ngIf=\"!seperateViewLink\">\n          <span\n            ><a href=\"javascript:void(0)\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">{{\n              attachment.fileName\n            }}</a></span\n          >\n        </div>\n        <div *ngIf=\"seperateViewLink\">\n          {{ attachment.fileName }}\n        </div>\n      </td>\n      <td *ngIf=\"!hideType\">{{ attachment.type }}</td>\n      <td *ngIf=\"!hideDescription\">{{ attachment.description }}</td>\n      <td *ngIf=\"!hideCreationDate\">{{ attachment.creationDate | date: 'dd/MM/yyyy' }}</td>\n      <td *ngIf=\"seperateViewLink\">\n        <div>\n          <span><a href=\"javascript:void(0)\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">Bekijk</a></span>\n        </div>\n      </td>\n      <td *ngIf=\"!hideEditButton && !disabled\" [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"edit(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-edit\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!attachment.isReadOnly\">\n            <p class=\"vl-icon-wrapper\">\n              <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"edit(attachment)\"\n                ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n                ><span id=\"txtEdit\">Bewerk</span></a\n              >\n            </p>\n          </ng-container>\n        </div>\n      </td>\n      <td *ngIf=\"hasPreview\" [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"view(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-view-add\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !useIconButtons && !attachment.isReadOnly\">\n            <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"view(attachment)\"\n              ><span\n                class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-view-add\"\n                [class.view-open]=\"attachment.attachmentGuid == activeAttachment\"\n                aria-hidden=\"true\"\n              ></span\n              ><span id=\"txtVerwijder\">Bekijk</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n      <td [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"delete(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-bin\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !useIconButtons && !attachment.isReadOnly\">\n            <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"delete(attachment)\"\n              ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n              ><span id=\"txtVerwijder\">Verwijder</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n    </tr>\n  </tbody>\n</table>\n\n<div *ngIf=\"singleFile && value && value.length == 1\">\n  <label *ngIf=\"label && alwaysDisplayLabel\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <div class=\"vl-action-group vl-action-group--space-between no-flex-wrap\">\n    <span class=\"cut\"\n      ><a href=\"javascript:void(0)\" (click)=\"download(value[0].attachmentGuid, value[0].fileName)\" [title]=\"value[0].fileName\">{{\n        value[0].fileName\n      }}</a></span\n    >\n    <div class=\"actions\">\n      <ng-container *ngIf=\"hasPreview && !disabled && !value[0].isReadOnly\">\n        <div class=\"single-file-viewButton\">\n          <a title=\"bekijk\" href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"view(value[0])\"\n            ><span\n              class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-view-add\"\n              [class.view-open]=\"value[0].attachmentGuid == activeAttachment\"\n              aria-hidden=\"true\"\n            ></span\n          ></a>\n        </div>\n      </ng-container>\n      <ng-container *ngIf=\"!hideSingleFileDeleteButton && !disabled && !value[0].isReadOnly\">\n        <div class=\"single-file-deleteButton\">\n          <a title=\"Verwijder\" href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"delete(value[0])\"\n            ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n          ></a>\n        </div>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!(singleFile && value && value.length == 1)\" class=\"anb-input js js-upload js-upload-bound\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n  <div>\n    <div *ngIf=\"!disabled\" class=\"upload__element\">\n      <input\n        #file\n        [attr.id]=\"id ? id : 'file_' + formControlName\"\n        type=\"file\"\n        (change)=\"onFileChange($event)\"\n        placeholder=\"Upload bestand\"\n        accept=\"\"\n        [disabled]=\"disabled || readOnly\"\n        style=\"display: none\"\n      />\n      <label class=\"upload__element__label\" [attr.for]=\"id ? id : 'file_' + formControlName\">\n        <i class=\"vi vi-paperclip\"></i>\n        <span> {{ addAttachmentLabel }}</span>\n        <small>{{ annotation }}</small>\n      </label>\n    </div>\n  </div>\n  <div *ngIf=\"fileError !== null\" class=\"webuniversum form__error\">\n    {{ fileError }}\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n\n<div class=\"overlay\" *ngIf=\"showExtraInfoForm\">\n  <div class=\"modal-dialog\" tabindex=\"-1\" role=\"document\" aria-hidden=\"true\">\n    <div class=\"modal-dialog__content\">\n      <section [formGroup]=\"extraInfoForm\">\n        <div class=\"vl-form__group\">\n          <div class=\"form__row\">\n            <anb-input-text [form]=\"extraInfoForm\" formControlName=\"filename\" [label]=\"filenameLabel\" [ase]=\"aseEnum.ReadOnly\">\n            </anb-input-text>\n          </div>\n          <div class=\"form__row\">\n            <anb-input-text\n              [form]=\"extraInfoForm\"\n              formControlName=\"description\"\n              [label]=\"descriptionLabel\"\n              [ase]=\"aseEnum.EditInsert\"\n              maxlength=\"200\"\n            >\n            </anb-input-text>\n          </div>\n          <div class=\"form__row\" *ngIf=\"!hideType\">\n            <anb-dropdown\n              [form]=\"extraInfoForm\"\n              formControlName=\"type\"\n              [label]=\"typeLabel\"\n              [values]=\"typeDropdownList\"\n              [ase]=\"aseEnum.EditInsert\"\n            >\n            </anb-dropdown>\n          </div>\n        </div>\n      </section>\n    </div>\n    <div class=\"vl-action-group vl-action-group--align-right\" style=\"margin-top: 10px\">\n      <button type=\"button\" class=\"vl-button vl-button--secondary\" (click)=\"onCancelClick()\">{{ cancelButtonLabel }}</button>\n      <button type=\"button\" class=\"vl-button\" (click)=\"onSaveClick()\">{{ saveButtonLabel }}</button>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"hasPreview\">\n  <anb-file-previewer [attachmentId]=\"activeAttachment\" [noPreviewAvailable]=\"noPreviewAvailable\"></anb-file-previewer>\n</div>\n", styles: ["table{width:100%;table-layout:fixed}.four{width:4%}.five{width:5%}.six{width:6%}.ten{width:10%}.twenty{width:20%}table.data-table-s3upload td{overflow-wrap:anywhere}.ellipsis_cell>div{position:relative;overflow:hidden;height:2rem}.ellipsis_cell>div>span{display:block;position:absolute;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:2rem}.ellipsis_cell>div:after{content:attr(title);overflow:hidden;height:0;display:block}.button-td{padding:0}.button-td .vl-button{margin-top:5px}.single-file-deleteButton,.single-file-viewButton{margin-left:2rem;display:inline-block}.single-file-deleteButton a,.single-file-viewButton a{vertical-align:middle}.cut{overflow:hidden;display:inline-block;text-overflow:ellipsis;white-space:nowrap;max-width:100%;margin-bottom:-4px}.no-flex-wrap{flex-wrap:nowrap}.vl-vi-view-add.view-open:after{content:\"/\";position:absolute;top:-3px;left:44%;transform:translate(-50%);font-weight:700;text-shadow:1px 0px 0px white}.data-table th{width:100%}.data-table th.action{width:50%}.data-table th.action.action-icon{width:20%}@media only screen and (max-width: 1200px){#txtVerwijder,#txtEdit{display:none}.data-table th.action{width:30%}}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: AnbDropdownComponent, selector: "anb-dropdown", inputs: ["values", "value", "dropdownID", "placeholder"] }, { kind: "component", type: AnbInputTextComponent, selector: "anb-input-text", inputs: ["placeholder", "maxlength", "inputType", "rawValue", "autocomplete", "mask", "predefinedMask"] }, { kind: "component", type: AnbFilePreviewerComponent, selector: "anb-file-previewer", inputs: ["attachmentId", "noPreviewAvailable"] }, { kind: "pipe", type: i2.DatePipe, name: "date" }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3UploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-s3-upload', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbS3UploadComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbS3UploadComponent),
                            multi: true,
                        },
                    ], template: "<table class=\"data-table data-table-s3upload vl-u-spacer--small\" *ngIf=\"!hideDatatable && value && value.length > 0\">\n  <colgroup>\n    <col class=\"twenty\" *ngIf=\"!hideDescription\" />\n    <col class=\"four\" *ngIf=\"!hideType\" />\n    <col class=\"twenty\" />\n    <col class=\"six\" *ngIf=\"!hideCreationDate\" />\n    <col class=\"ten\" *ngIf=\"seperateViewLink\" />\n    <col class=\"five\" *ngIf=\"!hideEditButton && !disabled\" />\n    <col class=\"five\" />\n  </colgroup>\n  <thead>\n    <tr class=\"data-table__header\">\n      <th class=\"data-table__header-title\">{{ filenameLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideType\">{{ typeLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideDescription\">{{ descriptionLabel }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!hideCreationDate\">{{ creationDateLabel }}</th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"seperateViewLink\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"!hideEditButton && !disabled\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\" *ngIf=\"hasPreview\"></th>\n      <th class=\"data-table__header-title action\" [class.action-icon]=\"useIconButtons\"></th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr *ngFor=\"let attachment of value\">\n      <td class=\"ellipsis_cell\">\n        <div *ngIf=\"!seperateViewLink\">\n          <span\n            ><a href=\"javascript:void(0)\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">{{\n              attachment.fileName\n            }}</a></span\n          >\n        </div>\n        <div *ngIf=\"seperateViewLink\">\n          {{ attachment.fileName }}\n        </div>\n      </td>\n      <td *ngIf=\"!hideType\">{{ attachment.type }}</td>\n      <td *ngIf=\"!hideDescription\">{{ attachment.description }}</td>\n      <td *ngIf=\"!hideCreationDate\">{{ attachment.creationDate | date: 'dd/MM/yyyy' }}</td>\n      <td *ngIf=\"seperateViewLink\">\n        <div>\n          <span><a href=\"javascript:void(0)\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">Bekijk</a></span>\n        </div>\n      </td>\n      <td *ngIf=\"!hideEditButton && !disabled\" [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"edit(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-edit\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!attachment.isReadOnly\">\n            <p class=\"vl-icon-wrapper\">\n              <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"edit(attachment)\"\n                ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n                ><span id=\"txtEdit\">Bewerk</span></a\n              >\n            </p>\n          </ng-container>\n        </div>\n      </td>\n      <td *ngIf=\"hasPreview\" [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"view(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-view-add\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !useIconButtons && !attachment.isReadOnly\">\n            <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"view(attachment)\"\n              ><span\n                class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-view-add\"\n                [class.view-open]=\"attachment.attachmentGuid == activeAttachment\"\n                aria-hidden=\"true\"\n              ></span\n              ><span id=\"txtVerwijder\">Bekijk</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n      <td [class.button-td]=\"useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"delete(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-bin\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !useIconButtons && !attachment.isReadOnly\">\n            <a href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"delete(attachment)\"\n              ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n              ><span id=\"txtVerwijder\">Verwijder</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n    </tr>\n  </tbody>\n</table>\n\n<div *ngIf=\"singleFile && value && value.length == 1\">\n  <label *ngIf=\"label && alwaysDisplayLabel\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <div class=\"vl-action-group vl-action-group--space-between no-flex-wrap\">\n    <span class=\"cut\"\n      ><a href=\"javascript:void(0)\" (click)=\"download(value[0].attachmentGuid, value[0].fileName)\" [title]=\"value[0].fileName\">{{\n        value[0].fileName\n      }}</a></span\n    >\n    <div class=\"actions\">\n      <ng-container *ngIf=\"hasPreview && !disabled && !value[0].isReadOnly\">\n        <div class=\"single-file-viewButton\">\n          <a title=\"bekijk\" href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"view(value[0])\"\n            ><span\n              class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-view-add\"\n              [class.view-open]=\"value[0].attachmentGuid == activeAttachment\"\n              aria-hidden=\"true\"\n            ></span\n          ></a>\n        </div>\n      </ng-container>\n      <ng-container *ngIf=\"!hideSingleFileDeleteButton && !disabled && !value[0].isReadOnly\">\n        <div class=\"single-file-deleteButton\">\n          <a title=\"Verwijder\" href=\"javascript:void(0)\" class=\"vl-link\" (click)=\"delete(value[0])\"\n            ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n          ></a>\n        </div>\n      </ng-container>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"!(singleFile && value && value.length == 1)\" class=\"anb-input js js-upload js-upload-bound\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n  <div>\n    <div *ngIf=\"!disabled\" class=\"upload__element\">\n      <input\n        #file\n        [attr.id]=\"id ? id : 'file_' + formControlName\"\n        type=\"file\"\n        (change)=\"onFileChange($event)\"\n        placeholder=\"Upload bestand\"\n        accept=\"\"\n        [disabled]=\"disabled || readOnly\"\n        style=\"display: none\"\n      />\n      <label class=\"upload__element__label\" [attr.for]=\"id ? id : 'file_' + formControlName\">\n        <i class=\"vi vi-paperclip\"></i>\n        <span> {{ addAttachmentLabel }}</span>\n        <small>{{ annotation }}</small>\n      </label>\n    </div>\n  </div>\n  <div *ngIf=\"fileError !== null\" class=\"webuniversum form__error\">\n    {{ fileError }}\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n\n<div class=\"overlay\" *ngIf=\"showExtraInfoForm\">\n  <div class=\"modal-dialog\" tabindex=\"-1\" role=\"document\" aria-hidden=\"true\">\n    <div class=\"modal-dialog__content\">\n      <section [formGroup]=\"extraInfoForm\">\n        <div class=\"vl-form__group\">\n          <div class=\"form__row\">\n            <anb-input-text [form]=\"extraInfoForm\" formControlName=\"filename\" [label]=\"filenameLabel\" [ase]=\"aseEnum.ReadOnly\">\n            </anb-input-text>\n          </div>\n          <div class=\"form__row\">\n            <anb-input-text\n              [form]=\"extraInfoForm\"\n              formControlName=\"description\"\n              [label]=\"descriptionLabel\"\n              [ase]=\"aseEnum.EditInsert\"\n              maxlength=\"200\"\n            >\n            </anb-input-text>\n          </div>\n          <div class=\"form__row\" *ngIf=\"!hideType\">\n            <anb-dropdown\n              [form]=\"extraInfoForm\"\n              formControlName=\"type\"\n              [label]=\"typeLabel\"\n              [values]=\"typeDropdownList\"\n              [ase]=\"aseEnum.EditInsert\"\n            >\n            </anb-dropdown>\n          </div>\n        </div>\n      </section>\n    </div>\n    <div class=\"vl-action-group vl-action-group--align-right\" style=\"margin-top: 10px\">\n      <button type=\"button\" class=\"vl-button vl-button--secondary\" (click)=\"onCancelClick()\">{{ cancelButtonLabel }}</button>\n      <button type=\"button\" class=\"vl-button\" (click)=\"onSaveClick()\">{{ saveButtonLabel }}</button>\n    </div>\n  </div>\n</div>\n\n<div *ngIf=\"hasPreview\">\n  <anb-file-previewer [attachmentId]=\"activeAttachment\" [noPreviewAvailable]=\"noPreviewAvailable\"></anb-file-previewer>\n</div>\n", styles: ["table{width:100%;table-layout:fixed}.four{width:4%}.five{width:5%}.six{width:6%}.ten{width:10%}.twenty{width:20%}table.data-table-s3upload td{overflow-wrap:anywhere}.ellipsis_cell>div{position:relative;overflow:hidden;height:2rem}.ellipsis_cell>div>span{display:block;position:absolute;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:2rem}.ellipsis_cell>div:after{content:attr(title);overflow:hidden;height:0;display:block}.button-td{padding:0}.button-td .vl-button{margin-top:5px}.single-file-deleteButton,.single-file-viewButton{margin-left:2rem;display:inline-block}.single-file-deleteButton a,.single-file-viewButton a{vertical-align:middle}.cut{overflow:hidden;display:inline-block;text-overflow:ellipsis;white-space:nowrap;max-width:100%;margin-bottom:-4px}.no-flex-wrap{flex-wrap:nowrap}.vl-vi-view-add.view-open:after{content:\"/\";position:absolute;top:-3px;left:44%;transform:translate(-50%);font-weight:700;text-shadow:1px 0px 0px white}.data-table th{width:100%}.data-table th.action{width:50%}.data-table th.action.action-icon{width:20%}@media only screen and (max-width: 1200px){#txtVerwijder,#txtEdit{display:none}.data-table th.action{width:30%}}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i1$2.HttpClient }, { type: i2$2.UntypedFormBuilder }, { type: S3UploadService }, { type: FileService }]; }, propDecorators: { acceptedFileTypes: [{
                type: Input
            }], maxFileSizeMB: [{
                type: Input
            }], hideType: [{
                type: Input
            }], hideDescription: [{
                type: Input
            }], hideCreationDate: [{
                type: Input
            }], hideEditButton: [{
                type: Input
            }], hideSingleFileDeleteButton: [{
                type: Input
            }], disableExtraInfoForm: [{
                type: Input
            }], isTypeRequired: [{
                type: Input
            }], useIconButtons: [{
                type: Input
            }], attachmentType: [{
                type: Input
            }], attachmentTypeId: [{
                type: Input
            }], hideDatatable: [{
                type: Input
            }], typeDropdownList: [{
                type: Input
            }], singleFile: [{
                type: Input
            }], seperateViewLink: [{
                type: Input
            }], alwaysDisplayLabel: [{
                type: Input
            }], hasPreview: [{
                type: Input
            }], descriptionLabel: [{
                type: Input
            }], typeLabel: [{
                type: Input
            }], filenameLabel: [{
                type: Input
            }], creationDateLabel: [{
                type: Input
            }], saveButtonLabel: [{
                type: Input
            }], cancelButtonLabel: [{
                type: Input
            }], addAttachmentLabel: [{
                type: Input
            }], noPreviewAvailable: [{
                type: Input
            }], showConfirmDialog: [{
                type: Input
            }], fileInputField: [{
                type: ViewChild,
                args: ['file']
            }], onAttachmentAdding: [{
                type: Output
            }], onAttachmentAdded: [{
                type: Output
            }], onAttachmentDeleted: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbTabDeprecatedComponent {
    constructor() {
        this.active = false;
        this.canSelect = true;
        this.order = 0;
        // eslint-disable-next-line @angular-eslint/no-output-on-prefix
        this.onSelectDenied = new EventEmitter();
    }
    ngOnInit() {
        if (!this.id) {
            this.id = 'tab' + this.title.replace(' ', '');
        }
        if (!this.tabHeaderControlId) {
            this.tabHeaderControlId = 'tabHeader' + this.title.replace(' ', '');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabDeprecatedComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTabDeprecatedComponent, selector: "anb-tab-deprecated", inputs: { title: ["tabTitle", "title"], active: "active", id: "id", canSelect: "canSelect", selectDeniedText: "selectDeniedText", canDeactivate: "canDeactivate", route: "route", order: "order" }, outputs: { onSelectDenied: "onSelectDenied", tabHeaderControlId: "tabHeaderControlId" }, ngImport: i0, template: "<div\n  *ngIf=\"active && !route\"\n  class=\"pane\"\n  role=\"tabpanel\"\n  [attr.id]=\"id\"\n  [attr.aria-labelledby]=\"tabHeaderControlId\"\n  [attr.aria-hidden]=\"!active\"\n>\n  <ng-content></ng-content>\n</div>\n", styles: [".pane{padding:1em}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabDeprecatedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-tab-deprecated', template: "<div\n  *ngIf=\"active && !route\"\n  class=\"pane\"\n  role=\"tabpanel\"\n  [attr.id]=\"id\"\n  [attr.aria-labelledby]=\"tabHeaderControlId\"\n  [attr.aria-hidden]=\"!active\"\n>\n  <ng-content></ng-content>\n</div>\n", styles: [".pane{padding:1em}\n"] }]
        }], propDecorators: { title: [{
                type: Input,
                args: ['tabTitle']
            }], active: [{
                type: Input
            }], id: [{
                type: Input
            }], canSelect: [{
                type: Input
            }], selectDeniedText: [{
                type: Input
            }], canDeactivate: [{
                type: Input
            }], route: [{
                type: Input
            }], order: [{
                type: Input
            }], onSelectDenied: [{
                type: Output
            }], tabHeaderControlId: [{
                type: Output
            }] } });

/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbTabsDeprecatedComponent {
    constructor(router, route) {
        this.router = router;
        this.route = route;
        this.onSelectStart = new EventEmitter();
        this.onSelect = new EventEmitter();
        this.isCurrentTabFirst = true;
        this.isCurrentTabLast = false;
        this.subs = new Subscription();
        const sub = router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.activateStep();
        });
        this.subs.add(sub);
    }
    // contentChildren are set
    ngAfterContentInit() {
        let tabs = this.tabs.toArray();
        tabs = tabs.sort((a, b) => {
            if (a.order < b.order) {
                return -1;
            }
            if (a.order > b.order) {
                return 1;
            }
            // a must be equal to b
            return 0;
        });
        this.tabs.reset(tabs);
        // Subscribe for changes on tabs. This will be triggerd when tabs are added or hidden with ngIf
        this.tabs.changes.subscribe(() => {
            this.initTabs();
        });
        this.initTabs();
    }
    ngOnDestroy() {
        this.subs.unsubscribe();
    }
    selectTab(tab) {
        if (this.onSelectStart) {
            this.onSelectStart.emit(tab);
        }
        if (!tab.canSelect) {
            tab.onSelectDenied.emit(tab);
            return;
        }
        const tabRoute = tab.route ? tab.route : this.defaultRoute;
        if (tabRoute) {
            this.router.navigate(tabRoute).then((value) => {
                if (value || value === null) {
                    this.setSelectedTab(tab);
                }
            });
        }
        else {
            this.setSelectedTab(tab);
        }
    }
    setSelectedTab(tab) {
        if (this.currentTab && this.currentTab.canDeactivate) {
            const canDeactivate = this.currentTab.canDeactivate;
            canDeactivate.then((data) => {
                if (data) {
                    this.tabSelect(tab);
                }
            });
        }
        else {
            this.tabSelect(tab);
        }
    }
    initTabs() {
        // get all active tabs
        const activeTabs = this.tabs.filter((tab) => tab.active);
        // if there is no active tab set, activate the first
        if (activeTabs.length === 0 && this.tabs.length) {
            this.selectTab(this.tabs.first);
        }
        this.activateStep();
    }
    tabSelect(tab) {
        // deactivate all tabs
        const tabArr = this.tabs.toArray();
        tabArr.forEach((t) => (t.active = false));
        // activate the tab the user has clicked on.
        tab.active = true;
        this.currentTab = tab;
        this.checkTabStatus();
    }
    activateStep() {
        if (this.tabs) {
            this.tabs.forEach((tab) => {
                if (tab.route) {
                    const currentUrlTree = this.router.parseUrl(this.router.url);
                    const stepUrlTree = this.router.createUrlTree(tab.route);
                    const currentUrl = this.getPathFromUrl(this.router.serializeUrl(currentUrlTree));
                    const stepUrl = this.router.serializeUrl(stepUrlTree);
                    if (currentUrl === stepUrl) {
                        tab.active = true;
                    }
                    else {
                        tab.active = false;
                    }
                }
            });
        }
    }
    getPathFromUrl(url) {
        return url.split(/[?#]/)[0];
    }
    checkCurrentTabFirst() {
        if (this.tabs) {
            const index = this.tabs.toArray().indexOf(this.currentTab);
            if (this.currentTab && index === 0) {
                this.isCurrentTabFirst = true;
                return;
            }
        }
        this.isCurrentTabFirst = false;
    }
    checkCurrentTabLast() {
        if (this.tabs) {
            const index = this.tabs.toArray().indexOf(this.currentTab);
            if ((this.currentTab && index + 2 === this.tabs.length) || (this.currentTab && index + 1 === this.tabs.length)) {
                this.isCurrentTabLast = true;
                return;
            }
        }
        this.isCurrentTabLast = false;
    }
    checkTabStatus() {
        this.checkCurrentTabFirst();
        this.checkCurrentTabLast();
        this.onSelect.emit({
            isCurrentTabFirst: this.isCurrentTabFirst,
            isCurrentTabLast: this.isCurrentTabLast,
        });
    }
    volgendeTab() {
        const index = this.tabs.toArray().indexOf(this.currentTab);
        const nextTab = this.tabs.toArray()[index + 1];
        this.selectTab(nextTab);
        this.checkTabStatus();
    }
    vorigeTab() {
        const index = this.tabs.toArray().indexOf(this.currentTab);
        const previousTab = this.tabs.toArray()[index - 1];
        this.selectTab(previousTab);
        this.checkTabStatus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabsDeprecatedComponent, deps: [{ token: i1$3.Router }, { token: i1$3.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTabsDeprecatedComponent, selector: "anb-tabs-deprecated", inputs: { defaultRoute: "defaultRoute" }, outputs: { onSelectStart: "onSelectStart", onSelect: "onSelect" }, queries: [{ propertyName: "tabs", predicate: AnbTabDeprecatedComponent }], ngImport: i0, template: "<div class=\"webuniversum tabs__wrapper\">\n  <ul class=\"webuniversum tabs\" role=\"tablist\">\n    <!--class=\"nav nav-tabs\">-->\n    <li\n      *ngFor=\"let tab of tabs\"\n      (click)=\"selectTab(tab)\"\n      class=\"tab\"\n      [class.tab--active]=\"tab.active\"\n      attr.role=\"tab\"\n      [attr.aria-controls]=\"tab.id\"\n      [attr.id]=\"tab.tabHeaderControlId\"\n      [attr.aria-selected]=\"tab.active\"\n    >\n      <!--[class.active]=\"tab.active\">-->\n      <a href=\"javascript:void(0)\" class=\"tab__link\" [attr.aria-expanded]=\"tab.active\">{{ tab.title }}</a>\n\n      <!-- [aria-expanded]=\"tab.active\" -->\n    </li>\n  </ul>\n  <ng-content></ng-content>\n  <div [class.pane]=\"tabOutlet.isActivated\">\n    <router-outlet name=\"tab\" #tabOutlet=\"outlet\"></router-outlet>\n  </div>\n</div>\n", styles: [".pane{padding:1em}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$3.RouterOutlet, selector: "router-outlet", inputs: ["name"], outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabsDeprecatedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-tabs-deprecated', template: "<div class=\"webuniversum tabs__wrapper\">\n  <ul class=\"webuniversum tabs\" role=\"tablist\">\n    <!--class=\"nav nav-tabs\">-->\n    <li\n      *ngFor=\"let tab of tabs\"\n      (click)=\"selectTab(tab)\"\n      class=\"tab\"\n      [class.tab--active]=\"tab.active\"\n      attr.role=\"tab\"\n      [attr.aria-controls]=\"tab.id\"\n      [attr.id]=\"tab.tabHeaderControlId\"\n      [attr.aria-selected]=\"tab.active\"\n    >\n      <!--[class.active]=\"tab.active\">-->\n      <a href=\"javascript:void(0)\" class=\"tab__link\" [attr.aria-expanded]=\"tab.active\">{{ tab.title }}</a>\n\n      <!-- [aria-expanded]=\"tab.active\" -->\n    </li>\n  </ul>\n  <ng-content></ng-content>\n  <div [class.pane]=\"tabOutlet.isActivated\">\n    <router-outlet name=\"tab\" #tabOutlet=\"outlet\"></router-outlet>\n  </div>\n</div>\n", styles: [".pane{padding:1em}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.Router }, { type: i1$3.ActivatedRoute }]; }, propDecorators: { defaultRoute: [{
                type: Input
            }], onSelectStart: [{
                type: Output
            }], onSelect: [{
                type: Output
            }], tabs: [{
                type: ContentChildren,
                args: [AnbTabDeprecatedComponent]
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbTextareaComponent extends BaseFormElementComponent {
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (this.value !== value) {
            this.value = value === null ? '' : value; // When binding to the value of a textarea and the value is null, otherwise in IE and Edge the textarea will display 'null'.
            setTimeout(() => {
                const innerTextArea = this.element.nativeElement.querySelector('textarea');
                this.resize({ target: innerTextArea });
            });
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(element, formService) {
        super(formService); // Constructors for derived classes must contain a 'super' call.
        this.element = element;
        this.formService = formService;
        this.autoResize = true;
        this.showRemainingCharacters = false;
        this.maxScrollHeight = 250;
        this.value = '';
        this.minScrollHeight = 50;
        this.remainingCharacters = 0;
    }
    ngOnInit() {
        super.ngOnInit();
    }
    onChange(event) {
        this.resize(event);
        this.value = event.target.value;
        // update form
        this.propagateChange(this.value);
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
    }
    resize(event) {
        if (!this.autoResize)
            return;
        event.target.style.height = 'auto';
        if (event.target.scrollHeight > this.minScrollHeight && event.target.scrollHeight < this.maxScrollHeight) {
            event.target.style.height = event.target.scrollHeight + 'px';
            this.lastScrollHeight = event.target.scrollHeight;
        }
        if (event.target.scrollHeight >= this.maxScrollHeight) {
            event.target.style.height = this.maxScrollHeight + 'px';
        }
    }
    clear() {
        this.value = '';
        this.propagateChange('');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTextareaComponent, deps: [{ token: i0.ElementRef }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTextareaComponent, selector: "anb-textarea", inputs: { cols: "cols", rows: "rows", placeholder: "placeholder", maxlength: "maxlength", autoResize: "autoResize", showRemainingCharacters: "showRemainingCharacters", maxScrollHeight: "maxScrollHeight" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbTextareaComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbTextareaComponent),
                multi: true,
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<label class=\"anb-input webuniversum form__label\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <textarea\n    class=\"webuniversum textarea textarea-block\"\n    [class.textarea--error]=\"showError() && !disabled\"\n    [class.textarea--disabled]=\"disabled\"\n    type=\"text\"\n    placeholder=\"\"\n    [attr.cols]=\"cols\"\n    [attr.rows]=\"rows\"\n    [disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxlength\"\n    [value]=\"value\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n  ></textarea>\n\n  <div class=\"flex-group\" [ngClass]=\"{ 'flex-group--space-between': showError(), 'flex-group--align-right': !showError() }\">\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n      {{ getError() }}\n    </div>\n    <ng-container *ngIf=\"showRemainingCharacters && maxlength\">\n      <div class=\"vl-u-text--small\">{{ value?.length }}/{{ maxlength }}</div>\n    </ng-container>\n  </div>\n</label>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,textarea{width:100%}textarea{display:block}.flex-group{display:flex;flex-wrap:wrap;align-items:center}.flex-group--align-right{justify-content:flex-end}.flex-group--space-between{justify-content:space-between}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTextareaComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-textarea', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbTextareaComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbTextareaComponent),
                            multi: true,
                        },
                    ], template: "<label class=\"anb-input webuniversum form__label\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <textarea\n    class=\"webuniversum textarea textarea-block\"\n    [class.textarea--error]=\"showError() && !disabled\"\n    [class.textarea--disabled]=\"disabled\"\n    type=\"text\"\n    placeholder=\"\"\n    [attr.cols]=\"cols\"\n    [attr.rows]=\"rows\"\n    [disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [attr.placeholder]=\"placeholder\"\n    [attr.maxlength]=\"maxlength\"\n    [value]=\"value\"\n    (change)=\"onChange($event)\"\n    (keyup)=\"onChange($event)\"\n  ></textarea>\n\n  <div class=\"flex-group\" [ngClass]=\"{ 'flex-group--space-between': showError(), 'flex-group--align-right': !showError() }\">\n    <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n      {{ getError() }}\n    </div>\n    <ng-container *ngIf=\"showRemainingCharacters && maxlength\">\n      <div class=\"vl-u-text--small\">{{ value?.length }}/{{ maxlength }}</div>\n    </ng-container>\n  </div>\n</label>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,textarea{width:100%}textarea{display:block}.flex-group{display:flex;flex-wrap:wrap;align-items:center}.flex-group--align-right{justify-content:flex-end}.flex-group--space-between{justify-content:space-between}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AnbFormService }]; }, propDecorators: { cols: [{
                type: Input
            }], rows: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], autoResize: [{
                type: Input
            }], showRemainingCharacters: [{
                type: Input
            }], maxScrollHeight: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-this-alias */
/* eslint-disable @typescript-eslint/no-explicit-any */
class FroalaEditorDirective {
    constructor(el, zone) {
        this.el = el;
        this.zone = zone;
        //* Editor options. */
        this.options = {
            immediateAngularModelUpdate: false,
            angularIgnoreAttrs: null,
        };
        this.SPECIAL_TAGS = ['img', 'button', 'input', 'a'];
        this.INNER_HTML_ATTR = 'innerHTML';
        this.hasSpecialTag = false;
        this.listeningEvents = [];
        this.editorInitialized = false;
        this.oldModel = null;
        // Begin ControlValueAccesor methods.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.onChange = (_) => { };
        this.onTouched = () => { };
        // froalaModel directive as output: update model if editor contentChanged
        this.froalaModelChange = new EventEmitter();
        // froalaInit directive as output: send manual editor initialization
        this.froalaInit = new EventEmitter();
        const element = el.nativeElement;
        // check if the element is a special tag
        if (this.SPECIAL_TAGS.indexOf(element.tagName.toLowerCase()) !== -1) {
            this.hasSpecialTag = true;
        }
        // jquery wrap and store element
        this.$element = $(element);
        this.zone = zone;
    }
    // Form model content changed.
    writeValue(content) {
        this.updateEditor(content);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // End ControlValueAccesor methods.
    // froalaEditor directive as input: store the editor options
    set froalaEditor(opts) {
        this.options = opts || this.options;
    }
    // froalaModel directive as input: store initial editor content
    set froalaModel(content) {
        this.updateEditor(content);
    }
    // Update editor with model contents.
    updateEditor(content) {
        if (JSON.stringify(this.oldModel) === JSON.stringify(content)) {
            return;
        }
        if (!this.hasSpecialTag) {
            this.oldModel = content;
        }
        else {
            this.model = content;
        }
        if (this.editorInitialized) {
            if (!this.hasSpecialTag) {
                this.$element.froalaEditor('html.set', content);
            }
            else {
                this.setContent();
            }
        }
        else {
            if (!this.hasSpecialTag) {
                this.$element.html(content);
            }
            else {
                this.setContent();
            }
        }
    }
    // update model if editor contentChanged
    updateModel() {
        this.zone.run(() => {
            let modelContent = null;
            if (this.hasSpecialTag) {
                const attributeNodes = this.$element[0].attributes;
                const attrs = {};
                for (let i = 0; i < attributeNodes.length; i++) {
                    const attrName = attributeNodes[i].name;
                    if (this.options.angularIgnoreAttrs && this.options.angularIgnoreAttrs.indexOf(attrName) !== -1) {
                        continue;
                    }
                    attrs[attrName] = attributeNodes[i].value;
                }
                if (this.$element[0].innerHTML) {
                    attrs[this.INNER_HTML_ATTR] = this.$element[0].innerHTML;
                }
                modelContent = attrs;
            }
            else {
                const returnedHtml = this.$element.froalaEditor('html.get');
                if (typeof returnedHtml === 'string') {
                    modelContent = returnedHtml;
                }
            }
            if (this.oldModel !== modelContent) {
                this.oldModel = modelContent;
                // Update froalaModel.
                this.froalaModelChange.emit(modelContent);
                // Update form model.
                this.onChange(modelContent);
            }
        });
    }
    // register event on jquery element
    registerEvent(element, eventName, callback) {
        if (!element || !eventName || !callback) {
            return;
        }
        this.listeningEvents.push(eventName);
        element.on(eventName, callback);
    }
    initListeners() {
        const self = this;
        // bind contentChange and keyup event to froalaModel
        this.registerEvent(this.$element, 'froalaEditor.contentChanged', function () {
            setTimeout(function () {
                self.updateModel();
            }, 0);
        });
        this.registerEvent(this.$element, 'froalaEditor.mousedown', function () {
            setTimeout(function () {
                self.onTouched();
            }, 0);
        });
        if (this.options.immediateAngularModelUpdate) {
            this.registerEvent(this.$element, 'keyup', function () {
                setTimeout(function () {
                    self.updateModel();
                }, 0);
            });
        }
    }
    // register events from editor options
    registerFroalaEvents() {
        if (!this.options.events) {
            return;
        }
        for (const eventName in this.options.events) {
            if (this.options.events.hasOwnProperty(eventName)) {
                this.registerEvent(this.$element, eventName, this.options.events[eventName]);
            }
        }
    }
    createEditor() {
        if (this.editorInitialized) {
            return;
        }
        this.setContent(true);
        // Registering events before initializing the editor will bind the initialized event correctly.
        this.registerFroalaEvents();
        this.initListeners();
        // init editor
        this.zone.runOutsideAngular(() => {
            this.$element.on('froalaEditor.initialized', () => {
                this.editorInitialized = true;
            });
            this.editor = this.$element.froalaEditor(this.options).data('froala.editor').$el;
        });
    }
    setHtml() {
        this.$element.froalaEditor('html.set', this.model || '', true);
        // This will reset the undo stack everytime the model changes externally. Can we fix this?
        this.$element.froalaEditor('undo.reset');
        this.$element.froalaEditor('undo.saveStep');
    }
    setContent(firstTime = false) {
        const self = this;
        // Set initial content
        if (this.model || this.model === '') {
            this.oldModel = this.model;
            if (this.hasSpecialTag) {
                const tags = this.model;
                // add tags on element
                if (tags) {
                    for (const attr in tags) {
                        if (tags.hasOwnProperty(attr) && attr !== this.INNER_HTML_ATTR) {
                            this.$element.attr(attr, tags[attr]);
                        }
                    }
                    if (tags.hasOwnProperty(this.INNER_HTML_ATTR)) {
                        this.$element[0].innerHTML = tags[this.INNER_HTML_ATTR];
                    }
                }
            }
            else {
                if (firstTime) {
                    this.registerEvent(this.$element, 'froalaEditor.initialized', function () {
                        self.setHtml();
                    });
                }
                else {
                    self.setHtml();
                }
            }
        }
    }
    destroyEditor() {
        if (this.editorInitialized) {
            this.$element.off(this.listeningEvents.join(' '));
            this.editor.off('keyup');
            this.$element.froalaEditor('destroy');
            this.listeningEvents.length = 0;
            this.editorInitialized = false;
        }
    }
    getEditor() {
        if (this.$element) {
            return this.$element.froalaEditor.bind(this.$element);
        }
        return null;
    }
    // send manual editor initialization
    generateManualController() {
        const controls = {
            initialize: this.createEditor.bind(this),
            destroy: this.destroyEditor.bind(this),
            getEditor: this.getEditor.bind(this),
        };
        this.froalaInit.emit(controls);
    }
    // // TODO not sure if ngOnInit is executed after @inputs
    ngOnInit() {
        // check if output froalaInit is present. Maybe observers is private and should not be used?? TODO how to better test that an output directive is present.
        if (!this.froalaInit.observers.length) {
            this.createEditor();
        }
        else {
            this.generateManualController();
        }
    }
    ngOnDestroy() {
        this.destroyEditor();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FroalaEditorDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: FroalaEditorDirective, selector: "[froalaEditor]", inputs: { froalaEditor: "froalaEditor", froalaModel: "froalaModel" }, outputs: { froalaModelChange: "froalaModelChange", froalaInit: "froalaInit" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FroalaEditorDirective),
                multi: true,
            },
        ], exportAs: ["froalaEditor"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FroalaEditorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[froalaEditor]',
                    exportAs: 'froalaEditor',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FroalaEditorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }]; }, propDecorators: { froalaEditor: [{
                type: Input
            }], froalaModel: [{
                type: Input
            }], froalaModelChange: [{
                type: Output
            }], froalaInit: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbTextEditorComponent extends BaseFormElementComponent {
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // End ControlValueAccesor methods.
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.options = {
            height: 300,
            placeholderText: '',
            quickInsertTags: [],
            charCounterCount: false,
            immediateAngularModelUpdate: true,
            toolbarButtons: ['bold', 'italic', 'underline', 'align', 'formatUL', 'formatOL'],
            events: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                'froalaEditor.focus': (e, editor) => {
                    console.log('focus');
                },
            },
        };
        this.value = '';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.onChange = (_) => { };
        this.onTouched = () => { };
    }
    ngOnInit() {
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTextEditorComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTextEditorComponent, selector: "anb-texteditor", inputs: { width: "width", height: "height", options: "options" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbTextEditorComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbTextEditorComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "froalaEditor", first: true, predicate: ["froalaEditor"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div [class.error]=\"showError() && !disabled\" [id]=\"id\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"!disabled\">\n    <textarea #froalaEditor [froalaEditor]=\"options\" (froalaModelChange)=\"onChange($event)\" [(froalaModel)]=\"value\"></textarea>\n  </div>\n\n  <div\n    *ngIf=\"disabled\"\n    [innerHtml]=\"value | safe: 'html'\"\n    class=\"textarea--disabled vl-typography\"\n    style=\"overflow: scroll; height: 300px; padding: 10px\"\n  ></div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,textarea{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: FroalaEditorDirective, selector: "[froalaEditor]", inputs: ["froalaEditor", "froalaModel"], outputs: ["froalaModelChange", "froalaInit"], exportAs: ["froalaEditor"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTextEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-texteditor', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbTextEditorComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbTextEditorComponent),
                            multi: true,
                        },
                    ], template: "<div [class.error]=\"showError() && !disabled\" [id]=\"id\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"!disabled\">\n    <textarea #froalaEditor [froalaEditor]=\"options\" (froalaModelChange)=\"onChange($event)\" [(froalaModel)]=\"value\"></textarea>\n  </div>\n\n  <div\n    *ngIf=\"disabled\"\n    [innerHtml]=\"value | safe: 'html'\"\n    class=\"textarea--disabled vl-typography\"\n    style=\"overflow: scroll; height: 300px; padding: 10px\"\n  ></div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,textarea{width:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { froalaEditor: [{
                type: ViewChild,
                args: ['froalaEditor']
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], options: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbTitleComponent extends BaseFormElementComponent {
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.value = '';
    }
    ngOnInit() {
        super.ngOnInit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTitleComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTitleComponent, selector: "anb-title", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbTitleComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div>\n  <h2 *ngIf=\"label\" class=\"webuniversum title--alt\" [innerHtml]=\"label | safe: 'html'\"></h2>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <!--<anb-popover [infoText]=\"subLabel\"></anb-popover>-->\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTitleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-title', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbTitleComponent),
                            multi: true,
                        },
                    ], template: "<div>\n  <h2 *ngIf=\"label\" class=\"webuniversum title--alt\" [innerHtml]=\"label | safe: 'html'\"></h2>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  <!--<anb-popover [infoText]=\"subLabel\"></anb-popover>-->\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; } });

/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUploadComponent extends BaseFormElementComponent {
    set acceptedFileTypes(input) {
        if (input)
            this.fileTypes = input;
    }
    // the method set in registerOnChange, it is just
    // a placeholder for a method that takes one parameter,
    // we use it to emit changes back to the form
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
            this.saveAttachments(value);
        }
        else {
            this.value = [];
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(fileService, alertService, formService) {
        super(formService);
        this.fileService = fileService;
        this.alertService = alertService;
        this.formService = formService;
        this.documentTypeID = '';
        this.useAlertService = false;
        this.maxFileSizeMB = 10;
        this.canDownload = false;
        this.canRemove = false;
        // This event gives a filelist which the consumer component should use to upload the file(s)
        this.onFileChange = new EventEmitter();
        this.onError = new EventEmitter();
        this.onUploadFile = new EventEmitter();
        this.onDownloadFile = new EventEmitter();
        this.attachmentList = [];
        this.hasError = false;
        this.isUploading = false;
        this.value = [];
        this.attachmentSetId = 0;
        this.fileTypes = '.pdf, .doc, .docx, .jpeg, .jpg, .png, .bmp, .rtf, .xls, .xlsx, .html, .htm, .eml, .msg, .gpx';
    }
    ngOnInit() {
        super.ngOnInit();
    }
    fileChange(event) {
        const fileList = event.target.files;
        if (this.attachmentList !== undefined && this.attachmentList.length > 0) {
            this.attachmentSetId = this.attachmentList[0].attachmentSetID;
        }
        const fileArray = [];
        if (fileList.length > 0) {
            this.onFileChange.emit(event);
            this.hasError = false;
            if (!this.fileService.verifyFiles(fileList, this.fileTypes)) {
                event.target.value = '';
                return;
            }
            Array.from(fileList).forEach((file) => {
                // Check filesize
                if (file.size / 1024 / 1024 >= this.maxFileSizeMB) {
                    // error aan fc toevoegen
                    this.fc.setErrors({ filesize: true });
                    this.fc.markAsDirty();
                    this.hasError = true;
                    this.getError();
                    return;
                }
                else {
                    fileArray.push(file.name);
                    this.hasError = false;
                }
            });
            if (!this.hasError) {
                this.isUploading = true;
                this.uploadService.uploadFiles(fileList, this.attachmentSetId).subscribe((value) => {
                    this.isUploading = false;
                    value.attachments.forEach((element) => {
                        const attachmentInfo = {};
                        attachmentInfo.attachmentInfoID = element.attachmentInfoID;
                        attachmentInfo.attachmentID = element.attachmentID;
                        attachmentInfo.fileName = element.fileName;
                        attachmentInfo.fileSize = element.fileSize;
                        attachmentInfo.attachmentDocumentTypeID = this.documentTypeID;
                        attachmentInfo.attachmentSetID = element.attachmentSetID; // this.attachmentSetId;
                        attachmentInfo.canBeDeleted = true;
                        this.attachmentList.push(attachmentInfo);
                    });
                });
            }
        }
        event.target.value = '';
    }
    hasValidAttachments() {
        return this.attachmentList.length > 0;
    }
    getAttachments() {
        return this.attachmentList;
    }
    saveAttachments(attachments) {
        const attachmentArr = attachments;
        const newAttachmentList = [];
        for (const attachment of attachmentArr) {
            let attachmentInfo = {};
            attachmentInfo = attachment;
            attachmentInfo.attachmentSetID = attachment.attachmentSetID;
            attachmentInfo.attachmentInfoID = attachment.attachmentInfoID;
            attachmentInfo.attachmentID = attachment.attachmentID;
            attachmentInfo.fileName = attachment.fileName;
            attachmentInfo.fileSize = attachment.fileSize;
            attachmentInfo.attachmentDocumentTypeID = this.documentTypeID;
            attachmentInfo.canBeDeleted = attachment.canBeDeleted;
            newAttachmentList.push(attachmentInfo);
        }
        this.attachmentList = newAttachmentList;
    }
    deleteAttachment(attachmentID) {
        const index = this.attachmentList.findIndex((p) => p.attachmentID === attachmentID);
        if (index > -1) {
            this.attachmentList.splice(index, 1);
        }
        this.writeValue(this.getAttachments());
        this.propagateChange(this.value);
    }
    onAttachmentClick(attachmentID, fileName) {
        this.onDownloadFile.emit({ attachmentID, fileName });
    }
    success(message) {
        if (this.useAlertService) {
            this.alertService.success(message);
        }
    }
    error(message) {
        this.hasError = true;
        this.onError.emit(message);
        if (this.useAlertService) {
            this.alertService.error(message);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUploadComponent, deps: [{ token: FileService }, { token: AlertService }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUploadComponent, selector: "anb-upload", inputs: { acceptedFileTypes: "acceptedFileTypes", documentTypeID: "documentTypeID", useAlertService: "useAlertService", maxFileSizeMB: "maxFileSizeMB", uploadService: "uploadService", canDownload: "canDownload", canRemove: "canRemove" }, outputs: { onFileChange: "onFileChange", onError: "onError", onUploadFile: "onUploadFile", onDownloadFile: "onDownloadFile" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUploadComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUploadComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input upload js js-upload js-upload-bound\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n  <br />\n  <div>\n    <div *ngIf=\"hasValidAttachments()\">\n      <div class=\"upload__files upload__files--has-files\" *ngFor=\"let attachment of attachmentList\">\n        <a\n          *ngIf=\"(attachment.canBeDeleted === true || canRemove === true) && !disabled\"\n          href=\"javascript:void(0);\"\n          class=\"upload__files__close link--icon--close\"\n          (click)=\"deleteAttachment(attachment.attachmentID)\"\n        >\n          <span class=\"u-visually-hidden\">Sluiten</span>\n        </a>\n        <div class=\"upload__files__container\">\n          <div class=\"upload__file\">\n            <ng-container *ngIf=\"canDownload === false || attachment.attachmentInfoID == null || attachment.attachmentInfoID === 0\">\n              <p class=\"upload__file__name\">\n                {{ attachment.fileName }}\n                <span class=\"upload__file__size\"></span>\n              </p>\n            </ng-container>\n            <ng-container *ngIf=\"canDownload && attachment.attachmentInfoID > 0\">\n              <a\n                class=\"upload__file__name\"\n                href=\"javascript:void(0);\"\n                (click)=\"onAttachmentClick(attachment.attachmentID, attachment.fileName)\"\n                >{{ attachment.fileName }}\n                <span class=\"upload__file__size\"></span>\n              </a>\n            </ng-container>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div *ngIf=\"!disabled\" class=\"upload__element\" [class.error]=\"hasError\">\n      <input\n        [attr.id]=\"'file_' + formControlName\"\n        type=\"file\"\n        (change)=\"fileChange($event)\"\n        placeholder=\"Upload bestand\"\n        accept=\"\"\n        [disabled]=\"disabled\"\n        multiple\n        style=\"display: none\"\n      />\n      <label class=\"upload__element__label\" [attr.for]=\"'file_' + formControlName\">\n        <i class=\"vi vi-paperclip\"></i>\n        <span>Bijlage toevoegen</span>\n        <small>{{ annotation }}</small>\n        <div *ngIf=\"isUploading\" class=\"loader\"></div>\n      </label>\n    </div>\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-upload', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUploadComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUploadComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input upload js js-upload js-upload-bound\">\n  <label class=\"webuniversum form__label\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n  </label>\n  <br />\n  <div>\n    <div *ngIf=\"hasValidAttachments()\">\n      <div class=\"upload__files upload__files--has-files\" *ngFor=\"let attachment of attachmentList\">\n        <a\n          *ngIf=\"(attachment.canBeDeleted === true || canRemove === true) && !disabled\"\n          href=\"javascript:void(0);\"\n          class=\"upload__files__close link--icon--close\"\n          (click)=\"deleteAttachment(attachment.attachmentID)\"\n        >\n          <span class=\"u-visually-hidden\">Sluiten</span>\n        </a>\n        <div class=\"upload__files__container\">\n          <div class=\"upload__file\">\n            <ng-container *ngIf=\"canDownload === false || attachment.attachmentInfoID == null || attachment.attachmentInfoID === 0\">\n              <p class=\"upload__file__name\">\n                {{ attachment.fileName }}\n                <span class=\"upload__file__size\"></span>\n              </p>\n            </ng-container>\n            <ng-container *ngIf=\"canDownload && attachment.attachmentInfoID > 0\">\n              <a\n                class=\"upload__file__name\"\n                href=\"javascript:void(0);\"\n                (click)=\"onAttachmentClick(attachment.attachmentID, attachment.fileName)\"\n                >{{ attachment.fileName }}\n                <span class=\"upload__file__size\"></span>\n              </a>\n            </ng-container>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div *ngIf=\"!disabled\" class=\"upload__element\" [class.error]=\"hasError\">\n      <input\n        [attr.id]=\"'file_' + formControlName\"\n        type=\"file\"\n        (change)=\"fileChange($event)\"\n        placeholder=\"Upload bestand\"\n        accept=\"\"\n        [disabled]=\"disabled\"\n        multiple\n        style=\"display: none\"\n      />\n      <label class=\"upload__element__label\" [attr.for]=\"'file_' + formControlName\">\n        <i class=\"vi vi-paperclip\"></i>\n        <span>Bijlage toevoegen</span>\n        <small>{{ annotation }}</small>\n        <div *ngIf=\"isUploading\" class=\"loader\"></div>\n      </label>\n    </div>\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: FileService }, { type: AlertService }, { type: AnbFormService }]; }, propDecorators: { acceptedFileTypes: [{
                type: Input
            }], documentTypeID: [{
                type: Input
            }], useAlertService: [{
                type: Input
            }], maxFileSizeMB: [{
                type: Input
            }], uploadService: [{
                type: Input
            }], canDownload: [{
                type: Input
            }], canRemove: [{
                type: Input
            }], onFileChange: [{
                type: Output
            }], onError: [{
                type: Output
            }], onUploadFile: [{
                type: Output
            }], onDownloadFile: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUsercodeAutocompleteComponent extends BaseFormElementComponent {
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.value = '';
        this.searchTerm$ = new Subject();
    }
    ngOnInit() {
        super.ngOnInit();
        const sub = this.searchTerm$
            .pipe(switchMap((term) => this.formService.UserCodes.pipe(tap((codes) => {
            this.usercodes = codes.filter((uc) => uc.userCodeGroupID === this.userCodeGroupID && uc.description.toLowerCase().indexOf(term.toLocaleLowerCase()) !== -1);
            if (this.dependencySet) {
                this.usercodes = this.usercodes.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(this.dependencySet) > -1);
            }
        }))))
            .subscribe();
        this.subs.add(sub);
    }
    ngOnDestroy() {
        super.ngOnDestroy();
    }
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onChange(event) {
        this.searchTerm$.next(event.target.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodeAutocompleteComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercodeAutocompleteComponent, selector: "anb-usercodeautocomplete", inputs: { value: "value", dependencySet: "dependencySet", userCodeGroupID: "userCodeGroupID" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercodeAutocompleteComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\">\n  <label class=\"webuniversum form__label\">\n    <span class=\"vraag\">{{ label }}</span>\n    <span class=\"subvraag\">{{ subLabel }}</span>\n    <input\n      class=\"webuniversum input-field\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      type=\"text\"\n      placeholder=\"autocomplete\"\n      [value]=\"value\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (keyup)=\"onChange($event)\"\n    />\n  </label>\n  <div *ngFor=\"let usercode of usercodes\">\n    <p>\n      {{ usercode?.description }}\n    </p>\n  </div>\n  <div *ngIf=\"usercodes?.length < 1\">\n    <hr />\n    <p>No results found :(</p>\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".horizontal{display:inline}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodeAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercodeautocomplete', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercodeAutocompleteComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\">\n  <label class=\"webuniversum form__label\">\n    <span class=\"vraag\">{{ label }}</span>\n    <span class=\"subvraag\">{{ subLabel }}</span>\n    <input\n      class=\"webuniversum input-field\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      type=\"text\"\n      placeholder=\"autocomplete\"\n      [value]=\"value\"\n      [attr.id]=\"id\"\n      [disabled]=\"disabled\"\n      (keyup)=\"onChange($event)\"\n    />\n  </label>\n  <div *ngFor=\"let usercode of usercodes\">\n    <p>\n      {{ usercode?.description }}\n    </p>\n  </div>\n  <div *ngIf=\"usercodes?.length < 1\">\n    <hr />\n    <p>No results found :(</p>\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".horizontal{display:inline}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { value: [{
                type: Input
            }], dependencySet: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUsercodeCheckboxComponent extends BaseFormElementComponent {
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.userCodeValue = { description: '' };
    }
    ngOnInit() {
        super.ngOnInit();
        const sub = this.formService.UserCodes.subscribe((codes) => {
            const userCodes = codes.filter((uc) => uc.userCodeGroupID === this.userCodeGroupID);
            this.userCodeValue = userCodes.find((uc) => uc.userCodeID === this.userCode);
        });
        this.subs.add(sub);
    }
    writeValue(_value) {
        this.value = _value;
        this.valueChange(this.value);
        if (this.value) {
            this.isContentAndereVisible = true;
        }
        else {
            if (this.contentTextarea) {
                this.contentTextarea.clear();
            }
            this.isContentAndereVisible = false;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    reset() {
        this.nativecheckbox.nativeElement.checked = false;
        this.propagateChange(false);
        this.writeValue(false);
    }
    onChange(event) {
        this.value = event.target.checked;
        // update form
        this.propagateChange(this.value);
        this.writeValue(this.value);
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodeCheckboxComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercodeCheckboxComponent, selector: "anb-usercodecheckbox", inputs: { name: "name", value: "value", userCodeGroupID: "userCodeGroupID", userCode: "userCode", hasOtherContent: "hasOtherContent" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercodeCheckboxComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUsercodeCheckboxComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "contentTextarea", first: true, predicate: AnbTextareaComponent, descendants: true }], viewQueries: [{ propertyName: "nativecheckbox", first: true, predicate: ["nativecheckbox"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum form__label checkbox\" [class.invisible]=\"!visible\">\n  <label class=\"\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id\">\n    <input\n      #nativecheckbox\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [value]=\"value\"\n      [checked]=\"value\"\n      (change)=\"onChange($event)\"\n      [disabled]=\"disabled\"\n      [attr.id]=\"id\"\n    /><span></span> <span [innerHtml]=\"userCodeValue.description | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n<div class=\"u-bg-block u-bg-block-items\" *ngIf=\"isContentAndereVisible && hasOtherContent\" #contentWrapper>\n  <ng-content #contentChildren></ng-content>\n</div>\n", styles: [".u-bg-block-items{padding:1rem 1.5rem}.u-bg-block{float:left;width:100%}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodeCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercodecheckbox', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercodeCheckboxComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUsercodeCheckboxComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum form__label checkbox\" [class.invisible]=\"!visible\">\n  <label class=\"\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id\">\n    <input\n      #nativecheckbox\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [value]=\"value\"\n      [checked]=\"value\"\n      (change)=\"onChange($event)\"\n      [disabled]=\"disabled\"\n      [attr.id]=\"id\"\n    /><span></span> <span [innerHtml]=\"userCodeValue.description | safe: 'html'\"></span>\n  </label>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n<div class=\"u-bg-block u-bg-block-items\" *ngIf=\"isContentAndereVisible && hasOtherContent\" #contentWrapper>\n  <ng-content #contentChildren></ng-content>\n</div>\n", styles: [".u-bg-block-items{padding:1rem 1.5rem}.u-bg-block{float:left;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { name: [{
                type: Input
            }], value: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }], userCode: [{
                type: Input
            }], hasOtherContent: [{
                type: Input
            }], nativecheckbox: [{
                type: ViewChild,
                args: ['nativecheckbox']
            }], contentTextarea: [{
                type: ContentChild,
                args: [AnbTextareaComponent]
            }] } });

class ExtendedUserCodeResult {
    constructor(userCodeID, extendedValue) {
        this.userCodeID = userCodeID;
        this.extendedValue = extendedValue;
    }
}

var ExtendedUserCodeTypeEnum;
(function (ExtendedUserCodeTypeEnum) {
    ExtendedUserCodeTypeEnum[ExtendedUserCodeTypeEnum["Text"] = 0] = "Text";
    ExtendedUserCodeTypeEnum[ExtendedUserCodeTypeEnum["Number"] = 1] = "Number";
    ExtendedUserCodeTypeEnum[ExtendedUserCodeTypeEnum["TextArea"] = 2] = "TextArea";
})(ExtendedUserCodeTypeEnum || (ExtendedUserCodeTypeEnum = {}));

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUserCodeCheckboxListExtendedComponent extends BaseFormElementComponent {
    constructor(formService, fb) {
        super(formService);
        this.formService = formService;
        this.fb = fb;
        this.extendedTypeEnum = ExtendedUserCodeTypeEnum;
        this.clearFormArray = (formArray) => {
            while (formArray.length !== 0) {
                formArray.removeAt(0);
            }
        };
        const internalFormGroup = fb.group({
            usercodes: this.fb.array([]),
        });
        const internalAnbFormGroup = new AnbFormGroup(internalFormGroup.controls, internalFormGroup.validator);
        const sub = internalAnbFormGroup.valueChanges.subscribe((x) => {
            this.onInternalFormValueChange(x.usercodes);
        });
        this.subs.add(sub);
        this.internalForm = internalAnbFormGroup;
    }
    ngOnChanges(changes) {
        super.ngOnChanges(changes);
        if (changes.values) {
            this.clearFormArray(this.usercodesArray);
            this.initUsercodesFormArray();
        }
        if (changes.form) {
            if (this.form) {
                this.internalForm.activityStatus = this.form.activityStatus;
                // Add the internal form as a control of the parent form. In this way the validation will also be triggered.
                this.form.addControl(`_${this.id}_internalform`, this.internalForm);
            }
        }
    }
    writeValue(_value) {
        if (this.value) {
            this.value = _value;
            // Fill the internal form with the values
            this.patchInternalFormValues(this.value);
        }
        else {
            this.value = [];
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onInternalFormValueChange(value) {
        // When the values of the internal form change, we set the values of the component
        if (this.internalForm && !this.internalForm.pristine) {
            if (value) {
                this.value = [];
                const filteredValue = value.filter((x) => x.checked === true);
                filteredValue.forEach((usercode) => {
                    this.value.push(new ExtendedUserCodeResult(usercode.usercodeID, usercode.extendedValue));
                });
            }
            this.propagateChange(this.value);
        }
    }
    initUsercodesFormArray() {
        // Create the internalform array based on the values parameter
        if (this.values) {
            this.values.forEach((v) => {
                const group = this.userCodeFormGroup;
                if (v.isExtendedRequired) {
                    group.get('checked').valueChanges.subscribe((value) => {
                        this.setRequiredValidator(group.get('extendedValue'), value);
                    });
                }
                // Path configuration
                group.patchValue({
                    usercodeID: v.userCodeID,
                    label: v.description,
                    isExtended: v.isExtended,
                    extendedPlaceholder: v.extendedPlaceholder ? v.extendedPlaceholder : '',
                    extendedType: v.extendedType ? v.extendedType : ExtendedUserCodeTypeEnum.Text,
                    extendedClass: v.extendedClass,
                    rowClass: v.rowClass,
                    maxlength: v.maxlength,
                    minNumber: v.minNumber,
                    maxNumber: v.maxNumber,
                });
                this.addUsercode(group);
            });
            this.patchInternalFormValues(this.value);
        }
    }
    setRequiredValidator(control, checked) {
        if (checked) {
            control.setValidators([Validators.required]);
        }
        else {
            control.setValidators([]);
        }
        control.updateValueAndValidity();
    }
    patchInternalFormValues(result) {
        if (result && this.usercodesArray) {
            result.forEach((x) => {
                const group = this.usercodesArray.controls.find((y) => y.get('usercodeID').value === x.userCodeID);
                if (group && (group.get('checked').value !== true || group.get('extendedValue').value !== x.extendedValue)) {
                    group.patchValue({
                        checked: true,
                        extendedValue: x.extendedValue,
                    });
                }
            });
        }
    }
    addUsercode(formGroup) {
        this.usercodesArray.push(formGroup);
        return formGroup;
    }
    get usercodesArray() {
        return this.internalForm.get('usercodes');
    }
    get userCodeFormGroup() {
        // Create a new form group for the internalform array
        const group = this.fb.group({
            usercodeID: [],
            checked: [],
            label: [],
            isExtended: [],
            extendedPlaceholder: [],
            extendedValue: [],
            extendedType: [],
            extendedClass: [],
            rowClass: [],
            maxlength: [],
            minNumber: [],
            maxNumber: [],
        });
        const formGroup = new AnbFormGroup(group.controls, group.validator);
        if (this.form) {
            formGroup.activityStatus = this.form.activityStatus;
        }
        return formGroup;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUserCodeCheckboxListExtendedComponent, deps: [{ token: AnbFormService }, { token: i2$2.UntypedFormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUserCodeCheckboxListExtendedComponent, selector: "anb-usercodecheckboxlist-extended", inputs: { values: "values", class: "class" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUserCodeCheckboxListExtendedComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUserCodeCheckboxListExtendedComponent),
                multi: true,
            },
        ], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div *ngIf=\"usercodesArray\" [formGroup]=\"internalForm\" [class]=\"class\">\n  <ng-container formArrayName=\"usercodes\" *ngFor=\"let usercode of usercodesArray.controls; let i = index\">\n    <div [formGroupName]=\"i\" [formGroup]=\"usercode\" [class]=\"usercode.get('rowClass').value\">\n      <div class=\"anb-input webuniversum form__label input-field--block checkbox\" [class.invisible]=\"!visible\">\n        <label class=\"\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id + '_' + usercode.get('usercodeID').value\">\n          <input\n            type=\"checkbox\"\n            class=\"webuniversum checkbox__toggle\"\n            [value]=\"usercode.get('checked').value\"\n            formControlName=\"checked\"\n            [attr.id]=\"id + '_' + usercode.get('usercodeID').value\"\n          /><span></span>\n          <span [innerHtml]=\"usercode.get('label').value | safe: 'html'\"></span>\n        </label>\n        <div *ngIf=\"usercode.get('checked').value && usercode.get('isExtended').value\" [class]=\"usercode.get('extendedClass').value\">\n          <ng-container [ngSwitch]=\"usercode.get('extendedType').value\">\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.Text\">\n              <anb-input-text\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              ></anb-input-text>\n            </ng-container>\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.Number\">\n              <anb-input-numeric\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                [autoCorrect]=\"true\"\n                [min]=\"usercode.get('minNumber').value\"\n                [max]=\"usercode.get('maxNumber').value\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              >\n              </anb-input-numeric>\n            </ng-container>\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.TextArea\">\n              <anb-textarea\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                rows=\"5\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              >\n              </anb-textarea>\n            </ng-container>\n          </ng-container>\n        </div>\n      </div>\n    </div>\n  </ng-container>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i2.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { kind: "directive", type: i2$2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2$2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i2$2.FormGroupName, selector: "[formGroupName]", inputs: ["formGroupName"] }, { kind: "directive", type: i2$2.FormArrayName, selector: "[formArrayName]", inputs: ["formArrayName"] }, { kind: "component", type: AnbInputTextComponent, selector: "anb-input-text", inputs: ["placeholder", "maxlength", "inputType", "rawValue", "autocomplete", "mask", "predefinedMask"] }, { kind: "component", type: AnbTextareaComponent, selector: "anb-textarea", inputs: ["cols", "rows", "placeholder", "maxlength", "autoResize", "showRemainingCharacters", "maxScrollHeight"] }, { kind: "component", type: AnbInputNumericComponent, selector: "anb-input-numeric", inputs: ["min", "max", "value", "placeholder", "decimals", "format", "autoCorrect", "rangeValidation", "autocomplete"], outputs: ["focus", "blur", "enter", "escape"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUserCodeCheckboxListExtendedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercodecheckboxlist-extended', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUserCodeCheckboxListExtendedComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUserCodeCheckboxListExtendedComponent),
                            multi: true,
                        },
                    ], template: "<div *ngIf=\"usercodesArray\" [formGroup]=\"internalForm\" [class]=\"class\">\n  <ng-container formArrayName=\"usercodes\" *ngFor=\"let usercode of usercodesArray.controls; let i = index\">\n    <div [formGroupName]=\"i\" [formGroup]=\"usercode\" [class]=\"usercode.get('rowClass').value\">\n      <div class=\"anb-input webuniversum form__label input-field--block checkbox\" [class.invisible]=\"!visible\">\n        <label class=\"\" [class.checkbox--disabled]=\"disabled\" [attr.for]=\"id + '_' + usercode.get('usercodeID').value\">\n          <input\n            type=\"checkbox\"\n            class=\"webuniversum checkbox__toggle\"\n            [value]=\"usercode.get('checked').value\"\n            formControlName=\"checked\"\n            [attr.id]=\"id + '_' + usercode.get('usercodeID').value\"\n          /><span></span>\n          <span [innerHtml]=\"usercode.get('label').value | safe: 'html'\"></span>\n        </label>\n        <div *ngIf=\"usercode.get('checked').value && usercode.get('isExtended').value\" [class]=\"usercode.get('extendedClass').value\">\n          <ng-container [ngSwitch]=\"usercode.get('extendedType').value\">\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.Text\">\n              <anb-input-text\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              ></anb-input-text>\n            </ng-container>\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.Number\">\n              <anb-input-numeric\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                [autoCorrect]=\"true\"\n                [min]=\"usercode.get('minNumber').value\"\n                [max]=\"usercode.get('maxNumber').value\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              >\n              </anb-input-numeric>\n            </ng-container>\n            <ng-container *ngSwitchCase=\"extendedTypeEnum.TextArea\">\n              <anb-textarea\n                formControlName=\"extendedValue\"\n                [form]=\"internalForm\"\n                [ase]=\"ase\"\n                [formControl]=\"usercode.get('extendedValue')\"\n                [customErrorList]=\"customErrorList\"\n                [placeholder]=\"usercode.get('extendedPlaceholder').value\"\n                label=\"\"\n                rows=\"5\"\n                [maxlength]=\"usercode.get('maxlength').value\"\n              >\n              </anb-textarea>\n            </ng-container>\n          </ng-container>\n        </div>\n      </div>\n    </div>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i2$2.UntypedFormBuilder }]; }, propDecorators: { values: [{
                type: Input
            }], class: [{
                type: Input
            }] } });

var AlignmentEnum;
(function (AlignmentEnum) {
    AlignmentEnum[AlignmentEnum["Vertical"] = 0] = "Vertical";
    AlignmentEnum[AlignmentEnum["Horizontal"] = 1] = "Horizontal";
    AlignmentEnum[AlignmentEnum["HorizontalMax"] = 2] = "HorizontalMax";
})(AlignmentEnum || (AlignmentEnum = {}));

class BaseUsercodecheckboxlistComponent extends BaseFormElementComponent {
    set alignment(value) {
        if (value == AlignmentEnum.Horizontal) {
            this.isHorizontal = true;
        }
        if (value == AlignmentEnum.HorizontalMax) {
            this.isHorizontalMax = true;
        }
    }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.isHorizontal = false;
        this.isHorizontalMax = false;
        //valueWithAllDelimiters houden we apart bij, zodat we naar buiten toe de waarde zonder trailing komma tonen (value), maar intern is het veiliger om te weten dat er steeds een delimiter is
        //steeds met trailing komma, zodat het mogelijk is om bv. VOLMACHT & VOLMACHTEXPRT te hebben, zonder dat de selectie van VOLMACHTEXPRT de automatische selectie van VOLMACHT veroorzaakt
        this.valueWithAllDelimiters = '';
        this.value = '';
        this.userCodeIdOther = '';
        this.isContentAndereVisible = false;
        this.includeSoftdeleted = false;
    }
    showUserCode(usercode) {
        if (this.onlyShowTheseUsercodes) {
            let userCodeValues = this.onlyShowTheseUsercodes.split(',');
            return !this.onlyShowTheseUsercodes || this.onlyShowTheseUsercodes === '' || userCodeValues.indexOf(usercode.userCodeID) > -1;
            // return !this.onlyShowTheseUsercodes
            //     || this.onlyShowTheseUsercodes === ''
            //     || (this.onlyShowTheseUsercodes + ',').indexOf(usercode.UserCodeID + ',') > -1;
        }
        else {
            return true;
        }
    }
    ngOnInit() {
        super.ngOnInit();
        this.formService.UserCodes.subscribe((uc) => {
            if (this.parentUserCodeID) {
                this.usercodes = uc.filter((uc) => uc.userCodeGroupID == this.userCodeGroupID && uc.softDeleted == this.includeSoftdeleted);
                if (this.dependencySet) {
                    this.usercodes = this.usercodes.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(this.dependencySet) > -1);
                }
                this.usercodes = this.usercodes.filter((usercode) => usercode.parentUserCodeID === this.parentUserCodeID && this.showUserCode(usercode));
            }
            else {
                this.usercodes = uc.filter((uc) => uc.userCodeGroupID == this.userCodeGroupID);
                if (this.dependencySet) {
                    this.usercodes = this.usercodes.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(this.dependencySet) > -1);
                }
                this.usercodes = this.usercodes.filter((usercode) => usercode.parentUserCodeID === null && this.showUserCode(usercode));
            }
        });
    }
    // contentChildren are set
    ngAfterContentInit() {
        //de content zal hier nog niet geladen zijn
        ////set initial state content andere
        //let vValueArray = this.value.split(",");
        //this.handleVisablityContentOther(vValueArray);
    }
    //private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
            this.valueWithAllDelimiters = this.value + ','; //steeds met trailing komma, zodat het mogelijk is om bv. VOLMACHT & VOLMACHTEXPRT te hebben, zonder dat de selectie van VOLMACHTEXPRT de automatische selectie van VOLMACHT veroorzaakt
            //set initial state content andere
            let vValueArray = this.value.split(',');
            this.handleVisablityContentOther(vValueArray);
            this.setValue(this.value);
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) { }
    //<to be added in array extention methods>
    add(vValueArray, value) {
        //Add to string array of checked values
        if (vValueArray.indexOf(value) == -1) {
            vValueArray.push(value);
        }
    }
    remove(vValueArray, value) {
        //Remove from string array of checked values
        var vIndex = vValueArray.indexOf(value);
        if (vIndex > -1) {
            vValueArray.splice(vIndex, 1);
        }
    }
    toString(vValueArray) {
        let str = vValueArray.join(',');
        if (str.indexOf(',') == 0) {
            str = str.substring(1);
        }
        return str;
    }
    //</to be added in array extention methods>
    getValueArray() {
        let vValueArray = this.value.split(',');
        if (vValueArray.indexOf('') > -1) {
            //if this.value==="", then the array will contain one element ""
            this.remove(vValueArray, '');
        }
        return vValueArray;
    }
    onChange(event) {
        //Get initial value (string array of initial checked values)
        let vValueArray = this.getValueArray();
        //Check if checkbox is checked or not
        let vChecked = event.target.checked;
        //Get value
        let vValue = event.target.value;
        if (vChecked) {
            this.add(vValueArray, vValue);
        }
        else {
            this.remove(vValueArray, vValue);
        }
        this.writeValue(this.toString(vValueArray));
        this.handleVisablityContentOther(vValueArray);
        //update form
        this.value = vValueArray.join();
        this.propagateChange(this.value);
        this.setValue(this.value);
    }
    isGroupChecked(groupContent) {
        var group = groupContent.replace('{', '').replace('}', '') + ',';
        return groupContent.indexOf(this.value + ',') > -1;
    }
    isOtherGroupChecked(valueToCheck) {
        var currentGroups = this.getCurrentGroups();
        var group = this.getGroup(valueToCheck);
        //var indexOfGroup = currentGroups.indexOf(group);
        //var currentGroupsWithoutChecked = // indexOfGroup > -1 ? currentGroups.splice(indexOfGroup, 1, group) : currentGroups;
        this.remove(currentGroups, group);
        return currentGroups.length > 0;
        //return currentGroupsWithoutChecked.length > 0;// && currentGroups.indexOf(group) > -1;
    }
    isCurrentGroupChecked(valueToCheck) {
        var currentGroups = this.getCurrentGroups();
        var group = this.getGroup(valueToCheck);
        return currentGroups.length > 0 && currentGroups.indexOf(group) > -1;
    }
    getGroup(valueToCheck) {
        var groups = this.getGroups();
        for (var i = 0; i < groups.length; i++) {
            if (groups[i].indexOf(valueToCheck) > -1) {
                return groups[i];
            }
        }
    }
    getCurrentGroups() {
        let vValueArray = this.getValueArray(); // this.value.split(",");
        var currentGroups = [];
        for (var i = 0; i < vValueArray.length; i++) {
            var valueToCheck = vValueArray[i];
            var group = this.getGroup(valueToCheck);
            this.add(currentGroups, group);
        }
        return currentGroups;
    }
    getGroups() {
        var groups = this.grouping.split('}{');
        for (var i = 0; i < groups.length; i++) {
            groups[i] = groups[i].replace('{', '').replace('}', '');
        }
        return groups;
    }
    isDisabled(valueToCheck) {
        if (this.disabled) {
            return true;
        }
        if (this.grouping && this.isOtherGroupChecked(valueToCheck)) {
            return true;
            //var groups = this.grouping.split('}{');
            //groups.forEach(group => this.showContent(group));
        }
        return this.disabled;
    }
    showOther(show) {
        //if (this.contentAndere !== undefined && this.contentAndere.length > 1) {
        //    debugger;
        //}
        // disable all
        //this.contentAndere.toArray().forEach(contenttt => contenttt.disabled = false);
        if (this.contentAndere !== undefined) {
            this.contentAndere.toArray().forEach((content) => this.showContent(content, show));
            this.contentAndere.toArray().forEach((content) => this.displayContent(content, show));
        }
        //    function (content) {
        //    //doordat we deze klasse ook de provider BaseFormElementComponent geven, komt deze ook uit bij QueryList<BaseFormElementComponent>;
        //    //we willen echter niet deze control volledig disablen
        //    debugger;
        //    if (content !== this)
        //    {
        //      content.disabled = !show
        //    }
        //});
    }
    showContent(content, show) {
        //doordat we deze klasse ook de provider BaseFormElementComponent geven, komt deze ook uit bij QueryList<BaseFormElementComponent>;
        //we willen echter niet deze control volledig disablen
        if (content !== this) {
            content.disabled = !show;
        }
    }
    displayContent(content, show) {
        //doordat we deze klasse ook de provider BaseFormElementComponent geven, komt deze ook uit bij QueryList<BaseFormElementComponent>;
        //we willen echter niet deze control volledig disablen
        this.isContentAndereVisible = false;
        if (content !== this) {
            content.visible = show;
            if (show) {
                this.isContentAndereVisible = true;
            }
        }
    }
    checkboxClass() {
        if (this.isHorizontal) {
            return 'horizontal webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs';
        }
        else if (this.isHorizontalMax) {
            return 'fleft webuniversum col--4-12 col--4-12--m col--6-12--s col--1-1--xs';
        }
        else {
            return 'vertical';
        }
    }
    handleVisablityContentOther(pValueArray) {
        //if other is enabled, check if other is selected, so we should enable/disable it
        this.showOther(this.userCodeIdOther && pValueArray.indexOf(this.userCodeIdOther) > -1);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseUsercodecheckboxlistComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BaseUsercodecheckboxlistComponent, selector: "base-usercodecheckboxlist", inputs: { value: "value", userCodeIdOther: "userCodeIdOther", userCodeGroupID: "userCodeGroupID", parentUserCodeID: "parentUserCodeID", parentUserCodeGroupID: "parentUserCodeGroupID", dependencySet: "dependencySet", alignment: "alignment", isContentAndereVisible: "isContentAndereVisible", grouping: "grouping", onlyShowTheseUsercodes: "onlyShowTheseUsercodes", includeSoftdeleted: "includeSoftdeleted" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => BaseUsercodecheckboxlistComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => BaseUsercodecheckboxlistComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "contentAndere", predicate: BaseFormElementComponent }], usesInheritance: true, ngImport: i0, template: "<div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n</div>\n<div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n</div>\n<div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"checkboxClass()\">\n  <label class=\"webuniversum form__label checkbox\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\">\n    <input\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [attr.name]=\"usercode.description\"\n      [attr.value]=\"usercode.userCodeID\"\n      [disabled]=\"isDisabled(usercode.userCodeID)\"\n      [attr.id]=\"'checkbox_' + usercode.userCodeID\"\n      [attr.checked]=\"valueWithAllDelimiters.indexOf(usercode.userCodeID + ',') > -1 ? 'checked' : null\"\n      (change)=\"onChange($event)\"\n    /><span></span>{{ usercode.description }}\n  </label>\n</div>\n<div class=\"u-bg-block\" *ngIf=\"isContentAndereVisible\">\n  <ng-content></ng-content>\n</div>\n<div *ngIf=\"showError()\" class=\"form__error\">\n  {{ getError() }}\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseUsercodecheckboxlistComponent, decorators: [{
            type: Component,
            args: [{ selector: 'base-usercodecheckboxlist', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => BaseUsercodecheckboxlistComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => BaseUsercodecheckboxlistComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n</div>\n<div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n</div>\n<div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"checkboxClass()\">\n  <label class=\"webuniversum form__label checkbox\" [class.checkbox--error]=\"showError() && !disabled\" [class.checkbox--disabled]=\"disabled\">\n    <input\n      type=\"checkbox\"\n      class=\"webuniversum checkbox__toggle\"\n      [attr.name]=\"usercode.description\"\n      [attr.value]=\"usercode.userCodeID\"\n      [disabled]=\"isDisabled(usercode.userCodeID)\"\n      [attr.id]=\"'checkbox_' + usercode.userCodeID\"\n      [attr.checked]=\"valueWithAllDelimiters.indexOf(usercode.userCodeID + ',') > -1 ? 'checked' : null\"\n      (change)=\"onChange($event)\"\n    /><span></span>{{ usercode.description }}\n  </label>\n</div>\n<div class=\"u-bg-block\" *ngIf=\"isContentAndereVisible\">\n  <ng-content></ng-content>\n</div>\n<div *ngIf=\"showError()\" class=\"form__error\">\n  {{ getError() }}\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { contentAndere: [{
                type: ContentChildren,
                args: [BaseFormElementComponent]
            }], value: [{
                type: Input
            }], userCodeIdOther: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }], parentUserCodeID: [{
                type: Input
            }], parentUserCodeGroupID: [{
                type: Input
            }], dependencySet: [{
                type: Input
            }], alignment: [{
                type: Input
            }], isContentAndereVisible: [{
                type: Input
            }], grouping: [{
                type: Input
            }], onlyShowTheseUsercodes: [{
                type: Input
            }], includeSoftdeleted: [{
                type: Input
            }] } });

class AnbUsercodecheckboxlistComponent extends BaseUsercodecheckboxlistComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodecheckboxlistComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercodecheckboxlistComponent, selector: "anb-usercodecheckboxlist", providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercodecheckboxlistComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUsercodecheckboxlistComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div>\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"checkboxClass()\">\n      <label\n        class=\"webuniversum form__label checkbox\"\n        [class.checkbox--error]=\"showError() && !disabled\"\n        [class.checkbox--disabled]=\"disabled\"\n      >\n        <input\n          type=\"checkbox\"\n          class=\"webuniversum checkbox__toggle\"\n          [attr.name]=\"usercode.description\"\n          [attr.value]=\"usercode.userCodeID\"\n          [disabled]=\"isDisabled(usercode.userCodeID)\"\n          [attr.id]=\"'checkbox_' + usercode.userCodeID\"\n          [attr.checked]=\"valueWithAllDelimiters.indexOf(usercode.userCodeID + ',') > -1 ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercode.description }}\n      </label>\n    </div>\n    <div class=\"u-bg-block\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}.u-bg-block{float:left;width:100%}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodecheckboxlistComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercodecheckboxlist', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercodecheckboxlistComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUsercodecheckboxlistComponent),
                            multi: true,
                        },
                    ], template: "<div>\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"checkboxClass()\">\n      <label\n        class=\"webuniversum form__label checkbox\"\n        [class.checkbox--error]=\"showError() && !disabled\"\n        [class.checkbox--disabled]=\"disabled\"\n      >\n        <input\n          type=\"checkbox\"\n          class=\"webuniversum checkbox__toggle\"\n          [attr.name]=\"usercode.description\"\n          [attr.value]=\"usercode.userCodeID\"\n          [disabled]=\"isDisabled(usercode.userCodeID)\"\n          [attr.id]=\"'checkbox_' + usercode.userCodeID\"\n          [attr.checked]=\"valueWithAllDelimiters.indexOf(usercode.userCodeID + ',') > -1 ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercode.description }}\n      </label>\n    </div>\n    <div class=\"u-bg-block\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}.u-bg-block{float:left;width:100%}\n"] }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUsercodedropdownComponent extends BaseFormElementComponent {
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.isHorizontal = false;
        this.value = '';
    }
    ngOnInit() {
        super.ngOnInit();
        const sub = this.formService.UserCodes.subscribe((codes) => {
            this.usercodes = codes.filter((uc) => uc.userCodeGroupID === this.userCodeGroupID);
            if (this.dependencySet) {
                this.usercodes = this.usercodes.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(this.dependencySet) > -1);
            }
        });
        this.subs.add(sub);
    }
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onChange(event) {
        // Get value
        this.value = event.target.value;
        // update form
        this.propagateChange(this.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodedropdownComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercodedropdownComponent, selector: "anb-usercodedropdown", inputs: { value: "value", userCodeGroupID: "userCodeGroupID", dependencySet: "dependencySet" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercodedropdownComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n\n    <select\n      class=\"webuniversum select col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"\n      [attr.name]=\"userCodeGroupID\"\n      [attr.id]=\"'dropdown_' + userCodeGroupID\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n    >\n      <option\n        *ngFor=\"let usercode of usercodes\"\n        [attr.value]=\"usercode.userCodeID\"\n        [attr.selected]=\"value.indexOf(usercode.userCodeID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ usercode.description }}\n      </option>\n    </select>\n  </label>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2$2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercodedropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercodedropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercodedropdownComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n  <label class=\"webuniversum form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n    <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n\n    <select\n      class=\"webuniversum select col--12-12 col--12-12--m col--12-12--s col--1-1--xs\"\n      [attr.name]=\"userCodeGroupID\"\n      [attr.id]=\"'dropdown_' + userCodeGroupID\"\n      [disabled]=\"disabled\"\n      (change)=\"onChange($event)\"\n    >\n      <option\n        *ngFor=\"let usercode of usercodes\"\n        [attr.value]=\"usercode.userCodeID\"\n        [attr.selected]=\"value.indexOf(usercode.userCodeID) > -1 ? 'selected' : null\"\n        [disabled]=\"disabled\"\n      >\n        {{ usercode.description }}\n      </option>\n    </select>\n  </label>\n  <div *ngIf=\"showError()\" class=\"form__error\">\n    {{ getError() }}\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { value: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }], dependencySet: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbUsercoderadiobuttoncascadinglistComponent extends BaseFormElementComponent {
    set alignment(value) {
        if (value === AlignmentEnum.Horizontal) {
            this.isHorizontal = true;
        }
    }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.isHorizontal = false;
        this.value = '';
        this.userCodeIdOther = '';
        /** Depth = het aantal niveaus er zijn (inclusief parent niveau)  */
        this.depth = 3;
        this.isContentAndereVisible = false;
        this.hideBackgroundClass = false;
    }
    ngOnInit() {
        super.ngOnInit();
        const sub = this.formService.UserCodes.subscribe((codes) => {
            this.usercodes = codes.filter((uc) => uc.userCodeGroupID === this.userCodeGroupID);
            if (this.dependencySet) {
                this.usercodes = this.usercodes.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(this.dependencySet) > -1);
            }
            this.parentusercodes = this.usercodes.filter((usercode) => usercode.parentUserCodeID === null);
        });
        this.subs.add(sub);
        this.changeCascadingList();
    }
    filter(usercodes, parentUserCodeID) {
        return usercodes.filter((usercode) => usercode.parentUserCodeID === parentUserCodeID && usercode.visible);
    }
    ngAfterContentInit() {
        // de waardes zijn hier nog niet geladen
        // //set initial state content andere
        // this.handleVisablityContentOther();
    }
    // private propagateChange = (_: any) => { };
    writeValue(value) {
        if (value) {
            this.value = value;
            // set initial state content andere
            this.handleVisablityContentOther();
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    onChange(event) {
        // Check if radiobutton is checked or not
        // const isChecked = event.target.checked;
        // Get value
        const vValue = event.target.value;
        this.value = vValue;
        this.changeCascadingList();
        this.handleVisablityContentOther();
        // update form
        this.propagateChange(this.value);
    }
    changeCascadingList() {
        // set child user codes visible
        this.usercodes.forEach((uc) => {
            if (uc.userCodeID === this.value) {
                if (uc.parentUserCodeID === null) {
                    this.usercodes.forEach((uc1) => (uc1.visible = false));
                }
                else {
                    // parent should be visible
                    this.usercodes.forEach((ucParent) => {
                        if (ucParent.userCodeID === uc.parentUserCodeID) {
                            ucParent.visible = true;
                            this.value = uc.userCodeID;
                        }
                    });
                }
                uc.visible = true;
            }
            if (uc.parentUserCodeID === this.value) {
                uc.visible = true;
                // parent(s) should always be visible
                this.usercodes.forEach((uc1) => {
                    if (uc1.userCodeID === uc.parentUserCodeID) {
                        uc1.visible = true;
                    }
                });
            }
        });
    }
    radiobuttonClass() {
        if (this.isHorizontal) {
            return 'horizontal webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs';
        }
        else {
            return 'vertical webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs';
        }
    }
    showOther(show) {
        if (this.contentAndere) {
            this.contentAndere.toArray().forEach((content) => this.displayContent(content, show));
        }
    }
    displayContent(content, show) {
        // doordat we deze klasse ook de provider BaseFormElementComponent geven, komt deze ook uit bij QueryList<BaseFormElementComponent>;
        // we willen echter niet deze control verbergen
        this.isContentAndereVisible = false;
        if (content !== this) {
            content.visible = show;
            if (show) {
                this.isContentAndereVisible = true;
            }
        }
    }
    handleVisablityContentOther() {
        // if other is enabled, check if other is selected, so we should enable/disable it
        this.showOther(this.userCodeIdOther && this.value === this.userCodeIdOther);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercoderadiobuttoncascadinglistComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercoderadiobuttoncascadinglistComponent, selector: "anb-usercoderadiobuttoncascadinglist", inputs: { value: "value", userCodeIdOther: "userCodeIdOther", depth: "depth", userCodeGroupID: "userCodeGroupID", parentUserCodeGroupID: "parentUserCodeGroupID", dependencySet: "dependencySet", alignment: "alignment", isContentAndereVisible: "isContentAndereVisible", hideBackgroundClass: "hideBackgroundClass" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercoderadiobuttoncascadinglistComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUsercoderadiobuttoncascadinglistComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "contentAndere", predicate: BaseFormElementComponent }], usesInheritance: true, ngImport: i0, template: "<div [class.invisible]=\"!visible\">\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercodeparent of parentusercodes\" [className]=\"radiobuttonClass()\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [attr.name]=\"usercodeparent.userCodeGroupID\"\n          [attr.value]=\"usercodeparent.userCodeID\"\n          [disabled]=\"disabled\"\n          [attr.id]=\"'radiobutton_' + usercodeparent.userCodeID\"\n          [attr.checked]=\"value.indexOf(usercodeparent.userCodeID) > -1 ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercodeparent.description }}\n      </label>\n      <div *ngIf=\"depth > 1\">\n        <div\n          style=\"margin-left: 10px\"\n          class=\"anb-input\"\n          *ngFor=\"let usercode1 of filter(usercodes, usercodeparent.userCodeID)\"\n          [className]=\"radiobuttonClass()\"\n        >\n          <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n            <input\n              type=\"radio\"\n              class=\"webuniversum radio__toggle\"\n              [attr.name]=\"usercode1.userCodeGroupID\"\n              [attr.value]=\"usercode1.userCodeID\"\n              [disabled]=\"disabled\"\n              [attr.id]=\"'radiobutton_' + usercode1.userCodeID\"\n              [attr.checked]=\"value.indexOf(usercode1.userCodeID) > -1 ? 'checked' : null\"\n              (change)=\"onChange($event)\"\n            /><span></span>{{ usercode1.description }}\n          </label>\n          <div *ngIf=\"depth > 2\">\n            <div\n              style=\"margin-left: 20px\"\n              class=\"anb-input\"\n              *ngFor=\"let usercode of filter(usercodes, usercode1.userCodeID)\"\n              [className]=\"radiobuttonClass()\"\n            >\n              <label\n                class=\"webuniversum form__label radio\"\n                [class.radio--error]=\"showError() && !disabled\"\n                [class.radio--disabled]=\"disabled\"\n              >\n                <input\n                  type=\"radio\"\n                  class=\"webuniversum radio__toggle\"\n                  [attr.name]=\"usercode.userCodeGroupID\"\n                  [attr.value]=\"usercode.userCodeID\"\n                  [disabled]=\"disabled\"\n                  [attr.id]=\"'radiobutton_' + usercode.userCodeID\"\n                  [attr.checked]=\"value.indexOf(usercode.userCodeID) > -1 ? 'checked' : null\"\n                  (change)=\"onChange($event)\"\n                /><span></span>{{ usercode.description }}\n              </label>\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div [class.u-bg-block]=\"!hideBackgroundClass\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercoderadiobuttoncascadinglistComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercoderadiobuttoncascadinglist', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercoderadiobuttoncascadinglistComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUsercoderadiobuttoncascadinglistComponent),
                            multi: true,
                        },
                    ], template: "<div [class.invisible]=\"!visible\">\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercodeparent of parentusercodes\" [className]=\"radiobuttonClass()\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [attr.name]=\"usercodeparent.userCodeGroupID\"\n          [attr.value]=\"usercodeparent.userCodeID\"\n          [disabled]=\"disabled\"\n          [attr.id]=\"'radiobutton_' + usercodeparent.userCodeID\"\n          [attr.checked]=\"value.indexOf(usercodeparent.userCodeID) > -1 ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercodeparent.description }}\n      </label>\n      <div *ngIf=\"depth > 1\">\n        <div\n          style=\"margin-left: 10px\"\n          class=\"anb-input\"\n          *ngFor=\"let usercode1 of filter(usercodes, usercodeparent.userCodeID)\"\n          [className]=\"radiobuttonClass()\"\n        >\n          <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n            <input\n              type=\"radio\"\n              class=\"webuniversum radio__toggle\"\n              [attr.name]=\"usercode1.userCodeGroupID\"\n              [attr.value]=\"usercode1.userCodeID\"\n              [disabled]=\"disabled\"\n              [attr.id]=\"'radiobutton_' + usercode1.userCodeID\"\n              [attr.checked]=\"value.indexOf(usercode1.userCodeID) > -1 ? 'checked' : null\"\n              (change)=\"onChange($event)\"\n            /><span></span>{{ usercode1.description }}\n          </label>\n          <div *ngIf=\"depth > 2\">\n            <div\n              style=\"margin-left: 20px\"\n              class=\"anb-input\"\n              *ngFor=\"let usercode of filter(usercodes, usercode1.userCodeID)\"\n              [className]=\"radiobuttonClass()\"\n            >\n              <label\n                class=\"webuniversum form__label radio\"\n                [class.radio--error]=\"showError() && !disabled\"\n                [class.radio--disabled]=\"disabled\"\n              >\n                <input\n                  type=\"radio\"\n                  class=\"webuniversum radio__toggle\"\n                  [attr.name]=\"usercode.userCodeGroupID\"\n                  [attr.value]=\"usercode.userCodeID\"\n                  [disabled]=\"disabled\"\n                  [attr.id]=\"'radiobutton_' + usercode.userCodeID\"\n                  [attr.checked]=\"value.indexOf(usercode.userCodeID) > -1 ? 'checked' : null\"\n                  (change)=\"onChange($event)\"\n                /><span></span>{{ usercode.description }}\n              </label>\n            </div>\n          </div>\n        </div>\n      </div>\n    </div>\n    <div [class.u-bg-block]=\"!hideBackgroundClass\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { contentAndere: [{
                type: ContentChildren,
                args: [BaseFormElementComponent]
            }], value: [{
                type: Input
            }], userCodeIdOther: [{
                type: Input
            }], depth: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }], parentUserCodeGroupID: [{
                type: Input
            }], dependencySet: [{
                type: Input
            }], alignment: [{
                type: Input
            }], isContentAndereVisible: [{
                type: Input
            }], hideBackgroundClass: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
// tslint:disable-next-line: max-line-length
class AnbUsercoderadiobuttonlistComponent extends BaseFormElementComponent {
    set alignment(value) {
        if (value === AlignmentEnum.Horizontal) {
            this.isHorizontal = true;
        }
    }
    constructor(formService) {
        super(formService);
        this.formService = formService;
        this.isHorizontal = false;
        this.id = 'radiobutton_';
        this.namePrefix = 'name_';
        this.value = '';
        this.userCodeIdOther = '';
        this.isContentAndereVisible = false;
        this.hideBackgroundClass = false;
        this.includeSoftdeleted = false;
    }
    ngOnInit() {
        super.ngOnInit();
        this.formService.UserCodes.subscribe((uc) => {
            if (this.parentUserCodeID) {
                this.usercodeCache = uc.filter((uc1) => uc1.userCodeGroupID === this.userCodeGroupID);
                this.usercodeCache = this.usercodeCache.filter((usercode) => usercode.parentUserCodeID === this.parentUserCodeID && usercode.softDeleted === this.includeSoftdeleted);
            }
            else {
                this.usercodeCache = uc.filter((uc1) => uc1.userCodeGroupID === this.userCodeGroupID);
                this.usercodeCache = this.usercodeCache.filter((usercode) => usercode.parentUserCodeID === null && usercode.softDeleted === this.includeSoftdeleted);
            }
            // this value can change, so if we store the filtered version inside the cache, we cannot retrieve other values
            if (this.dependencySet && this.usercodeCache) {
                this.usercodes = this.usercodeCache.filter((uc1) => uc1.dependencySet !== null && uc1.dependencySet.indexOf(this.dependencySet) > -1 && uc1.softDeleted === this.includeSoftdeleted);
            }
            else {
                this.usercodes = this.usercodeCache.slice();
            }
        });
    }
    ngAfterContentInit() {
        this.handleVisablityContentOther();
    }
    writeValue(value) {
        if (value) {
            this.value = value;
            // set initial state content andere
            this.handleVisablityContentOther();
        }
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    ngOnChanges(changes) {
        const vDependencySet = changes.dependencySet;
        if (vDependencySet && vDependencySet.currentValue !== null && this.usercodeCache) {
            this.usercodes = this.usercodeCache.filter((uc) => uc.dependencySet !== null && uc.dependencySet.indexOf(vDependencySet.currentValue) > -1);
        }
    }
    onChange(event) {
        // Check if radiobutton is checked or not
        // const vChecked = event.target.checked;
        // Get value
        const vValue = event.target.value;
        this.value = vValue;
        this.handleVisablityContentOther();
        // update form
        this.propagateChange(this.value);
        this.setValue(this.value);
    }
    radiobuttonClass() {
        if (this.isHorizontal) {
            return 'horizontal webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs';
        }
        else {
            return 'vertical webuniversum col--12-12 col--12-12--m col--12-12--s col--1-1--xs';
        }
    }
    showOther(show) {
        if (this.contentAndere) {
            this.contentAndere.toArray().forEach((content) => this.displayContent(content, show));
        }
    }
    displayContent(content, show) {
        // doordat we deze klasse ook de provider BaseFormElementComponent geven, komt deze ook uit bij QueryList<BaseFormElementComponent>;
        // we willen echter niet deze control verbergen
        this.isContentAndereVisible = false;
        if (content !== this) {
            content.visible = show;
            if (show) {
                this.isContentAndereVisible = true;
            }
        }
    }
    handleVisablityContentOther() {
        // if other is enabled, check if other is selected, so we should enable/disable it
        this.showOther(this.userCodeIdOther && this.value === this.userCodeIdOther);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercoderadiobuttonlistComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbUsercoderadiobuttonlistComponent, selector: "anb-usercoderadiobuttonlist", inputs: { id: "id", namePrefix: "namePrefix", value: "value", userCodeIdOther: "userCodeIdOther", userCodeGroupID: "userCodeGroupID", parentUserCodeID: "parentUserCodeID", parentUserCodeGroupID: "parentUserCodeGroupID", dependencySet: "dependencySet", alignment: "alignment", isContentAndereVisible: "isContentAndereVisible", hideBackgroundClass: "hideBackgroundClass", includeSoftdeleted: "includeSoftdeleted" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbUsercoderadiobuttonlistComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbUsercoderadiobuttonlistComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "contentAndere", predicate: BaseFormElementComponent }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div [class.invisible]=\"!visible\">\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"radiobuttonClass()\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [attr.name]=\"namePrefix + usercode.userCodeGroupID + '_' + usercode.parentUserCodeID\"\n          [attr.value]=\"usercode.userCodeID\"\n          [disabled]=\"disabled\"\n          [attr.id]=\"id + usercode.userCodeID\"\n          [attr.checked]=\"value === usercode.userCodeID ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercode.description }}\n      </label>\n    </div>\n    <div [class.u-bg-block]=\"!hideBackgroundClass\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}.u-bg-block{float:left;width:100%}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbUsercoderadiobuttonlistComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-usercoderadiobuttonlist', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbUsercoderadiobuttonlistComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbUsercoderadiobuttonlistComponent),
                            multi: true,
                        },
                    ], template: "<div [class.invisible]=\"!visible\">\n  <fieldset>\n    <div class=\"vraag webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n    </div>\n    <div class=\"webuniversum anb-input form__label col--12-12 col--12-12--m col--12-12--s col--1-1--xs\">\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n    </div>\n    <div class=\"anb-input\" *ngFor=\"let usercode of usercodes\" [className]=\"radiobuttonClass()\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [attr.name]=\"namePrefix + usercode.userCodeGroupID + '_' + usercode.parentUserCodeID\"\n          [attr.value]=\"usercode.userCodeID\"\n          [disabled]=\"disabled\"\n          [attr.id]=\"id + usercode.userCodeID\"\n          [attr.checked]=\"value === usercode.userCodeID ? 'checked' : null\"\n          (change)=\"onChange($event)\"\n        /><span></span>{{ usercode.description }}\n      </label>\n    </div>\n    <div [class.u-bg-block]=\"!hideBackgroundClass\" *ngIf=\"isContentAndereVisible\">\n      <ng-content></ng-content>\n    </div>\n    <div *ngIf=\"annotation\" class=\"webuniversum form__annotation\">{{ annotation }}</div>\n    <div *ngIf=\"showError()\" class=\"form__error\">\n      {{ getError() }}\n    </div>\n  </fieldset>\n</div>\n", styles: [".horizontal{display:inline}.u-bg-block{float:left;width:100%}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { contentAndere: [{
                type: ContentChildren,
                args: [BaseFormElementComponent]
            }], id: [{
                type: Input
            }], namePrefix: [{
                type: Input
            }], value: [{
                type: Input
            }], userCodeIdOther: [{
                type: Input
            }], userCodeGroupID: [{
                type: Input
            }], parentUserCodeID: [{
                type: Input
            }], parentUserCodeGroupID: [{
                type: Input
            }], dependencySet: [{
                type: Input
            }], alignment: [{
                type: Input
            }], isContentAndereVisible: [{
                type: Input
            }], hideBackgroundClass: [{
                type: Input
            }], includeSoftdeleted: [{
                type: Input
            }] } });

class AnbWorkflowButtonsComponent {
    constructor() {
        this.workflowButtonClick = new EventEmitter();
    }
    buttonClick(wfTransition) {
        this.workflowButtonClick.emit(wfTransition);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbWorkflowButtonsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbWorkflowButtonsComponent, selector: "anb-workflow-buttons", inputs: { showIndienen: "showIndienen", workflowTransitions: "workflowTransitions" }, outputs: { workflowButtonClick: "workflowButtonClick" }, ngImport: i0, template: "<ng-container *ngFor=\"let wfTransition of workflowTransitions\">\n  <ng-container *ngIf=\"(wfTransition.workflowEventID === 'ENT' && showIndienen) || wfTransition.workflowEventID !== 'ENT'\">\n    <a\n      class=\"webuniversum button\"\n      [attr.id]=\"'button' + wfTransition.workflowEventID\"\n      (click)=\"buttonClick(wfTransition)\"\n      style=\"color: #fff; margin-right: 10px\"\n      >{{ wfTransition.description }}</a\n    >\n  </ng-container></ng-container\n>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbWorkflowButtonsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-workflow-buttons', template: "<ng-container *ngFor=\"let wfTransition of workflowTransitions\">\n  <ng-container *ngIf=\"(wfTransition.workflowEventID === 'ENT' && showIndienen) || wfTransition.workflowEventID !== 'ENT'\">\n    <a\n      class=\"webuniversum button\"\n      [attr.id]=\"'button' + wfTransition.workflowEventID\"\n      (click)=\"buttonClick(wfTransition)\"\n      style=\"color: #fff; margin-right: 10px\"\n      >{{ wfTransition.description }}</a\n    >\n  </ng-container></ng-container\n>\n" }]
        }], propDecorators: { showIndienen: [{
                type: Input
            }], workflowTransitions: [{
                type: Input
            }], workflowButtonClick: [{
                type: Output
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbInputPercentageComponent extends BaseFormElementComponent {
    get value() {
        return this.percentageForm.get(this.FORM_PARAM.percent)?.value;
    }
    constructor(formService, fb) {
        super(formService);
        this.formService = formService;
        this.fb = fb;
        this.precision = 2;
        this.min = 0;
        this.max = 100;
        this.sub = new Subscription();
        this.FORM_PARAM = {
            percent: 'percent',
        };
        this.percentageForm = this.fb.group({
            [this.FORM_PARAM.percent]: [null, null],
        });
        this.onChange = () => undefined;
        this.onTouch = () => undefined;
    }
    ngOnInit() {
        super.ngOnInit();
        const sink = this.percentageForm.get(this.FORM_PARAM.percent).valueChanges.subscribe((value) => {
            this.writeValue(value);
            this.onTouch();
            this.onChange(maskitoParseNumber(value));
        });
        this.sub.add(sink);
        this.mask = maskitoNumberOptionsGenerator({
            precision: this.precision,
            decimalSeparator: ',',
            thousandSeparator: '.',
            min: this.min,
            max: this.max,
            postfix: '%'
        });
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    writeValue(percent) {
        this.percentageForm.patchValue({ percent: maskitoParseNumber(percent.toString()) }, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputPercentageComponent, deps: [{ token: AnbFormService }, { token: i2$2.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputPercentageComponent, selector: "anb-input-percentage", inputs: { placeholder: "placeholder", precision: "precision", min: "min", max: "max" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputPercentageComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputPercentageComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\" [formGroup]=\"percentageForm\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    type=\"text\"\n    suffix=\"%\"\n    [formControlName]=\"FORM_PARAM.percent\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [maskito]=\"mask\"\n    class=\"webuniversum input-field input-number\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n  />\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error vl-typography\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:5em}.input-number{text-align:right}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i3.MaskitoDirective, selector: "[maskito]", inputs: ["maskito", "maskitoElement"] }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputPercentageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-percentage', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputPercentageComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputPercentageComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\" [formGroup]=\"percentageForm\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel\"></span>\n  <input\n    type=\"text\"\n    suffix=\"%\"\n    [formControlName]=\"FORM_PARAM.percent\"\n    [placeholder]=\"placeholder\"\n    [disabled]=\"disabled\"\n    [attr.id]=\"id\"\n    [maskito]=\"mask\"\n    class=\"webuniversum input-field input-number\"\n    [class.input-field--error]=\"showError() && !disabled\"\n    [class.input-field--disabled]=\"disabled\"\n  />\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error vl-typography\" [innerHtml]=\"getError() | safe: 'html'\"></div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:5em}.input-number{text-align:right}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i2$2.FormBuilder }]; }, propDecorators: { placeholder: [{
                type: Input
            }], precision: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbYesNoRadioComponent extends BaseFormElementComponent {
    get value() {
        return this.innerValue;
    }
    constructor(formService, fb) {
        super(formService);
        this.formService = formService;
        this.fb = fb;
        this.ja = 'Ja';
        this.nee = 'Nee';
        this.sub = new Subscription();
        this.innerValue = null;
        this.FORM_PARAMS = {
            selected: 'selected',
        };
        this.radioForm = this.fb.group({
            [this.FORM_PARAMS.selected]: [],
        });
        this.onChange = () => undefined;
        this.onTouch = () => undefined;
    }
    ngOnInit() {
        super.ngOnInit();
        const sink = this.radioForm.get(this.FORM_PARAMS.selected).valueChanges.subscribe((value) => {
            this.writeValue(value);
            this.onTouch();
            this.onChange(value);
        });
        this.sub.add(sink);
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    writeValue(val) {
        this.innerValue = val;
        this.radioForm.patchValue({ selected: val }, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbYesNoRadioComponent, deps: [{ token: AnbFormService }, { token: i2$2.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbYesNoRadioComponent, selector: "anb-yes-no-radio", inputs: { ja: "ja", nee: "nee" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbYesNoRadioComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbYesNoRadioComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div>\n  <div class=\"float-container\" [formGroup]=\"radioForm\">\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"true\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n        /><span></span><span [innerHtml]=\"ja\"></span>\n      </label>\n    </div>\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"false\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\" /><span></span><span [innerHtml]=\"nee\"></span\n      ></label>\n    </div>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".float-container{padding:0;width:-moz-min-content;width:min-content}.float-container div:first-child{padding-right:2rem}.float-child{width:50%;float:left}.radio.radio--disabled{color:#666}.readonly{font-weight:500;padding-left:1rem}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbYesNoRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-yes-no-radio', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbYesNoRadioComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbYesNoRadioComponent),
                            multi: true,
                        },
                    ], template: "<div>\n  <div class=\"float-container\" [formGroup]=\"radioForm\">\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"true\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n        /><span></span><span [innerHtml]=\"ja\"></span>\n      </label>\n    </div>\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"false\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\" /><span></span><span [innerHtml]=\"nee\"></span\n      ></label>\n    </div>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".float-container{padding:0;width:-moz-min-content;width:min-content}.float-container div:first-child{padding-right:2rem}.float-child{width:50%;float:left}.radio.radio--disabled{color:#666}.readonly{font-weight:500;padding-left:1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i2$2.FormBuilder }]; }, propDecorators: { ja: [{
                type: Input
            }], nee: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class AnbYesNoContainerComponent extends BaseFormElementComponent {
    get value() {
        return this.innerValue;
    }
    constructor(formService, fb) {
        super(formService);
        this.formService = formService;
        this.fb = fb;
        this.question = 'Vraag?';
        this.ja = 'Ja';
        this.nee = 'Nee';
        this.sub = new Subscription();
        this.innerValue = null;
        this.FORM_PARAMS = {
            selected: 'selected',
        };
        this.radioForm = this.fb.group({
            [this.FORM_PARAMS.selected]: [],
        });
        this.onChange = () => undefined;
        this.onTouch = () => undefined;
    }
    ngOnInit() {
        super.ngOnInit();
        const sink = this.radioForm.get(this.FORM_PARAMS.selected).valueChanges.subscribe((value) => {
            this.writeValue(value);
            this.onTouch();
            this.onChange(value);
        });
        this.sub.add(sink);
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    writeValue(val) {
        this.innerValue = val;
        this.radioForm.patchValue({ selected: this.value }, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbYesNoContainerComponent, deps: [{ token: AnbFormService }, { token: i2$2.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbYesNoContainerComponent, selector: "anb-yes-no-container", inputs: { question: "question", ja: "ja", nee: "nee" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbYesNoContainerComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbYesNoContainerComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div>\n  <div class=\"question\">{{ question }}</div>\n  <div class=\"float-container\" [formGroup]=\"radioForm\">\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"true\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n        /><span></span><span [innerHtml]=\"ja\"></span>\n      </label>\n    </div>\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"false\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\" /><span></span><span [innerHtml]=\"nee\"></span\n      ></label>\n    </div>\n  </div>\n\n  <div class=\"vl-u-spacer--small vl-u-clearfix\"></div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n\n  <div *ngIf=\"value\">\n    <ng-content select=\"[yesContent]\"></ng-content>\n  </div>\n  <div *ngIf=\"value === false\">\n    <ng-content select=\"[noContent]\"></ng-content>\n  </div>\n</div>\n", styles: [".question{margin-bottom:1rem}.radios{margin-bottom:2rem;margin-left:10px}.float-container{padding-left:1rem;width:-moz-min-content;width:min-content}.float-container div:first-child{padding-right:2rem}.float-child{width:50%;float:left}.radio.radio--disabled{color:#666}.readonly{font-weight:500;padding-left:1rem}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbYesNoContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-yes-no-container', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbYesNoContainerComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbYesNoContainerComponent),
                            multi: true,
                        },
                    ], template: "<div>\n  <div class=\"question\">{{ question }}</div>\n  <div class=\"float-container\" [formGroup]=\"radioForm\">\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"true\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n        /><span></span><span [innerHtml]=\"ja\"></span>\n      </label>\n    </div>\n    <div class=\"float-child anb-input\">\n      <label class=\"webuniversum form__label radio\" [class.radio--error]=\"showError() && !disabled\" [class.radio--disabled]=\"disabled\">\n        <input\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [value]=\"false\"\n          [formControlName]=\"FORM_PARAMS.selected\"\n          [attr.disabled]=\"disabled ? '' : null\" /><span></span><span [innerHtml]=\"nee\"></span\n      ></label>\n    </div>\n  </div>\n\n  <div class=\"vl-u-spacer--small vl-u-clearfix\"></div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n\n  <div *ngIf=\"value\">\n    <ng-content select=\"[yesContent]\"></ng-content>\n  </div>\n  <div *ngIf=\"value === false\">\n    <ng-content select=\"[noContent]\"></ng-content>\n  </div>\n</div>\n", styles: [".question{margin-bottom:1rem}.radios{margin-bottom:2rem;margin-left:10px}.float-container{padding-left:1rem;width:-moz-min-content;width:min-content}.float-container div:first-child{padding-right:2rem}.float-child{width:50%;float:left}.radio.radio--disabled{color:#666}.readonly{font-weight:500;padding-left:1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: i2$2.FormBuilder }]; }, propDecorators: { question: [{
                type: Input
            }], ja: [{
                type: Input
            }], nee: [{
                type: Input
            }] } });

class AnbBaseFormService {
    constructor() {
        this.baseFormService = inject(AnbFormService);
    }
    get isValid() {
        if (!this.form.valid) {
            this.baseFormService.markAsDirty(this.form);
            return false;
        }
        return true;
    }
    isFormGroupValid(formGroup) {
        if (!formGroup.valid) {
            this.baseFormService.markAsDirty(formGroup);
        }
        return formGroup.valid;
    }
    set activityStatus(activityStatus) {
        if (this.form.activityStatus) {
            this.form.activityStatus.next(activityStatus);
        }
    }
    resetForm() {
        this.form.reset();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbBaseFormService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbBaseFormService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbBaseFormService, decorators: [{
            type: Injectable
        }] });

const FORM_PARAMS = {
    id: 'id',
    filename: 'filename',
    description: 'description',
    type: 'type',
    date: 'date',
};
class AnbS3UploadInfoFormService extends AnbBaseFormService {
    constructor(fb) {
        super();
        this.fb = fb;
        this.errorService = inject(ErrorService);
        this.formService = inject(AnbFormService);
        this.uiService = inject(AnbUIService);
    }
    get form() {
        if (!this.anbForm) {
            const form = this.fb.group({
                [FORM_PARAMS.id]: [null, null],
                [FORM_PARAMS.filename]: [null, null],
                [FORM_PARAMS.description]: [null, Validators.required],
                [FORM_PARAMS.type]: [null, null],
                [FORM_PARAMS.date]: [null, null],
            });
            this.anbForm = new AnbFormGroup(form.controls, form.validator);
            this.anbForm.setActivityStatus(ActivityStatusEnum.Edit);
        }
        return this.anbForm;
    }
    get customErrors() {
        const errors = [];
        return errors;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3UploadInfoFormService, deps: [{ token: AnbFormBuilder }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3UploadInfoFormService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3UploadInfoFormService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: AnbFormBuilder }]; } });

class AnbS3MultiUploadInfoComponent {
    constructor(formService, modalRef) {
        this.formService = formService;
        this.modalRef = modalRef;
        this.isTypeRequired = false;
        this.seperateViewLink = false;
        this.FORM_PARAMS = FORM_PARAMS;
        this.aseEnum = ActivityStatusEnablementEnum;
        this.form = this.formService.form;
    }
    ngOnInit() {
        this.data = this.modalRef.data;
        const { config } = this.data;
        this.l18n = config.l18n;
        const typeControl = this.form.get(FORM_PARAMS.type);
        if (config?.attachmentType?.required ?? false) {
            typeControl.setValidators(Validators.required);
        }
        else {
            typeControl.setValidators(null);
        }
        const descriptionControl = this.form.get(FORM_PARAMS.description);
        if (config?.description?.required ?? true) {
            descriptionControl.setValidators(Validators.required);
        }
        else {
            descriptionControl.setValidators(null);
        }
        this.form.updateValueAndValidity();
        const id = this.data?.id;
        const type = this.data?.type;
        const description = this.data?.description ?? '';
        const filename = this.data?.file?.name ?? this.data?.fileName;
        const date = this.data?.date ?? Date();
        this.form.patchValue({ filename, type, description, date, id });
    }
    cancel() {
        this.modalRef.close(undefined);
    }
    close() {
        if (!this.form.valid) {
            return;
        }
        const { description, type, id, date } = this.form.value;
        const fileName = this.data?.file?.name ?? this.data?.fileName;
        const response = { ...this.modalRef.data, id, description, type, fileName, date };
        this.modalRef.close(response);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3MultiUploadInfoComponent, deps: [{ token: AnbS3UploadInfoFormService }, { token: i2$4.AnbModalRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbS3MultiUploadInfoComponent, selector: "anb-s3-multi-upload-info", inputs: { isTypeRequired: "isTypeRequired", typeDropdownList: "typeDropdownList", seperateViewLink: "seperateViewLink" }, providers: [AnbS3UploadInfoFormService], viewQueries: [{ propertyName: "anbModalComponent", first: true, predicate: ["extraInfoModal"], descendants: true, static: true }], ngImport: i0, template: "<anb-modal #extraInfoModal id=\"extraInfoModal\" [title]=\"l18n?.title\" [closeOnBackdrop]=\"false\">\n  <div class=\"modal-dialog__content\">\n    <section [formGroup]=\"form\">\n      <div class=\"vl-form__group\">\n        <div class=\"form__row\">\n          <anb-input-text\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.filename\"\n            [label]=\"l18n?.filename?.label || 'Bestandsnaam'\"\n            [ase]=\"aseEnum.ReadOnly\"\n          >\n          </anb-input-text>\n        </div>\n        <div class=\"form__row\">\n          <anb-input-text\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.description\"\n            [label]=\"l18n?.description?.label || 'Omschrijving'\"\n            [ase]=\"aseEnum.EditInsert\"\n            maxlength=\"200\"\n          >\n          </anb-input-text>\n        </div>\n        <div class=\"form__row\" *ngIf=\"!data?.config?.attachmentType?.hidden || true\">\n          <anb-dropdown\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.type\"\n            [label]=\"l18n?.type?.label || 'Type'\"\n            [values]=\"data?.typeOptions\"\n            [ase]=\"aseEnum.EditInsert\"\n          >\n          </anb-dropdown>\n        </div>\n      </div>\n    </section>\n  </div>\n\n  <div class=\"vl-action-group mt-10\">\n    <button class=\"vl-button vl-button--secondary\" type=\"button\" (click)=\"cancel()\">{{ l18n?.cancelButton || 'Annuleer' }}</button>\n    <button class=\"vl-button\" type=\"button\" (click)=\"close()\">{{ l18n?.confirmButton || 'Opslaan' }}</button>\n  </div>\n</anb-modal>\n", dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$4.AnbModalComponent, selector: "anb-modal", inputs: ["id", "title", "hideCloseButton", "hideBackdrop", "open", "closeOnBackdrop", "position"], outputs: ["close"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: AnbDropdownComponent, selector: "anb-dropdown", inputs: ["values", "value", "dropdownID", "placeholder"] }, { kind: "component", type: AnbInputTextComponent, selector: "anb-input-text", inputs: ["placeholder", "maxlength", "inputType", "rawValue", "autocomplete", "mask", "predefinedMask"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3MultiUploadInfoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-s3-multi-upload-info', providers: [AnbS3UploadInfoFormService], template: "<anb-modal #extraInfoModal id=\"extraInfoModal\" [title]=\"l18n?.title\" [closeOnBackdrop]=\"false\">\n  <div class=\"modal-dialog__content\">\n    <section [formGroup]=\"form\">\n      <div class=\"vl-form__group\">\n        <div class=\"form__row\">\n          <anb-input-text\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.filename\"\n            [label]=\"l18n?.filename?.label || 'Bestandsnaam'\"\n            [ase]=\"aseEnum.ReadOnly\"\n          >\n          </anb-input-text>\n        </div>\n        <div class=\"form__row\">\n          <anb-input-text\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.description\"\n            [label]=\"l18n?.description?.label || 'Omschrijving'\"\n            [ase]=\"aseEnum.EditInsert\"\n            maxlength=\"200\"\n          >\n          </anb-input-text>\n        </div>\n        <div class=\"form__row\" *ngIf=\"!data?.config?.attachmentType?.hidden || true\">\n          <anb-dropdown\n            [form]=\"form\"\n            [formControlName]=\"FORM_PARAMS.type\"\n            [label]=\"l18n?.type?.label || 'Type'\"\n            [values]=\"data?.typeOptions\"\n            [ase]=\"aseEnum.EditInsert\"\n          >\n          </anb-dropdown>\n        </div>\n      </div>\n    </section>\n  </div>\n\n  <div class=\"vl-action-group mt-10\">\n    <button class=\"vl-button vl-button--secondary\" type=\"button\" (click)=\"cancel()\">{{ l18n?.cancelButton || 'Annuleer' }}</button>\n    <button class=\"vl-button\" type=\"button\" (click)=\"close()\">{{ l18n?.confirmButton || 'Opslaan' }}</button>\n  </div>\n</anb-modal>\n" }]
        }], ctorParameters: function () { return [{ type: AnbS3UploadInfoFormService }, { type: i2$4.AnbModalRef }]; }, propDecorators: { anbModalComponent: [{
                type: ViewChild,
                args: ['extraInfoModal', { static: true }]
            }], isTypeRequired: [{
                type: Input
            }], typeDropdownList: [{
                type: Input
            }], seperateViewLink: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class DownloadUtil {
    /**
     * Download file to disk
     * @param data
     * @param id
     */
    static downloadToDisk(data, filename) {
        const nav = window.navigator;
        if (nav && nav.msSaveOrOpenBlob) {
            // IE
            nav.msSaveOrOpenBlob(data, filename);
        }
        else {
            // chrome
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
        }
    }
    static downloadByUrl(httpClient, downloadUrl, fileName) {
        httpClient.get(downloadUrl, { responseType: 'blob' }).subscribe((file) => {
            DownloadUtil.downloadToDisk(file, fileName);
        });
    }
    /** If the file is valid, it returns null, else it sets the incorrect info. */
    static validateAttributes(file, allowedExtensions, maxSize = 0) {
        const fileExtension = file.name.split('.').pop();
        const fileSize = file.size / 1024 / 1024;
        const isInvalidType = !allowedExtensions.includes(fileExtension);
        const isInvalidSize = maxSize > 0 && fileSize > maxSize; // Mb
        if (isInvalidType || isInvalidSize) {
            const type = isInvalidType ? fileExtension : null;
            const size = isInvalidSize ? fileSize : null;
            return { type, size };
        }
        return null;
    }
}

class AnbDragDropDirective {
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.filesDropped = new EventEmitter();
        this.error = new EventEmitter();
        this.maxFiles = 10;
        this.isEnabled = true;
    }
    onDragOver(evt) {
        if (!this.isEnabled)
            return;
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = true;
    }
    onDragLeave(evt) {
        if (!this.isEnabled)
            return;
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = false;
    }
    onDrop(evt) {
        if (!this.isEnabled)
            return;
        evt.preventDefault();
        evt.stopPropagation();
        this.fileOver = false;
        const files = evt.dataTransfer.files;
        if (files.length > 0) {
            if (files.length <= this.maxFiles) {
                this.filesDropped.emit(files);
            }
            else {
                this.error.emit('maxFiles');
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDragDropDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AnbDragDropDirective, selector: "[dragDrop]", inputs: { maxFiles: "maxFiles", isEnabled: ["dragDrop", "isEnabled"] }, outputs: { filesDropped: "filesDropped", error: "error" }, host: { listeners: { "dragover": "onDragOver($event)", "dragleave": "onDragLeave($event)", "drop": "onDrop($event)" }, properties: { "class.fileover": "this.fileOver" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbDragDropDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dragDrop]',
                }]
        }], propDecorators: { fileOver: [{
                type: HostBinding,
                args: ['class.fileover']
            }], filesDropped: [{
                type: Output
            }], error: [{
                type: Output
            }], maxFiles: [{
                type: Input
            }], isEnabled: [{
                type: Input,
                args: ['dragDrop']
            }], onDragOver: [{
                type: HostListener,
                args: ['dragover', ['$event']]
            }], onDragLeave: [{
                type: HostListener,
                args: ['dragleave', ['$event']]
            }], onDrop: [{
                type: HostListener,
                args: ['drop', ['$event']]
            }] } });

const ATTACHMENT_FORM_PARAMS = {
    attachmentGuid: 'attachmentGuid',
    fileName: 'fileName',
    description: 'description',
    type: 'type',
    typeId: 'typeId',
    creationDate: 'creationDate',
};
const AttachmentFormGroup = {
    [ATTACHMENT_FORM_PARAMS.attachmentGuid]: [null, null],
    [ATTACHMENT_FORM_PARAMS.fileName]: [null, [Validators.required]],
    [ATTACHMENT_FORM_PARAMS.description]: [null, null],
    [ATTACHMENT_FORM_PARAMS.type]: [null, null],
    [ATTACHMENT_FORM_PARAMS.typeId]: [null, null],
    [ATTACHMENT_FORM_PARAMS.creationDate]: [null, [Validators.required]],
};
class AnbS3MultiUploadComponent extends BaseFormElementComponent {
    constructor(http, fb, uploadService, fileService, anbModal, formService) {
        super(formService);
        this.http = http;
        this.fb = fb;
        this.uploadService = uploadService;
        this.fileService = fileService;
        this.anbModal = anbModal;
        this.formService = formService;
        this.options = [];
        this.disabled = false;
        this.added = new EventEmitter();
        this.updated = new EventEmitter();
        this.uploaded = new EventEmitter();
        this.deleted = new EventEmitter();
        this.attachmentNames = [];
        this.subscriptions = new Subscription();
        this.errorMessagesSrc = new BehaviorSubject([]);
        this.errorMessages$ = this.errorMessagesSrc.asObservable();
        this.attachmentActionSrc = new BehaviorSubject(null);
        this.attachmentAction$ = this.attachmentActionSrc.asObservable();
        /* Upload to s3 w/ presigned url. */
        this.apiUpload$ = (attachment, file) => this.uploadService.addAttachment(attachment).pipe(filter((response) => !!response?.preSignedUploadURL && !!response?.attachmentGuid), switchMap((response) => {
            const { preSignedUploadURL, attachmentGuid } = response;
            const aws$ = this.http.put(preSignedUploadURL, file);
            return aws$.pipe(switchMap(() => this.uploadService
                .acknowledgeAttachment({ ...attachment, attachmentGuid })
                .pipe(map(() => ({ ...attachment, attachmentGuid })))));
        }));
        /* pops up the extra information dialog */
        this.popupModal$ = (data) => this.anbModal.open(AnbS3MultiUploadInfoComponent, { data }).afterClosed.pipe(filter((response) => !!response), switchMap((response) => {
            const type = response?.config?.attachmentType?.options?.find((o) => o.dropdownModelID === response.type)?.description || response.type;
            const typeId = response?.type;
            const description = response?.description;
            const creationDate = new Date();
            const attachment = {
                fileName: response.file?.name,
                attachmentGuid: response?.id,
                preSignedURL: undefined,
                type,
                typeId,
                description,
                creationDate,
            };
            return of(attachment);
        }));
        this.attachmentFromConfig$ = (file, extraInfo) => {
            const attachment = {
                fileName: file.name,
                attachmentGuid: undefined,
                preSignedURL: undefined,
                type: extraInfo?.type,
                typeId: extraInfo?.typeId,
                description: extraInfo?.description,
                creationDate: extraInfo?.creationDate ?? new Date(),
            };
            return of(attachment);
        };
        this.deleteAttachment$ = this.attachmentAction$.pipe(filter((data) => !!data && data.action === 'delete' && !!data.attachment), tap(({ attachment }) => {
            const index = this.value?.findIndex((v) => v.attachmentGuid === attachment.attachmentGuid);
            if (index >= 0) {
                this.attachments.removeAt(index);
                this.form.markAsDirty();
                this.deleted.emit(attachment.attachmentGuid);
            }
        }));
        this.attachmentForm = () => {
            const group = this.fb.group(AttachmentFormGroup);
            return new AnbFormGroup(group.controls, group.validator);
        };
    }
    get value() {
        return this.attachments?.value;
    }
    get isDragDropEnabled() {
        return this.allowDragDrop;
    }
    addAttachment(attachment, markAsDirty = true) {
        const formGroup = this.attachmentForm();
        formGroup.patchValue({
            [ATTACHMENT_FORM_PARAMS.attachmentGuid]: attachment?.attachmentGuid,
            [ATTACHMENT_FORM_PARAMS.fileName]: attachment?.fileName,
            [ATTACHMENT_FORM_PARAMS.description]: attachment?.description,
            [ATTACHMENT_FORM_PARAMS.type]: attachment?.type,
            [ATTACHMENT_FORM_PARAMS.typeId]: attachment?.typeId,
            [ATTACHMENT_FORM_PARAMS.creationDate]: attachment?.creationDate,
        }, { emitEvent: false });
        this.attachments.push(formGroup);
        if (markAsDirty) {
            this.form.markAsDirty();
            this.form.updateValueAndValidity();
        }
    }
    get attachments() {
        return this.form?.get(this.formArrayName) ?? new FormArray([]);
    }
    get attachmentCountStatus() {
        switch (this.remainingAttachmentCount) {
            case this.maxAttachmentCount:
                return (this.l18n?.maxUploadCount?.replace('{{maxCount}}', `${this.remainingAttachmentCount}`) ??
                    `Maximum aantal bestanden: ${this.remainingAttachmentCount}`);
            case 0:
                return (this.l18n?.maxUploadsReached?.replace('{{maxCount}}', `${this.maxAttachmentCount}`) ??
                    `Maximum aantal bestanden opgeladen: ${this.maxAttachmentCount}`);
            default:
                return (this.l18n?.remainingUploads?.replace('{{remaining}}', `${this.remainingAttachmentCount}`) ??
                    `Resterend aantal bestanden: ${this.remainingAttachmentCount}`);
        }
    }
    ngOnInit() {
        this.l18n = this.config?.l18n;
        this.table = this.config?.dataTable;
        this.allowDragDrop = this.config?.allowDragDrop ?? true;
        this.maxAttachmentCount = this.config?.singleFile ? 1 : this.config?.maxFiles;
        this.remainingAttachmentCount = this.value ? this.maxAttachmentCount - this.value?.length : this.maxAttachmentCount;
        this.allowedExtensions = this.config?.allowedExtensions ?? 'pdf,doc,docx,jpeg,jpg,png,bmp,rtf,xls,xlsx,html,htm,eml,msg,gpx'.split(',');
        this.allowedExtensionsInput = this.allowedExtensions.map((ext) => `.${ext}`).join(',');
        this.attachmentNames = this.value?.map((a) => a.fileName);
        const config = {
            ...this.config?.extraInfo?.config,
            attachmentType: {
                ...this.config?.extraInfo?.config?.attachmentType,
                options: this.options,
            },
        };
        const removeSub = this.attachments.valueChanges.subscribe(() => {
            this.remainingAttachmentCount = this.value ? this.maxAttachmentCount - this.value?.length : this.maxAttachmentCount;
            this.attachmentNames = this.value.map((a) => a.fileName);
        });
        this.subscriptions.add(removeSub);
        const aseSub = this.form.activityStatus
            .pipe(tap((ase) => {
            this.disabled = [ActivityStatusEnum.View, ActivityStatusEnum.Select].includes(ase);
        }))
            .subscribe();
        this.subscriptions.add(aseSub);
        const edit$ = this.attachmentAction$
            .pipe(filter((data) => !!data && data.action === 'update' && !!data.attachment), switchMap(({ attachment }) => {
            const { description, typeId, fileName, attachmentGuid } = attachment;
            const data = {
                config,
                id: attachmentGuid,
                fileName,
                description,
                type: typeId,
                typeId,
                typeOptions: this.options,
            };
            return this.popupModal$(data);
        }), tap((attachment) => {
            const updated = this.value.find((i) => i.attachmentGuid === attachment.attachmentGuid);
            updated.description = attachment.description;
            updated.typeId = attachment.typeId;
            updated.type = attachment.typeId;
        }))
            .subscribe();
        this.subscriptions.add(edit$);
        // get info for each file
        const add$ = this.uploaded
            .pipe(switchMap((files) => from(files).pipe(concatMap((file) => {
            const data = { file, config, typeOptions: this.options };
            const extraInfo = this.config.extraInfo;
            const selected$ = extraInfo.hidden ? this.attachmentFromConfig$(file, extraInfo) : this.popupModal$(data);
            return selected$.pipe(switchMap((att) => this.apiUpload$(att, file)));
        }), toArray(), tap((added) => {
            added.forEach((attachment) => this.addAttachment(attachment));
            this.form.markAsDirty();
        }))))
            .subscribe();
        this.subscriptions.add(add$);
        const delete$ = this.deleteAttachment$.subscribe();
        this.subscriptions.add(delete$);
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    typeDescription(typeId) {
        if (!this.options)
            return typeId;
        return this.options?.find((o) => o.dropdownModelID === typeId)?.description;
    }
    clearErrorMessages() {
        this.errorMessagesSrc.next([]);
    }
    addErrorMessage(error) {
        const existing = this.errorMessagesSrc.value;
        this.errorMessagesSrc.next([...existing, error]);
    }
    // drag drop
    filesDropped(files) {
        this.validateFiles(files);
    }
    dragDropError(key) {
        this.clearErrorMessages();
        if (key === 'maxFiles') {
            const message = this.l18n?.maxUploadsReached?.replace('{{maxCount}}', `${this.maxAttachmentCount}`) ??
                `Maximum aantal bestanden opgeladen: ${this.maxAttachmentCount}`;
            const error = { fileName: null, message };
            this.addErrorMessage(error);
        }
    }
    // files via browse / link
    filesChanged(event) {
        const files = event.target.files;
        this.validateFiles(files);
        event.target.value = null;
    }
    validateFiles(files) {
        this.clearErrorMessages();
        if (files.length > this.remainingAttachmentCount) {
            const message = this.l18n?.maxUploadsReached?.replace('{{maxCount}}', `${this.maxAttachmentCount}`) ??
                `Maximum aantal bestanden opgeladen: ${this.maxAttachmentCount}`;
            const error = { fileName: null, message };
            this.addErrorMessage(error);
        }
        const validFiles = [];
        const maxSizeMb = this.config?.maxFileSizeMb;
        [...files].forEach((file) => {
            const invalidAttributes = DownloadUtil.validateAttributes(file, this.allowedExtensions, maxSizeMb);
            const fileName = file.name;
            if (invalidAttributes) {
                const { size, type } = invalidAttributes;
                if (size) {
                    const message = this.l18n?.fileTooLarge?.replace('{{fileName}}', `${file.name}`).replace('{{maxFileSizeMB}}', `${maxSizeMb}`) ??
                        `Bestandsgrootte voor ${file.name} werd overschreden. Maximum ${maxSizeMb}MB toegelaten.`;
                    this.addErrorMessage({ fileName, message });
                }
                if (type) {
                    const extensions = this.allowedExtensions.join(',');
                    const message = this.l18n?.illegalExtension?.replace('{{fileName}}', `${file.name}`).replace('{{extensions}}', `${extensions}`) ??
                        `Bestandsextensie voor ${file.name} is niet toegelaten. Enkel deze extensies worden toegelaten: ${extensions}`;
                    this.addErrorMessage({ fileName, message });
                }
            }
            else if (!(this.config?.allowDuplicateFileNames ?? false) && this.attachmentNames?.includes(file.name)) {
                const message = this.l18n?.fileNameExists?.replace('{{fileName}}', `${file.name}`) ??
                    `Een bestand met het naam ${file.name} is reeds hier opgeladen.`;
                this.addErrorMessage({ fileName, message });
            }
            else {
                validFiles.push(file);
            }
        });
        this.uploaded.emit(validFiles);
        return true;
    }
    downloadSingle(attachment) {
        return this.download(attachment.attachmentGuid, attachment.fileName);
    }
    download(attachmentGuid, filename) {
        this.fileService.download(attachmentGuid, filename);
        return true;
    }
    delete(attachment) {
        this.attachmentActionSrc.next({ action: 'delete', attachment });
        return true;
    }
    edit(attachment) {
        this.attachmentActionSrc.next({ action: 'update', attachment });
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3MultiUploadComponent, deps: [{ token: i1$2.HttpClient }, { token: i2$2.FormBuilder }, { token: S3UploadService }, { token: FileService }, { token: i2$4.AnbModal }, { token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbS3MultiUploadComponent, selector: "anb-s3-multi-upload", inputs: { form: "form", formArrayName: "formArrayName", config: "config", options: "options", disabled: "disabled" }, outputs: { added: "added", updated: "updated", uploaded: "uploaded", deleted: "deleted" }, viewQueries: [{ propertyName: "fileInputField", first: true, predicate: ["file"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<table class=\"data-table data-table-s3upload vl-u-spacer--small\" *ngIf=\"!table?.hidden && value && value.length > 0 && !config?.singleFile\">\n  <colgroup>\n    <col *ngIf=\"!table?.column?.description?.hidden\" />\n    <col *ngIf=\"!table?.column?.type?.hidden\" />\n    <col />\n    <col *ngIf=\"!table?.column?.date?.hidden\" />\n    <col class=\"ten\" *ngIf=\"table?.seperateViewLink\" />\n    <col class=\"ten\" *ngIf=\"!table?.column?.edit?.hidden && !disabled\" />\n    <col class=\"ten\" *ngIf=\"!table?.column?.delete?.hidden && !disabled\" />\n  </colgroup>\n  <thead>\n    <tr class=\"data-table__header\">\n      <th class=\"data-table__header-title\">{{ l18n?.filename || 'Bestandsnaam' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.type?.hidden\">{{ l18n?.type || 'Type' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.description?.hidden\">{{ l18n?.description || 'Omschrijving' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.date?.hidden\">{{ l18n?.date || 'Datum upload' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"table?.seperateViewLink\"></th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.edit?.hidden && !disabled\"></th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.delete?.hidden && !disabled\"></th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr *ngFor=\"let attachment of value\">\n      <td class=\"ellipsis_cell\">\n        <div *ngIf=\"!table?.seperateViewLink\">\n          <span\n            ><a class=\"pointer\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">{{ attachment.fileName }}</a></span\n          >\n        </div>\n        <div *ngIf=\"table?.seperateViewLink\">\n          {{ attachment.fileName }}\n        </div>\n      </td>\n      <td *ngIf=\"!table?.column?.type?.hidden\">{{ typeDescription(attachment.typeId) }}</td>\n      <td *ngIf=\"!table?.column?.description?.hidden\">{{ attachment.description }}</td>\n\n      <td *ngIf=\"!table?.column?.date?.hidden\">{{ attachment.creationDate | date: table?.column?.date?.format || 'dd/MM/yyyy' }}</td>\n      <td *ngIf=\"table?.seperateViewLink\">\n        <span\n          ><a class=\"pointer\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\"> {{ l18n?.view || 'Bekijk' }}</a></span\n        >\n      </td>\n      <td *ngIf=\"!table?.column?.edit?.hidden && !disabled\" [class.button-td]=\"table?.useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && table?.useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"edit(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-edit\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!attachment.isReadOnly\">\n            <p class=\"vl-icon-wrapper\">\n              <a class=\"vl-link pointer\" (click)=\"edit(attachment)\"\n                ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n                ><span id=\"txtEdit\">{{ l18n?.edit || 'Bewerk' }}</span></a\n              >\n            </p>\n          </ng-container>\n        </div>\n      </td>\n      <td *ngIf=\"!table?.column?.delete?.hidden && !disabled\" [class.button-td]=\"table?.useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && table?.useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon pointer\"\n            (click)=\"delete(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-bin\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !table?.useIconButtons && !attachment.isReadOnly\">\n            <a class=\"vl-link pointer\" (click)=\"delete(attachment)\"\n              ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n              ><span id=\"txtVerwijder\">{{ l18n?.delete || 'Verwijder' }}</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n    </tr>\n  </tbody>\n</table>\n\n<div *ngIf=\"config?.singleFile && value && value.length === 1\">\n  <div class=\"vl-u-spacer--small formarray-item-upload\" *ngFor=\"let attachment of value\">\n    <div class=\"single__file_name\">\n      <span>\n        <a class=\"pointer\" [title]=\"attachment.description\" (click)=\"downloadSingle(attachment)\"\n          ><small>{{ attachment.fileName }} {{ attachment.description ? ' - ' + attachment.description : '' }}</small></a\n        >\n      </span>\n    </div>\n    <div>\n      <ng-container *ngIf=\"!disabled && !table?.useIconButtons\">\n        <a class=\"vl-link pointer\" (click)=\"edit(attachment)\"\n          ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n          ><span id=\"txtVerwijder\">{{ l18n?.edit || 'Bewerk' }}</span></a\n        >\n      </ng-container>\n      <ng-container *ngIf=\"!disabled && !table?.useIconButtons\">\n        <a class=\"vl-link pointer\" (click)=\"delete(attachment)\"\n          ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n          ><span id=\"txtVerwijder\">{{ l18n?.delete || 'Verwijder' }}</span></a\n        >\n      </ng-container>\n    </div>\n  </div>\n  <div class=\"vl-u-clearfix\"></div>\n</div>\n\n<div *ngIf=\"remainingAttachmentCount > 0\">\n  <div class=\"fright upload-info upload-info-group\" *ngIf=\"!!config?.maxFiles && !config?.singleFile && !disabled\">\n    <small>{{ attachmentCountStatus }}</small>\n  </div>\n  <div class=\"vl-u-clearfix\"></div>\n  <div\n    *ngIf=\"!(config?.singleFile && value && value.length === 1) && remainingAttachmentCount\"\n    class=\"anb-input upload js js-upload js-upload-bound formarray-item-upload\"\n    [dragDrop]=\"isDragDropEnabled\"\n    [maxFiles]=\"remainingAttachmentCount\"\n    (error)=\"dragDropError($event)\"\n    (filesDropped)=\"filesDropped($event)\"\n  >\n    <label class=\"webuniversum form__label\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n      <div class=\"upload-info-group\">\n        <div *ngIf=\"!disabled\" class=\"upload__element\">\n          <label class=\"upload__element__label\">\n            <input\n              #file\n              type=\"file\"\n              [attr.multiple]=\"config?.singleFile ? null : 'multiple'\"\n              [attr.accept]=\"allowedExtensionsInput\"\n              [disabled]=\"disabled || remainingAttachmentCount < 1\"\n              (change)=\"filesChanged($event)\"\n              style=\"display: none\"\n            />\n            <div>\n              <i class=\"vi vi-paperclip\"></i>\n              <span>{{ l18n?.add || 'Bijlage(s) toevoegen' }}</span>\n            </div>\n            <div>\n              <small>{{ l18n?.annotation || allowDragDrop ? 'Sleep de bijlage(s) naar hier om toe te voegen' : '' }}</small>\n            </div>\n          </label>\n        </div>\n      </div>\n    </label>\n  </div>\n\n  <div *ngFor=\"let error of errorMessages$ | async\" class=\"webuniversum form__error\">{{ error.message }}</div>\n</div>\n", styles: ["@media only screen and (max-width: 1200px){#txtVerwijder,#txtEdit{display:none}}table{width:100%;table-layout:fixed}.four{width:4%}.five{width:5%}.six{width:6%}.ten{width:10%}.twenty{width:20%}table.data-table-s3upload td{overflow-wrap:anywhere}.ellipsis_cell>div{position:relative;overflow:hidden;height:2rem}.ellipsis_cell>div>span{display:block;position:absolute;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:2rem}.ellipsis_cell>div:after{content:attr(title);overflow:hidden;height:0;display:block}.button-td{padding:0}.button-td .vl-button{margin-top:5px}.formarray-item-upload{margin-top:1rem}.formarray-item-uploaded{margin-top:3rem}.fileover{background-color:#0000000d}.upload-info-group small{font-size:smaller;vertical-align:middle}.upload__element{margin-top:1.5rem;margin-bottom:0}.upload__element{margin-top:1.5rem}.error .js .upload__element{border:2px dashed #db3434;background-color:#fbe6e6}.fleft{float:left;padding:0 .3em;width:35%}.fright{float:right;padding:0 .3em}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: AnbDragDropDirective, selector: "[dragDrop]", inputs: ["maxFiles", "dragDrop"], outputs: ["filesDropped", "error"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: i2.DatePipe, name: "date" }, { kind: "pipe", type: SafePipe, name: "safe" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbS3MultiUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-s3-multi-upload', template: "<table class=\"data-table data-table-s3upload vl-u-spacer--small\" *ngIf=\"!table?.hidden && value && value.length > 0 && !config?.singleFile\">\n  <colgroup>\n    <col *ngIf=\"!table?.column?.description?.hidden\" />\n    <col *ngIf=\"!table?.column?.type?.hidden\" />\n    <col />\n    <col *ngIf=\"!table?.column?.date?.hidden\" />\n    <col class=\"ten\" *ngIf=\"table?.seperateViewLink\" />\n    <col class=\"ten\" *ngIf=\"!table?.column?.edit?.hidden && !disabled\" />\n    <col class=\"ten\" *ngIf=\"!table?.column?.delete?.hidden && !disabled\" />\n  </colgroup>\n  <thead>\n    <tr class=\"data-table__header\">\n      <th class=\"data-table__header-title\">{{ l18n?.filename || 'Bestandsnaam' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.type?.hidden\">{{ l18n?.type || 'Type' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.description?.hidden\">{{ l18n?.description || 'Omschrijving' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.date?.hidden\">{{ l18n?.date || 'Datum upload' }}</th>\n      <th class=\"data-table__header-title\" *ngIf=\"table?.seperateViewLink\"></th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.edit?.hidden && !disabled\"></th>\n      <th class=\"data-table__header-title\" *ngIf=\"!table?.column?.delete?.hidden && !disabled\"></th>\n    </tr>\n  </thead>\n  <tbody>\n    <tr *ngFor=\"let attachment of value\">\n      <td class=\"ellipsis_cell\">\n        <div *ngIf=\"!table?.seperateViewLink\">\n          <span\n            ><a class=\"pointer\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\">{{ attachment.fileName }}</a></span\n          >\n        </div>\n        <div *ngIf=\"table?.seperateViewLink\">\n          {{ attachment.fileName }}\n        </div>\n      </td>\n      <td *ngIf=\"!table?.column?.type?.hidden\">{{ typeDescription(attachment.typeId) }}</td>\n      <td *ngIf=\"!table?.column?.description?.hidden\">{{ attachment.description }}</td>\n\n      <td *ngIf=\"!table?.column?.date?.hidden\">{{ attachment.creationDate | date: table?.column?.date?.format || 'dd/MM/yyyy' }}</td>\n      <td *ngIf=\"table?.seperateViewLink\">\n        <span\n          ><a class=\"pointer\" (click)=\"download(attachment.attachmentGuid, attachment.fileName)\"> {{ l18n?.view || 'Bekijk' }}</a></span\n        >\n      </td>\n      <td *ngIf=\"!table?.column?.edit?.hidden && !disabled\" [class.button-td]=\"table?.useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && table?.useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon\"\n            (click)=\"edit(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-edit\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!attachment.isReadOnly\">\n            <p class=\"vl-icon-wrapper\">\n              <a class=\"vl-link pointer\" (click)=\"edit(attachment)\"\n                ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n                ><span id=\"txtEdit\">{{ l18n?.edit || 'Bewerk' }}</span></a\n              >\n            </p>\n          </ng-container>\n        </div>\n      </td>\n      <td *ngIf=\"!table?.column?.delete?.hidden && !disabled\" [class.button-td]=\"table?.useIconButtons\">\n        <div class=\"vl-action-group vl-action-group--align-right\">\n          <button\n            *ngIf=\"!disabled && table?.useIconButtons && !attachment.isReadOnly\"\n            class=\"vl-button vl-button--narrow vl-button--tertiary vl-button--noborder vl-button--icon pointer\"\n            (click)=\"delete(attachment)\"\n            type=\"button\"\n          >\n            <i class=\"vl-button__icon vl-vi vl-vi-bin\" aria-hidden=\"true\"></i>\n            <span class=\"vl-u-hidden\"></span>\n          </button>\n          <ng-container *ngIf=\"!disabled && !table?.useIconButtons && !attachment.isReadOnly\">\n            <a class=\"vl-link pointer\" (click)=\"delete(attachment)\"\n              ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n              ><span id=\"txtVerwijder\">{{ l18n?.delete || 'Verwijder' }}</span></a\n            >\n          </ng-container>\n        </div>\n      </td>\n    </tr>\n  </tbody>\n</table>\n\n<div *ngIf=\"config?.singleFile && value && value.length === 1\">\n  <div class=\"vl-u-spacer--small formarray-item-upload\" *ngFor=\"let attachment of value\">\n    <div class=\"single__file_name\">\n      <span>\n        <a class=\"pointer\" [title]=\"attachment.description\" (click)=\"downloadSingle(attachment)\"\n          ><small>{{ attachment.fileName }} {{ attachment.description ? ' - ' + attachment.description : '' }}</small></a\n        >\n      </span>\n    </div>\n    <div>\n      <ng-container *ngIf=\"!disabled && !table?.useIconButtons\">\n        <a class=\"vl-link pointer\" (click)=\"edit(attachment)\"\n          ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-edit\" aria-hidden=\"true\"></span\n          ><span id=\"txtVerwijder\">{{ l18n?.edit || 'Bewerk' }}</span></a\n        >\n      </ng-container>\n      <ng-container *ngIf=\"!disabled && !table?.useIconButtons\">\n        <a class=\"vl-link pointer\" (click)=\"delete(attachment)\"\n          ><span class=\"vl-link__icon vl-link__icon--before vl-vi vl-vi-trash\" aria-hidden=\"true\"></span\n          ><span id=\"txtVerwijder\">{{ l18n?.delete || 'Verwijder' }}</span></a\n        >\n      </ng-container>\n    </div>\n  </div>\n  <div class=\"vl-u-clearfix\"></div>\n</div>\n\n<div *ngIf=\"remainingAttachmentCount > 0\">\n  <div class=\"fright upload-info upload-info-group\" *ngIf=\"!!config?.maxFiles && !config?.singleFile && !disabled\">\n    <small>{{ attachmentCountStatus }}</small>\n  </div>\n  <div class=\"vl-u-clearfix\"></div>\n  <div\n    *ngIf=\"!(config?.singleFile && value && value.length === 1) && remainingAttachmentCount\"\n    class=\"anb-input upload js js-upload js-upload-bound formarray-item-upload\"\n    [dragDrop]=\"isDragDropEnabled\"\n    [maxFiles]=\"remainingAttachmentCount\"\n    (error)=\"dragDropError($event)\"\n    (filesDropped)=\"filesDropped($event)\"\n  >\n    <label class=\"webuniversum form__label\">\n      <span *ngIf=\"label\" class=\"vraag\" [innerHtml]=\"label | safe: 'html'\"></span>\n      <span *ngIf=\"subLabel\" class=\"subvraag\" [innerHtml]=\"subLabel | safe: 'html'\"></span>\n      <div class=\"upload-info-group\">\n        <div *ngIf=\"!disabled\" class=\"upload__element\">\n          <label class=\"upload__element__label\">\n            <input\n              #file\n              type=\"file\"\n              [attr.multiple]=\"config?.singleFile ? null : 'multiple'\"\n              [attr.accept]=\"allowedExtensionsInput\"\n              [disabled]=\"disabled || remainingAttachmentCount < 1\"\n              (change)=\"filesChanged($event)\"\n              style=\"display: none\"\n            />\n            <div>\n              <i class=\"vi vi-paperclip\"></i>\n              <span>{{ l18n?.add || 'Bijlage(s) toevoegen' }}</span>\n            </div>\n            <div>\n              <small>{{ l18n?.annotation || allowDragDrop ? 'Sleep de bijlage(s) naar hier om toe te voegen' : '' }}</small>\n            </div>\n          </label>\n        </div>\n      </div>\n    </label>\n  </div>\n\n  <div *ngFor=\"let error of errorMessages$ | async\" class=\"webuniversum form__error\">{{ error.message }}</div>\n</div>\n", styles: ["@media only screen and (max-width: 1200px){#txtVerwijder,#txtEdit{display:none}}table{width:100%;table-layout:fixed}.four{width:4%}.five{width:5%}.six{width:6%}.ten{width:10%}.twenty{width:20%}table.data-table-s3upload td{overflow-wrap:anywhere}.ellipsis_cell>div{position:relative;overflow:hidden;height:2rem}.ellipsis_cell>div>span{display:block;position:absolute;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;line-height:2rem}.ellipsis_cell>div:after{content:attr(title);overflow:hidden;height:0;display:block}.button-td{padding:0}.button-td .vl-button{margin-top:5px}.formarray-item-upload{margin-top:1rem}.formarray-item-uploaded{margin-top:3rem}.fileover{background-color:#0000000d}.upload-info-group small{font-size:smaller;vertical-align:middle}.upload__element{margin-top:1.5rem;margin-bottom:0}.upload__element{margin-top:1.5rem}.error .js .upload__element{border:2px dashed #db3434;background-color:#fbe6e6}.fleft{float:left;padding:0 .3em;width:35%}.fright{float:right;padding:0 .3em}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: i2$2.FormBuilder }, { type: S3UploadService }, { type: FileService }, { type: i2$4.AnbModal }, { type: AnbFormService }]; }, propDecorators: { form: [{
                type: Input
            }], formArrayName: [{
                type: Input
            }], config: [{
                type: Input
            }], options: [{
                type: Input
            }], disabled: [{
                type: Input
            }], added: [{
                type: Output
            }], updated: [{
                type: Output
            }], uploaded: [{
                type: Output
            }], deleted: [{
                type: Output
            }], fileInputField: [{
                type: ViewChild,
                args: ['file']
            }] } });

/**
 * This Pipe will return the url of the s3 Image based on the attachmentGuid
 * It is also possible to resize the image. You use the following properties for this: width, height and fileName
 *
 */
class S3ImageUrlPipe {
    constructor(s3UploadService) {
        this.s3UploadService = s3UploadService;
    }
    transform(attachmentGuid, width = 0, height = 0, fileName = null) {
        const result = new Observable((o) => {
            if (attachmentGuid) {
                this.s3UploadService.getAttachment(attachmentGuid, width, height, fileName).subscribe((response) => {
                    o.next(response.preSignedURL);
                });
            }
        });
        return result;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: S3ImageUrlPipe, deps: [{ token: S3UploadService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: S3ImageUrlPipe, name: "s3ImageUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: S3ImageUrlPipe, decorators: [{
            type: Pipe,
            args: [{ name: 's3ImageUrl' }]
        }], ctorParameters: function () { return [{ type: S3UploadService }]; } });

class AnbRadioButtonGroupComponent extends BaseFormElementComponent {
    set radioChoices(values) {
        this.choices = values.reduce((acc, val) => acc.concat(Object.keys(val).map((key) => ({ key, value: val[key] }))), []);
    }
    get value() {
        return this.innerValue;
    }
    constructor(formService, anbFb, renderer) {
        super(formService);
        this.formService = formService;
        this.anbFb = anbFb;
        this.renderer = renderer;
        this.showAsButtons = false;
        this.FORM_PARAM = {
            selected: 'selected',
        };
        this.onTouch = () => undefined;
    }
    ngOnInit() {
        this.radioForm = this.anbFb.anbGroup({
            [this.FORM_PARAM.selected]: [],
        });
        super.ngOnInit();
    }
    ngAfterViewInit() {
        const checkedInput = this.inputs.toArray().find((x) => x.nativeElement.id === this.innerValue);
        if (checkedInput) {
            this.renderer.setProperty(checkedInput.nativeElement, 'checked', true);
        }
    }
    onInputChange(input) {
        this.renderer.setProperty(input, 'checked', true);
    }
    writeValue(val) {
        this.innerValue = val;
        this.radioForm.get(this.FORM_PARAM.selected).patchValue(this.value, { emitEvent: false });
    }
    registerOnChange(fn) {
        this.radioForm.valueChanges.subscribe((value) => {
            fn(value.selected);
        });
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbRadioButtonGroupComponent, deps: [{ token: AnbFormService }, { token: AnbFormBuilder }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbRadioButtonGroupComponent, selector: "anb-radiobutton-group", inputs: { radioChoices: "radioChoices", showAsButtons: "showAsButtons" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: AnbRadioButtonGroupComponent,
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputs", predicate: ["input"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div *ngIf=\"choices.length\">\n  <label class=\"form__label\">{{ label }}</label>\n  <div class=\"float-container\" [formGroup]=\"radioForm\" [ngClass]=\"{ 'anb-input--button-group': showAsButtons }\">\n    <div *ngFor=\"let choice of choices; index as i\" class=\"float-child anb-input\">\n      <label\n        class=\"webuniversum form__label\"\n        [class.radio]=\"!showAsButtons\"\n        [class.radio--error]=\"showError() && !disabled\"\n        [class.radio--disabled]=\"disabled\"\n        [ngClass]=\"{ 'button toggle-button': showAsButtons }\"\n        [class.button--secondary]=\"!(radioForm.value.selected === choice.key) && showAsButtons\"\n      >\n        <input\n          #input\n          [attr.id]=\"choice.key\"\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [formControlName]=\"FORM_PARAM.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n          [value]=\"choice.key\"\n          [disabled]=\"disabled\"\n          (change)=\"onInputChange(input)\"\n        />\n        <ng-container *ngIf=\"!showAsButtons; else hasButtonGroup\">\n          <span></span>\n          <span [innerHtml]=\"choice.value\"></span>\n        </ng-container>\n        <ng-template #hasButtonGroup>\n          <div [innerHtml]=\"choice.value\"></div>\n        </ng-template>\n      </label>\n    </div>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".anb-input--horizontal,.anb-input--button-group{display:flex;flex-direction:row;flex-wrap:wrap;align-items:center}.toggle-button{border:.3rem solid #06c;margin-right:-.3rem;padding:0rem 1rem}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2$2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2$2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2$2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2$2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2$2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2$2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbRadioButtonGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-radiobutton-group', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: AnbRadioButtonGroupComponent,
                            multi: true,
                        },
                    ], template: "<div *ngIf=\"choices.length\">\n  <label class=\"form__label\">{{ label }}</label>\n  <div class=\"float-container\" [formGroup]=\"radioForm\" [ngClass]=\"{ 'anb-input--button-group': showAsButtons }\">\n    <div *ngFor=\"let choice of choices; index as i\" class=\"float-child anb-input\">\n      <label\n        class=\"webuniversum form__label\"\n        [class.radio]=\"!showAsButtons\"\n        [class.radio--error]=\"showError() && !disabled\"\n        [class.radio--disabled]=\"disabled\"\n        [ngClass]=\"{ 'button toggle-button': showAsButtons }\"\n        [class.button--secondary]=\"!(radioForm.value.selected === choice.key) && showAsButtons\"\n      >\n        <input\n          #input\n          [attr.id]=\"choice.key\"\n          type=\"radio\"\n          class=\"webuniversum radio__toggle\"\n          [formControlName]=\"FORM_PARAM.selected\"\n          [attr.disabled]=\"disabled ? '' : null\"\n          [value]=\"choice.key\"\n          [disabled]=\"disabled\"\n          (change)=\"onInputChange(input)\"\n        />\n        <ng-container *ngIf=\"!showAsButtons; else hasButtonGroup\">\n          <span></span>\n          <span [innerHtml]=\"choice.value\"></span>\n        </ng-container>\n        <ng-template #hasButtonGroup>\n          <div [innerHtml]=\"choice.value\"></div>\n        </ng-template>\n      </label>\n    </div>\n  </div>\n\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".anb-input--horizontal,.anb-input--button-group{display:flex;flex-direction:row;flex-wrap:wrap;align-items:center}.toggle-button{border:.3rem solid #06c;margin-right:-.3rem;padding:0rem 1rem}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }, { type: AnbFormBuilder }, { type: i0.Renderer2 }]; }, propDecorators: { radioChoices: [{
                type: Input
            }], showAsButtons: [{
                type: Input
            }], inputs: [{
                type: ViewChildren,
                args: ['input']
            }] } });

class AnbTabComponent {
    constructor() {
        this.active = false;
        this.canSelect = true;
        this.order = 0;
        this.selectDenied = new EventEmitter();
    }
    ngOnInit() {
        if (!this.id) {
            this.id = 'tab' + this.tabTitle.replace(' ', '');
        }
        if (!this.tabHeaderControlId) {
            this.tabHeaderControlId = 'tabHeader' + this.tabTitle.replace(' ', '');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTabComponent, selector: "anb-tab", inputs: { tabTitle: "tabTitle", active: "active", id: "id", canSelect: "canSelect", canDeactivate: "canDeactivate", route: "route", order: "order", childTabs: "childTabs" }, outputs: { tabHeaderControlId: "tabHeaderControlId", selectDenied: "selectDenied" }, ngImport: i0, template: "<div *ngIf=\"active && !route\" role=\"tabpanel\" [attr.id]=\"id\" [attr.aria-labelledby]=\"tabHeaderControlId\" [attr.aria-hidden]=\"!active\">\n  <ng-content></ng-content>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-tab', template: "<div *ngIf=\"active && !route\" role=\"tabpanel\" [attr.id]=\"id\" [attr.aria-labelledby]=\"tabHeaderControlId\" [attr.aria-hidden]=\"!active\">\n  <ng-content></ng-content>\n</div>\n" }]
        }], propDecorators: { tabTitle: [{
                type: Input
            }], active: [{
                type: Input
            }], id: [{
                type: Input
            }], canSelect: [{
                type: Input
            }], canDeactivate: [{
                type: Input
            }], route: [{
                type: Input
            }], order: [{
                type: Input
            }], childTabs: [{
                type: Input
            }], tabHeaderControlId: [{
                type: Output
            }], selectDenied: [{
                type: Output
            }] } });

class AnbTabsComponent {
    set currentUrl(value) {
        this.currentUrlInternal$.next(value);
    }
    constructor(router) {
        this.router = router;
        this.sub = new SubSink();
        /**
         * If false, the default router outlet will not be used and the user has to add the router outlet manually in his template.
         */
        this.useDefaultRouterOutlet = true;
        this.selectStart = new EventEmitter();
        this.select = new EventEmitter();
        this.isCurrentTabFirst = true;
        this.isCurrentTabLast = false;
        this.currentUrlInternal$ = new BehaviorSubject(null);
        // Holds the first level of child tabs (from the selected parent tab)
        this.childRoutes = [];
        this.activeChildRoute = null;
        // Holds potential nested child tabs (from the active child tab)
        this.nestedChildRoutes = [];
    }
    ngAfterContentInit() {
        const sortTabs = this.tabs.toArray().sort((a, b) => a.order - b.order);
        this.tabs.reset(sortTabs);
        this.sub.sink = this.tabs.changes
            .pipe(withLatestFrom(this.currentUrlInternal$), filter(([_, url]) => !!url))
            .subscribe(([_, url]) => this.setCurrentTabBasedOnCurrentUrlValue(this.tabs, url));
        this.sub.sink = this.currentUrlInternal$
            .pipe(filter((url) => !!url), filter(() => !!this.tabs))
            .subscribe((url) => this.setCurrentTabBasedOnCurrentUrlValue(this.tabs, url));
    }
    setCurrentTabBasedOnCurrentUrlValue(tabs, url) {
        this.tabs.filter(tab => !!tab.route).forEach((tab) => {
            const currentUrlTree = this.router.parseUrl(url);
            const stepUrlTree = this.router.createUrlTree(tab.route);
            const currentUrl = this.getPathFromUrl(this.router.serializeUrl(currentUrlTree));
            const stepUrl = this.router.serializeUrl(stepUrlTree);
            tab.active = currentUrl.includes(stepUrl);
            if (tab.active) {
                this.currentTab = tab;
                this.setupChildRoutes(tab);
            }
        });
    }
    ngOnDestroy() {
        this.sub.unsubscribe();
    }
    selectTab(tab) {
        if (this.selectStart) {
            this.selectStart.emit(tab);
        }
        if (!tab.canSelect) {
            tab.selectDenied.emit(tab);
            return;
        }
        const tabRoute = tab.route ? tab.route : this.defaultRoute;
        if (tabRoute) {
            this.router.navigate(tabRoute).then((value) => {
                if (value || value === null) {
                    this.setSelectedTab(tab);
                }
            });
        }
        else {
            this.setSelectedTab(tab);
        }
    }
    selectChildTab(child) {
        // Emit selectStart event
        if (this.selectStart) {
            this.selectStart.emit(child);
        }
        if (child.hasOwnProperty('canSelect') && !child.canSelect) {
            if (child.selectDenied) {
                child.selectDenied.emit(child);
            }
            return;
        }
        // Determine the parent's route first.
        const parentRoute = this.currentTab?.route || [];
        // Determine the child route segment; support both array and string formats.
        const childSegment = child.route ? (Array.isArray(child.route) ? child.route : [child.route]) : [child.path];
        // Combine the parent route with the child segment.
        const combinedRoute = [...parentRoute, ...childSegment];
        // Navigate to the full combined route.
        this.router.navigate(combinedRoute)
            .then((activated) => {
            if (activated || activated === null) {
                this.setSelectedChild(child);
            }
        });
    }
    setSelectedChild(child) {
        // If the currently active child has a canDeactivate function, check before switching
        if (this.activeChildRoute && this.activeChildRoute.canDeactivate) {
            const canDeactivate = this.activeChildRoute.canDeactivate;
            canDeactivate.then((data) => {
                if (data) {
                    this.activateChild(child);
                }
            });
        }
        else {
            this.activateChild(child);
        }
    }
    activateChild(child) {
        this.activeChildRoute = child;
        this.nestedChildRoutes = child.childTabs && child.childTabs.length
            ? child.childTabs
            : [];
    }
    setSelectedTab(tab) {
        if (this.currentTab && this.currentTab.canDeactivate) {
            const canDeactivate = this.currentTab.canDeactivate;
            canDeactivate.then((data) => {
                if (data) {
                    this.activateTab(tab);
                }
            });
        }
        else {
            this.activateTab(tab);
        }
    }
    activateTab(tab) {
        this.tabs.toArray().forEach((t) => (t.active = false));
        tab.active = true;
        this.currentTab = tab;
        this.checkTabStatus();
        this.setupChildRoutes(tab);
    }
    getPathFromUrl(url) {
        return url.split(/[?#]/)[0];
    }
    checkCurrentTabFirst() {
        if (this.tabs) {
            const index = this.tabs.toArray().indexOf(this.currentTab);
            if (this.currentTab && index === 0) {
                this.isCurrentTabFirst = true;
                return;
            }
        }
        this.isCurrentTabFirst = false;
    }
    checkCurrentTabLast() {
        if (this.tabs) {
            const index = this.tabs.toArray().indexOf(this.currentTab);
            if (this.currentTab && index === this.tabs.length - 1) {
                this.isCurrentTabLast = true;
                return;
            }
        }
        this.isCurrentTabLast = false;
    }
    checkTabStatus() {
        this.checkCurrentTabFirst();
        this.checkCurrentTabLast();
        this.select.emit({
            isCurrentTabFirst: this.isCurrentTabFirst,
            isCurrentTabLast: this.isCurrentTabLast,
        });
    }
    // New helper method: recursively find a matching route in the router config
    findRoute(routeSegments, routes) {
        console.log("findRoute", routeSegments, routes);
        if (!routeSegments.length) {
            return null;
        }
        const segment = routeSegments[0];
        for (const route of routes) {
            console.log("route", route.path, segment);
            if (route.path === segment) {
                if (routeSegments.length === 1) {
                    return route;
                }
                if (route.children) {
                    return this.findRoute(routeSegments.slice(1), route.children);
                }
            }
        }
        return null;
    }
    setupChildRoutes(tab) {
        if (tab.childTabs && tab.childTabs.length) {
            // Only use the configuration passed in as childTabs.
            this.childRoutes = tab.childTabs;
            this.activeChildRoute = this.childRoutes[0];
            this.selectChildTab(this.childRoutes[0]);
            this.nestedChildRoutes = this.activeChildRoute.childTabs && this.activeChildRoute.childTabs.length
                ? this.activeChildRoute.childTabs
                : [];
        }
        else {
            this.childRoutes = [];
            this.activeChildRoute = null;
            this.nestedChildRoutes = [];
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabsComponent, deps: [{ token: i1$3.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbTabsComponent, selector: "anb-tabs", inputs: { defaultRoute: "defaultRoute", currentUrl: "currentUrl", useDefaultRouterOutlet: "useDefaultRouterOutlet" }, outputs: { selectStart: "selectStart", select: "select" }, queries: [{ propertyName: "tabs", predicate: AnbTabComponent }], ngImport: i0, template: "<div class=\"webuniversum tabs__wrapper\">\n  <ul class=\"webuniversum tabs\" role=\"tablist\">\n    <li\n      *ngFor=\"let tab of tabs\"\n      (click)=\"selectTab(tab)\"\n      class=\"tab\"\n      [class.tab--active]=\"tab.active\"\n      attr.role=\"tab\"\n      [attr.aria-controls]=\"tab.id\"\n      [attr.id]=\"tab.tabHeaderControlId\"\n      [attr.aria-selected]=\"tab.active\"\n    >\n      <a href=\"javascript:void(0)\" class=\"tab__link pointer\" [attr.aria-expanded]=\"tab.active\">{{ tab.tabTitle }}</a>\n    </li>\n  </ul>\n\n  <!-- Render first-level child tabs -->\n  <ng-container *ngIf=\"childRoutes && childRoutes.length\">\n    <ul class=\"webuniversum tabs sub-tabs\" role=\"tablist\">\n      <li\n        *ngFor=\"let child of childRoutes\"\n        (click)=\"selectChildTab(child)\"\n        class=\"tab sub-tab\"\n        [class.tab--active]=\"activeChildRoute?.route === child.route\"\n        attr.role=\"tab\"\n        [attr.aria-selected]=\"activeChildRoute?.route === child.route\"\n      >\n        <a href=\"javascript:void(0)\" class=\"tab__link sub-tab__link pointer\">\n          {{ child.tabTitle || child.route }}\n        </a>\n      </li>\n    </ul>\n  </ng-container>\n\n  <div class=\"pane\">\n    <ng-content></ng-content>\n    <ng-container *ngIf=\"useDefaultRouterOutlet\">\n      <router-outlet *ngIf=\"currentTab?.route || activeChildRoute\" #tabOutlet=\"outlet\"></router-outlet>\n    </ng-container>\n  </div>\n</div>\n", styles: [".pane{padding:1em 0}.pointer{cursor:pointer}.tabs__wrapper .tabs{margin-bottom:0}.tabs__wrapper .tabs:last-of-type{margin-bottom:3rem}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$3.RouterOutlet, selector: "router-outlet", inputs: ["name"], outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbTabsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-tabs', template: "<div class=\"webuniversum tabs__wrapper\">\n  <ul class=\"webuniversum tabs\" role=\"tablist\">\n    <li\n      *ngFor=\"let tab of tabs\"\n      (click)=\"selectTab(tab)\"\n      class=\"tab\"\n      [class.tab--active]=\"tab.active\"\n      attr.role=\"tab\"\n      [attr.aria-controls]=\"tab.id\"\n      [attr.id]=\"tab.tabHeaderControlId\"\n      [attr.aria-selected]=\"tab.active\"\n    >\n      <a href=\"javascript:void(0)\" class=\"tab__link pointer\" [attr.aria-expanded]=\"tab.active\">{{ tab.tabTitle }}</a>\n    </li>\n  </ul>\n\n  <!-- Render first-level child tabs -->\n  <ng-container *ngIf=\"childRoutes && childRoutes.length\">\n    <ul class=\"webuniversum tabs sub-tabs\" role=\"tablist\">\n      <li\n        *ngFor=\"let child of childRoutes\"\n        (click)=\"selectChildTab(child)\"\n        class=\"tab sub-tab\"\n        [class.tab--active]=\"activeChildRoute?.route === child.route\"\n        attr.role=\"tab\"\n        [attr.aria-selected]=\"activeChildRoute?.route === child.route\"\n      >\n        <a href=\"javascript:void(0)\" class=\"tab__link sub-tab__link pointer\">\n          {{ child.tabTitle || child.route }}\n        </a>\n      </li>\n    </ul>\n  </ng-container>\n\n  <div class=\"pane\">\n    <ng-content></ng-content>\n    <ng-container *ngIf=\"useDefaultRouterOutlet\">\n      <router-outlet *ngIf=\"currentTab?.route || activeChildRoute\" #tabOutlet=\"outlet\"></router-outlet>\n    </ng-container>\n  </div>\n</div>\n", styles: [".pane{padding:1em 0}.pointer{cursor:pointer}.tabs__wrapper .tabs{margin-bottom:0}.tabs__wrapper .tabs:last-of-type{margin-bottom:3rem}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$3.Router }]; }, propDecorators: { tabs: [{
                type: ContentChildren,
                args: [AnbTabComponent]
            }], defaultRoute: [{
                type: Input
            }], currentUrl: [{
                type: Input
            }], useDefaultRouterOutlet: [{
                type: Input
            }], selectStart: [{
                type: Output
            }], select: [{
                type: Output
            }] } });

var ButtonType;
(function (ButtonType) {
    ButtonType["primary"] = "primary";
    ButtonType["secondary"] = "secondary";
    ButtonType["tertiary"] = "tertiary";
})(ButtonType || (ButtonType = {}));
class PickerFile {
    constructor(name, dataString) {
        this.name = name;
        this.dataString = dataString;
    }
}

class AnbInputFilePickerComponent extends BaseFormElementComponent {
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    registerOnTouched(fn) { }
    constructor(formService) {
        super(formService); // Constructors for derived classes must contain a 'super' call.
        this.formService = formService;
        this.placeholder = "Kies een bestand";
        this.fileTypes = '.doc,.docx,.pdf';
        this.buttonType = ButtonType.primary;
        this.icon = 'file-office-doc';
        this.buttonText = "Upload";
        this.buttonRight = false;
        this.id = crypto.randomUUID();
        this.filename = '';
    }
    writeValue(value) {
    }
    setDisabledState(isDisabled) {
    }
    doOnChange(value) {
        if (this.value !== value) {
            this.value = value;
        }
    }
    onChange(event) {
        //calculate size in MB
        const fileSize = event.target.files[0].size / 1024 / 1024;
        if (this.maxFileSize && fileSize > this.maxFileSize) {
            this.filename = `Bestand is groter dan ${this.maxFileSize}MB`;
            this.propagateChange(null);
            this.valueChange(null);
            return;
        }
        this.value = event.target.files[0];
        this.filename = this.value.name;
        const reader = new FileReader();
        reader.readAsDataURL(this.value);
        reader.onload = () => {
            let fileContent = '';
            if (!(reader.result instanceof ArrayBuffer)) {
                fileContent = reader.result.split(',').pop();
            }
            this.propagateChange(new PickerFile(this.filename, fileContent));
            this.valueChange(new PickerFile(this.filename, fileContent));
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputFilePickerComponent, deps: [{ token: AnbFormService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnbInputFilePickerComponent, selector: "anb-input-file-picker", inputs: { placeholder: "placeholder", maxFileSize: "maxFileSize", fileTypes: "fileTypes", buttonType: "buttonType", icon: "icon", buttonText: "buttonText", buttonRight: "buttonRight" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AnbInputFilePickerComponent),
                multi: true,
            },
            {
                provide: BaseFormElementComponent,
                useExisting: forwardRef(() => AnbInputFilePickerComponent),
                multi: true,
            },
        ], usesInheritance: true, ngImport: i0, template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <div class=\"input-container\" [class.reverse]=\"buttonRight\">\n    <button\n      class=\"vl-button vl-button--icon-before vl-button--{{ buttonType }}\"\n      vl-button\n      [attr.for]=\"'file-picker-' + id\"\n      (click)=\"file.click()\"\n    >\n      <i class=\"vl-button__icon vl-button__icon--before vl-vi vl-vi-{{ icon }}\" aria-hidden=\"true\"></i>\n      <span class=\"vl-button__label\">{{ buttonText }}</span>\n      <span class=\"vl-u-visually-hidden\">{{ 'BEHANDELING.ADVIES.TEMPLATE.FILE_HOVER' }}</span>\n    </button>\n    <input\n      class=\"webuniversum input-field input-field--disabled\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      type=\"text\"\n      [value]=\"filename || placeholder\"\n      [attr.id]=\"id\"\n      disabled=\"disabled\"\n      [attr.placeholder]=\"placeholder\"\n    />\n    <input\n      #file\n      [attr.id]=\"'file-picker-' + id\"\n      type=\"file\"\n      placeholder=\"Upload bestand\"\n      [accept]=\"fileTypes\"\n      [hidden]=\"'true'\"\n      (change)=\"onChange($event)\"\n      style=\"display: none\"\n    />\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}.input-container{position:relative;display:flex;flex-direction:row}.input-container.reverse{position:relative;display:flex;flex-direction:row-reverse}.input-container .input-field--disabled{padding-right:50px}.input-container .vl-button{flex:none}.input-container label{position:absolute;top:0;width:100%;left:0;height:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbInputFilePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'anb-input-file-picker', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AnbInputFilePickerComponent),
                            multi: true,
                        },
                        {
                            provide: BaseFormElementComponent,
                            useExisting: forwardRef(() => AnbInputFilePickerComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"anb-input\" [class.invisible]=\"!visible\">\n  <label *ngIf=\"label\" class=\"webuniversum form__label\" [innerHtml]=\"label\"></label>\n  <div class=\"input-container\" [class.reverse]=\"buttonRight\">\n    <button\n      class=\"vl-button vl-button--icon-before vl-button--{{ buttonType }}\"\n      vl-button\n      [attr.for]=\"'file-picker-' + id\"\n      (click)=\"file.click()\"\n    >\n      <i class=\"vl-button__icon vl-button__icon--before vl-vi vl-vi-{{ icon }}\" aria-hidden=\"true\"></i>\n      <span class=\"vl-button__label\">{{ buttonText }}</span>\n      <span class=\"vl-u-visually-hidden\">{{ 'BEHANDELING.ADVIES.TEMPLATE.FILE_HOVER' }}</span>\n    </button>\n    <input\n      class=\"webuniversum input-field input-field--disabled\"\n      [class.input-field--error]=\"showError() && !disabled\"\n      [class.input-field--disabled]=\"disabled\"\n      type=\"text\"\n      [value]=\"filename || placeholder\"\n      [attr.id]=\"id\"\n      disabled=\"disabled\"\n      [attr.placeholder]=\"placeholder\"\n    />\n    <input\n      #file\n      [attr.id]=\"'file-picker-' + id\"\n      type=\"file\"\n      placeholder=\"Upload bestand\"\n      [accept]=\"fileTypes\"\n      [hidden]=\"'true'\"\n      (change)=\"onChange($event)\"\n      style=\"display: none\"\n    />\n  </div>\n  <div *ngIf=\"showError()\" class=\"webuniversum form__error\">\n    {{ getError() }}\n  </div>\n</div>\n", styles: [".text-danger{float:left}.anb-input{float:left;clear:left}.anb-input,label,input{width:100%}.input-container{position:relative;display:flex;flex-direction:row}.input-container.reverse{position:relative;display:flex;flex-direction:row-reverse}.input-container .input-field--disabled{padding-right:50px}.input-container .vl-button{flex:none}.input-container label{position:absolute;top:0;width:100%;left:0;height:100%}\n", "::ng-deep .vl-input-field::-moz-placeholder,::ng-deep .input-field::-moz-placeholder,::ng-deep .textarea::-moz-placeholder{color:#999}.placeholder,::ng-deep .vl-input-field::placeholder,::ng-deep .input-field::placeholder,::ng-deep .textarea::placeholder{color:#999}.select option{color:#333}\n"] }]
        }], ctorParameters: function () { return [{ type: AnbFormService }]; }, propDecorators: { placeholder: [{
                type: Input
            }], maxFileSize: [{
                type: Input
            }], fileTypes: [{
                type: Input
            }], buttonType: [{
                type: Input
            }], icon: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], buttonRight: [{
                type: Input
            }] } });

const components = [
    AnbCheckboxComponent,
    AnbDatepickerDeprecatedComponent,
    AnbDatepickerComponent,
    AnbDropdownComponent,
    AnbInputPercentComponent,
    AnbInputTextComponent,
    AnbQuestionComponent,
    AnbRadiobuttonComponent,
    AnbRadioButtonGroupComponent,
    AnbSearchDropdownComponent,
    AnbTabComponent,
    AnbTabsComponent,
    AnbTabDeprecatedComponent,
    AnbTabsDeprecatedComponent,
    AnbTextareaComponent,
    AnbTextEditorComponent,
    AnbTitleComponent,
    AnbUploadComponent,
    AnbWorkflowButtonsComponent,
    AnbUsercodeAutocompleteComponent,
    AnbUsercodecheckboxlistComponent,
    AnbUsercodedropdownComponent,
    AnbUsercodeCheckboxComponent,
    AnbUsercoderadiobuttoncascadinglistComponent,
    AnbUsercoderadiobuttonlistComponent,
    BaseUsercodecheckboxlistComponent,
    BaseFormElementComponent,
    AnbInputTextAutocompleteComponent,
    AnbS3UploadComponent,
    AnbFilePreviewerComponent,
    AnbMultiSelectComponent,
    AnbMultiLevelMultiSelectComponent,
    SelectItemComponent,
    AnbUserCodeCheckboxListExtendedComponent,
    AnbFormArrayEditorWrapperComponent,
    AnbInputNumericComponent,
    AnbErrorContainerComponent,
    AnbInputAddressComponent,
    AnbPillInputComponent,
    AnbDatepickerHeaderComponent,
    AnbDatepickerBaseComponent,
    AnbTabComponent,
    AnbTabsComponent,
    AnbInputFilePickerComponent,
    AnbYesNoRadioComponent,
    AnbYesNoContainerComponent,
    AnbInputPercentageComponent,
    AnbS3MultiUploadComponent,
    AnbS3MultiUploadInfoComponent
];
const directives = [
    IfAseDirective,
    FroalaEditorDirective,
    AnbStreetAutocompleteDirective,
    AnbPostalCodeAutocompleteDirective,
    AnbMunicipalityAutocompleteDirective,
    AnbDragDropDirective
];
const pipes = [SafePipe, S3ImageUrlPipe];
class AnbFormElementsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormElementsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AnbFormElementsModule, declarations: [AnbCheckboxComponent,
            AnbDatepickerDeprecatedComponent,
            AnbDatepickerComponent,
            AnbDropdownComponent,
            AnbInputPercentComponent,
            AnbInputTextComponent,
            AnbQuestionComponent,
            AnbRadiobuttonComponent,
            AnbRadioButtonGroupComponent,
            AnbSearchDropdownComponent,
            AnbTabComponent,
            AnbTabsComponent,
            AnbTabDeprecatedComponent,
            AnbTabsDeprecatedComponent,
            AnbTextareaComponent,
            AnbTextEditorComponent,
            AnbTitleComponent,
            AnbUploadComponent,
            AnbWorkflowButtonsComponent,
            AnbUsercodeAutocompleteComponent,
            AnbUsercodecheckboxlistComponent,
            AnbUsercodedropdownComponent,
            AnbUsercodeCheckboxComponent,
            AnbUsercoderadiobuttoncascadinglistComponent,
            AnbUsercoderadiobuttonlistComponent,
            BaseUsercodecheckboxlistComponent,
            BaseFormElementComponent,
            AnbInputTextAutocompleteComponent,
            AnbS3UploadComponent,
            AnbFilePreviewerComponent,
            AnbMultiSelectComponent,
            AnbMultiLevelMultiSelectComponent,
            SelectItemComponent,
            AnbUserCodeCheckboxListExtendedComponent,
            AnbFormArrayEditorWrapperComponent,
            AnbInputNumericComponent,
            AnbErrorContainerComponent,
            AnbInputAddressComponent,
            AnbPillInputComponent,
            AnbDatepickerHeaderComponent,
            AnbDatepickerBaseComponent,
            AnbTabComponent,
            AnbTabsComponent,
            AnbInputFilePickerComponent,
            AnbYesNoRadioComponent,
            AnbYesNoContainerComponent,
            AnbInputPercentageComponent,
            AnbS3MultiUploadComponent,
            AnbS3MultiUploadInfoComponent, IfAseDirective,
            FroalaEditorDirective,
            AnbStreetAutocompleteDirective,
            AnbPostalCodeAutocompleteDirective,
            AnbMunicipalityAutocompleteDirective,
            AnbDragDropDirective, SafePipe, S3ImageUrlPipe], imports: [CommonModule, AnbUIElementsModule, RouterModule, ReactiveFormsModule, MatDatepickerModule, MatIconModule, MaskitoDirective, MaskitoPipe], exports: [AnbCheckboxComponent,
            AnbDatepickerDeprecatedComponent,
            AnbDatepickerComponent,
            AnbDropdownComponent,
            AnbInputPercentComponent,
            AnbInputTextComponent,
            AnbQuestionComponent,
            AnbRadiobuttonComponent,
            AnbRadioButtonGroupComponent,
            AnbSearchDropdownComponent,
            AnbTabComponent,
            AnbTabsComponent,
            AnbTabDeprecatedComponent,
            AnbTabsDeprecatedComponent,
            AnbTextareaComponent,
            AnbTextEditorComponent,
            AnbTitleComponent,
            AnbUploadComponent,
            AnbWorkflowButtonsComponent,
            AnbUsercodeAutocompleteComponent,
            AnbUsercodecheckboxlistComponent,
            AnbUsercodedropdownComponent,
            AnbUsercodeCheckboxComponent,
            AnbUsercoderadiobuttoncascadinglistComponent,
            AnbUsercoderadiobuttonlistComponent,
            BaseUsercodecheckboxlistComponent,
            BaseFormElementComponent,
            AnbInputTextAutocompleteComponent,
            AnbS3UploadComponent,
            AnbFilePreviewerComponent,
            AnbMultiSelectComponent,
            AnbMultiLevelMultiSelectComponent,
            SelectItemComponent,
            AnbUserCodeCheckboxListExtendedComponent,
            AnbFormArrayEditorWrapperComponent,
            AnbInputNumericComponent,
            AnbErrorContainerComponent,
            AnbInputAddressComponent,
            AnbPillInputComponent,
            AnbDatepickerHeaderComponent,
            AnbDatepickerBaseComponent,
            AnbTabComponent,
            AnbTabsComponent,
            AnbInputFilePickerComponent,
            AnbYesNoRadioComponent,
            AnbYesNoContainerComponent,
            AnbInputPercentageComponent,
            AnbS3MultiUploadComponent,
            AnbS3MultiUploadInfoComponent, IfAseDirective,
            FroalaEditorDirective,
            AnbStreetAutocompleteDirective,
            AnbPostalCodeAutocompleteDirective,
            AnbMunicipalityAutocompleteDirective,
            AnbDragDropDirective, SafePipe, S3ImageUrlPipe, MatIconModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormElementsModule, providers: [AnbFormService, AnbFormBuilder, FileService, AlertService, AnbS3UploadInfoFormService, { provide: MAT_DATE_LOCALE, useValue: 'nl-BE' }], imports: [CommonModule, AnbUIElementsModule, RouterModule, ReactiveFormsModule, MatDatepickerModule, MatIconModule, MatIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbFormElementsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...components, ...directives, ...pipes],
                    imports: [CommonModule, AnbUIElementsModule, RouterModule, ReactiveFormsModule, MatDatepickerModule, MatIconModule, MaskitoDirective, MaskitoPipe],
                    exports: [...components, ...directives, ...pipes, MatIconModule],
                    providers: [AnbFormService, AnbFormBuilder, FileService, AlertService, AnbS3UploadInfoFormService, { provide: MAT_DATE_LOCALE, useValue: 'nl-BE' }],
                }]
        }] });

/** The base component for each page. Each page must implement this component.
A page is a component that has a route. */
class AnbBasePageComponent {
    constructor() {
        this.aseEnum = ActivityStatusEnablementEnum;
    }
    canDeactivate() {
        if (this.form.dirty) {
            return this.showConfirmDialog();
        }
        return true;
    }
}

/** Decides if a route can be deactivated */
class AnbCanDeactivateGuardService {
    /** Decides if a route can be deactivated */
    canDeactivate(component) {
        return component.canDeactivate ? component.canDeactivate() : true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCanDeactivateGuardService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCanDeactivateGuardService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnbCanDeactivateGuardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });
const AnbCanDeactivateGuard = (component) => {
    return inject(AnbCanDeactivateGuardService).canDeactivate(component);
};

/* eslint-disable @typescript-eslint/no-explicit-any */
class Attachment {
}

class AttachmentInfo {
    constructor() {
        this.canBeDeleted = true;
    }
}

class ExtendedUserCode {
    constructor(userCodeID, userCodeGroupID, description, softDeleted, isExtended, extendedPlaceholder, extendedClass, isExtendedRequired, extendedType = ExtendedUserCodeTypeEnum.Text, rowClass, parentUserCodeID, parentUserCodeGroupID, dependencySet, sortOrder, comment, visible = true) {
        this.userCodeID = userCodeID;
        this.userCodeGroupID = userCodeGroupID;
        this.description = description;
        this.softDeleted = softDeleted;
        this.isExtended = isExtended;
        this.extendedPlaceholder = extendedPlaceholder;
        this.extendedClass = extendedClass;
        this.isExtendedRequired = isExtendedRequired;
        this.extendedType = extendedType;
        this.rowClass = rowClass;
        this.parentUserCodeID = parentUserCodeID;
        this.parentUserCodeGroupID = parentUserCodeGroupID;
        this.dependencySet = dependencySet;
        this.sortOrder = sortOrder;
        this.comment = comment;
        this.visible = visible;
    }
}

class Upload {
}
class UploadItem {
}
class UploadServiceResponse {
}

class UserCode {
    constructor(userCodeID, userCodeGroupID, description, softDeleted, parentUserCodeID, parentUserCodeGroupID, dependencySet, sortOrder, comment, visible = true) {
        this.userCodeID = userCodeID;
        this.userCodeGroupID = userCodeGroupID;
        this.description = description;
        this.softDeleted = softDeleted;
        this.parentUserCodeID = parentUserCodeID;
        this.parentUserCodeGroupID = parentUserCodeGroupID;
        this.dependencySet = dependencySet;
        this.sortOrder = sortOrder;
        this.comment = comment;
        this.visible = visible;
    }
}

class WorkflowTransition {
}

class RadioChoice {
    constructor(key, value) {
        this[key] = value;
    }
}

class AnbFormsUtils {
    static parseDatePickerValueToDate(value) {
        if (!value) {
            return;
        }
        let date = null;
        if (isDate(value)) {
            date = value;
        }
        else if (isValid(parseISO(value))) {
            date = parseISO(value);
        }
        else if (isValid(parse(value, 'dd/MM/yyyy', new Date()))) {
            date = parse(value, 'dd/MM/yyyy', new Date());
        }
        return date;
    }
    static parseStringToNumber(value) {
        return parseFloat(value);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ActivityStatusEnablementEnum, ActivityStatusEnum, Alert, AlertService, AlertType, AlignmentEnum, AnbBaseFormComponent, AnbBaseFormService, AnbBasePageComponent, AnbCanDeactivateGuard, AnbCheckboxComponent, AnbDatepickerBaseComponent, AnbDatepickerComponent, AnbDatepickerDeprecatedComponent, AnbDatepickerHeaderComponent, AnbDragDropDirective, AnbDropdownComponent, AnbErrorContainerComponent, AnbFilePreviewerComponent, AnbFormArrayEditorWrapperComponent, AnbFormBuilder, AnbFormElementsModule, AnbFormGroup, AnbFormService, AnbFormsUtils, AnbInputAddressComponent, AnbInputAddressUtils, ANB_INPUT_ADDRESS_FORM_PARAMS as AnbInputAddress_FORM_PARAMS, AnbInputFilePickerComponent, AnbInputNumericComponent, AnbInputPercentComponent, AnbInputPercentageComponent, AnbInputTextAutocompleteComponent, AnbInputTextComponent, AnbMultiLevelDropdownModel, AnbMultiLevelMultiSelectComponent, AnbMultiSelectComponent, AnbMunicipalityAutocompleteDirective, AnbPillInputComponent, AnbPostalCodeAutocompleteDirective, AnbQuestionComponent, AnbRadioButtonGroupComponent, AnbRadiobuttonComponent, AnbS3MultiUploadComponent, AnbS3MultiUploadInfoComponent, AnbS3UploadComponent, AnbS3UploadInfoFormService, AnbSearchDropdownComponent, AnbStreetAutocompleteDirective, AnbTabComponent, AnbTabDeprecatedComponent, AnbTabsComponent, AnbTabsDeprecatedComponent, AnbTextEditorComponent, AnbTextareaComponent, AnbTitleComponent, AnbUploadComponent, AnbUserCodeCheckboxListExtendedComponent, AnbUsercodeAutocompleteComponent, AnbUsercodeCheckboxComponent, AnbUsercodecheckboxlistComponent, AnbUsercodedropdownComponent, AnbUsercoderadiobuttoncascadinglistComponent, AnbUsercoderadiobuttonlistComponent, AnbWorkflowButtonsComponent, AnbYesNoContainerComponent, AnbYesNoRadioComponent, Attachment, AttachmentInfo, BaseFormElementComponent, BaseUsercodecheckboxlistComponent, BasisregisterService, CustomError, DownloadUtil, DropdownModel, ErrorUtils, ExtendedUserCode, ExtendedUserCodeResult, ExtendedUserCodeTypeEnum, FileService, FroalaEditorDirective, GetFormGroup, IfAseDirective, MasterEnReferentieDataService, PredefinedMaskEnum, RadioChoice, S3Attachment, S3ImageUrlPipe, S3UploadService, SafePipe, SelectItemComponent, Upload, UploadServiceResponse, UserCode, WorkflowTransition };
